import { hrmsGet, resolveApiUrl } from "../../../api/api";
import i18n from "../../../i18n";
import {
  fetchCatalogProductById,
  fetchPartNumbersByMake,
  getQuotationPmcs,
  getQuotationSpecificTerms,
  getQuotationSystemLines,
} from "../../../services/quotationService";
import {
  createApprovalRule,
  deleteApprovalRule,
  getPmcConfigs,
  getTerms,
} from "../../../services/salesConfigService";
import sessionDB from "../../../utils/sessionDB";


const CUSTOMER_STAGE_STATUSES = ["Approved", "Sent to Customer", "Under Negotiation", "Accepted", "Rejected"];
const SALES_TYPES = ["Supply", "SITC", "SERVICE"];
const STATUS_OPTIONS = [
  "Draft",
  "Pending Approval",
  "Approved",
  "Sent to Customer",
  "Under Negotiation",
  "Accepted",
  "Rejected",
  "Import Failed",
];

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function localizedFieldProps() {
  const language = ["hi", "te", "kn"].includes(i18n.resolvedLanguage)
    ? i18n.resolvedLanguage
    : "en";

  return {
    lang: language,
    autoCapitalize: "off",
    spellCheck: false,
  };
}

function currentIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatInputNumber(value) {
  const rounded = Number(value || 0);
  if (!Number.isFinite(rounded)) return "";
  return Number.isInteger(rounded) ? String(rounded) : String(Number(rounded.toFixed(2)));
}

function translateQuotationType(type) {
  if (type === "Supply") return sq("typeSupply");
  if (type === "SITC") return sq("typeSitc");
  if (type === "SERVICE" || type === "Service") return sq("typeService");
  return type;
}

/** Working Sheet install columns/calcs — same path for SITC and SERVICE; Supply excluded. */
function supportsInstallation(quotationType) {
  const normalized = String(quotationType || "").trim().toUpperCase();
  return normalized === "SITC" || normalized === "SERVICE";
}

function translateQuotationStatus(status) {
  const map = {
    Draft: "statusDraft",
    "Pending Approval": "statusPendingApproval",
    Approved: "statusApproved",
    "Sent to Customer": "statusSentToCustomer",
    "Under Negotiation": "statusUnderNegotiation",
    Accepted: "statusAccepted",
    Rejected: "statusRejected",
    Expired: "statusExpired",
    "Import Failed": "statusImportFailed",
  };
  return map[status] ? sq(map[status]) : status;
}

function normalizeCustomerOption(customer = {}) {
  return {
    id: customer.customerId ?? customer.id ?? "",
    name: customer.companyName ?? customer.company_name ?? customer.name ?? "",
    raw: customer,
  };
}

function normalizeLocationOption(location = {}) {
  return {
    id: location.locationId ?? location.id ?? "",
    name:
      location.locationName ??
      location.name ??
      location.addressLine1 ??
      location.address ??
      "",
    customerId:
      location.customerId ??
      location.customer_id ??
      location.customerInfoId ??
      location.customerInfo?.customerId ??
      "",
    raw: location,
  };
}

function normalizeContactOption(contact = {}) {
  const firstName = contact.firstName ?? "";
  const lastName = contact.lastName ?? "";
  return {
    id: contact.contactId ?? contact.id ?? "",
    name:
      contact.contactName ??
      contact.name ??
      `${firstName} ${lastName}`.trim(),
    phone:
      contact.phone ??
      contact.phoneNumber ??
      contact.mobile ??
      contact.contactPhone ??
      "",
    email:
      contact.email ??
      contact.contactEmail ??
      "",
    customerId:
      contact.customerId ??
      contact.customer_id ??
      contact.customerInfoId ??
      contact.customerInfo?.customerId ??
      "",
    raw: contact,
  };
}

function normalizeEmployeeOption(employee = {}) {
  const firstName = employee.firstName ?? employee.first_Name ?? "";
  const lastName = employee.lastName ?? employee.last_Name ?? "";
  const fullName =
    employee.fullName ??
    employee.employeeName ??
    employee.name ??
    [firstName, lastName].filter(Boolean).join(" ");

  return {
    id: employee.empId ?? employee.emp_id ?? employee.employeeId ?? employee.id ?? "",
    name: String(fullName || "").trim(),
    raw: employee,
  };
}

const EMP_INFO_SEARCH_PATH = "/emp-info/search?page=0&size=10&sortBy=empId&sortDir=ASC";
let employeeOptionsInflight = null;

/** Shared HRMS employee list — dedupes concurrent callers; loads only on demand. */
async function fetchEmployeeOptions() {
  if (!employeeOptionsInflight) {
    employeeOptionsInflight = (async () => {
      const response = await hrmsGet(EMP_INFO_SEARCH_PATH, "HR01S");
      const payload = response?.data ?? response;
      return extractApiCollection(payload)
        .map(normalizeEmployeeOption)
        .filter((item) => item.id && item.name);
    })().catch((error) => {
      employeeOptionsInflight = null;
      throw error;
    });
  }
  return employeeOptionsInflight;
}

function seedPmcEntriesFromQuotation(savedEntries = []) {
  return extractApiCollection(savedEntries)
    .filter((entry) => isPmcCostHeadActive(entry))
    .map((entry, index) => normalizePmcEntry(entry, index));
}

function seedTermsStateFromQuotation(specificTerms = [], libraryTerms = []) {
  return resolveTermsFromQuotation(specificTerms || [], libraryTerms || []);
}

function hasQuotationPmcData(pmcs) {
  return extractApiCollection(pmcs).length > 0;
}

function hasQuotationSpecificTermsData(specificTerms) {
  return extractApiCollection(specificTerms).length > 0;
}

function extractQuotationSystemLineItems(payload) {
  const root = payload?.data !== undefined ? payload.data : payload;
  if (Array.isArray(root)) return root;
  if (!root || typeof root !== "object") return [];
  const direct = extractApiCollection(
    root.lineItems ?? root.lines ?? root.items ?? root.workingRows ?? root.content
  );
  if (direct.length) return direct;
  if (root.data != null) return extractQuotationSystemLineItems(root.data);
  return [];
}

function extractQuotationPmcPayload(payload) {
  const root = payload?.data !== undefined ? payload.data : payload;
  if (Array.isArray(root)) return root;
  if (!root || typeof root !== "object") return [];
  const direct = extractApiCollection(
    root.pmcs ??
      root.pmcEntries ??
      root.pmcCostHeads ??
      root.heads ??
      root.items ??
      root.content ??
      root.lines
  );
  if (direct.length) return direct;
  if (root.data != null) return extractQuotationPmcPayload({ data: root.data });
  return [];
}

function extractQuotationSpecificTermsPayload(payload) {
  // Support both raw API bodies and quotationService request() envelopes ({ raw, data, message }).
  const roots = [];
  if (payload != null && typeof payload === "object" && (payload.raw !== undefined || payload.message !== undefined)) {
    if (payload.data !== undefined) roots.push(payload.data);
    if (payload.raw !== undefined) roots.push(payload.raw);
  } else if (payload?.data !== undefined) {
    roots.push(payload.data);
  } else {
    roots.push(payload);
  }

  for (const root of roots) {
    if (Array.isArray(root)) return root;
    if (!root || typeof root !== "object") continue;
    const direct = extractApiCollection(
      root.specificTerms ??
        root.quotationSpecificTerms ??
        root.selectedTerms ??
        root.termList ??
        root.termsList ??
        root.terms ??
        root.items ??
        root.content ??
        root.standardTerms
    );
    if (direct.length) return direct;
    if (root.data != null) {
      const nested = extractQuotationSpecificTermsPayload({ data: root.data });
      if (nested.length) return nested;
    }
  }
  return [];
}

/** Normalize quotation-specific term rows for rematch + save (prefer library id over row pk). */
function normalizeSpecificTermFromApi(term = {}, index = 0) {
  const libraryId = extractNumericId(
    term.standardTermId ??
      term.libraryTermId ??
      term.libraryId ??
      term.termId ??
      term.term_id ??
      term.id
  );
  return {
    id: libraryId || 0,
    title:
      String(
        term.title || term.name || term.header || term.termTitle || term.term_title || ""
      ).trim() || `Term ${index + 1}`,
    description: String(
      term.description ||
        term.text ||
        term.body ||
        term.termDescription ||
        term.term_description ||
        ""
    ).trim(),
    displayOrder: Number(term.displayOrder ?? term.order ?? index + 1),
  };
}

function normalizeSpecificTermsCollection(terms = []) {
  return extractApiCollection(terms).map((term, index) => normalizeSpecificTermFromApi(term, index));
}

/**
 * Prefer GET .../quotations/get/{id}/pmcs for saved C+P data.
 * Only fall back to pmc-configs when the quotation has no saved PMC rows.
 */
async function loadPmcEntriesForQuotation(quotationId, fallbackPmcs = []) {
  const id = extractNumericId(quotationId) || String(quotationId || "").trim();
  if (id) {
    try {
      const response = await getQuotationPmcs(id);
      const pmcs = extractQuotationPmcPayload(response);
      if (pmcs.length) {
        return {
          entries: seedPmcEntriesFromQuotation(pmcs),
          pmcs,
          source: "quotation",
        };
      }
    } catch {
      // Fall through to embedded / config sources.
    }
  }

  if (hasQuotationPmcData(fallbackPmcs)) {
    return {
      entries: seedPmcEntriesFromQuotation(fallbackPmcs),
      pmcs: extractApiCollection(fallbackPmcs),
      source: "embedded",
    };
  }

  return { entries: null, pmcs: [], source: "none" };
}

/**
 * Prefer GET .../quotations/get/{id}/specific-terms for saved Terms.
 * Only fall back to the generic terms library when none are saved.
 */
async function loadSpecificTermsForQuotation(quotationId, fallbackTerms = []) {
  const id = extractNumericId(quotationId) || String(quotationId || "").trim();
  if (id) {
    try {
      const response = await getQuotationSpecificTerms(id);
      const specificTerms = normalizeSpecificTermsCollection(
        extractQuotationSpecificTermsPayload(response)
      );
      if (specificTerms.length) {
        return { specificTerms, source: "quotation" };
      }
    } catch {
      // Fall through to embedded / library sources.
    }
  }

  if (hasQuotationSpecificTermsData(fallbackTerms)) {
    return {
      specificTerms: normalizeSpecificTermsCollection(fallbackTerms),
      source: "embedded",
    };
  }

  return { specificTerms: [], source: "none" };
}

/**
 * Terms tab state: always load standard terms library for the Standard Terms list,
 * then map quotation-specific terms onto selected standard / custom additional entries.
 * Does not change which APIs are called — only how responses are mapped into UI buckets.
 */
async function loadTermsTabState(quotationId, quotationType, fallbackTerms = []) {
  // Fetch library + saved terms independently — a library failure must not skip /specific-terms.
  const [libraryTerms, loaded] = await Promise.all([
    loadStandardTermsForQuotationType(quotationType).catch(() => []),
    loadSpecificTermsForQuotation(quotationId, fallbackTerms),
  ]);
  const specificTerms =
    loaded.source === "quotation" || loaded.source === "embedded"
      ? loaded.specificTerms
      : [];
  const resolved = resolveTermsFromQuotation(specificTerms, libraryTerms || []);
  return {
    standardTerms: libraryTerms || [],
    selectedIds: resolved.selectedIds,
    additionalTermEntries: resolved.additionalTermEntries,
    previewOrder: resolved.previewOrder,
    specificTerms,
    source: loaded.source === "none" ? "library" : loaded.source,
  };
}

function mapSystemApiLinesToWorkingRows(
  lineItems = [],
  system = {},
  quotationType = "Supply",
  options = {}
) {
  const sectionName = String(system?.name || system?.systemName || "").trim();
  const safeSectionName = isClientPlaceholderSystemName(sectionName) ? "" : sectionName;
  const safeSectionId = isClientPlaceholderSystemId(system?.id ?? system?.systemId)
    ? ""
    : String(system?.id || system?.systemId || "").trim();

  return extractApiCollection(lineItems).map((item, index) => {
    const mapped = mapApiLineItemToWorkingRow(item, index, quotationType, options);
    const mappedName = String(mapped.systemName || "").trim();
    const nextName = mappedName || safeSectionName;
    return {
      ...mapped,
      systemId: mapped.systemId || safeSectionId || "",
      // Never re-apply placeholder labels like "Default System" from the section key.
      systemName: isClientPlaceholderSystemName(nextName) ? "" : nextName,
    };
  });
}

function parseCurrency(value) {
  const normalized = String(value ?? "").replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isUnsetNumeric(value) {
  const text = String(value ?? "").trim();
  if (!text) return true;
  return parseCurrency(text) === 0;
}

function mapStoredAmount(value) {
  if (isUnsetNumeric(value)) return "";
  return String(value);
}

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function pickFiniteNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

/** Read a finite pricing total from quotation root, nested Working Sheet, or system summaries. */
function pickPricingMetricFromQuotation(item = {}, ...keys) {
  const workingSheet =
    item.workingSheet && typeof item.workingSheet === "object" ? item.workingSheet : null;
  const sources = [
    item,
    workingSheet,
    item.pricing,
    item.pricingMetrics,
    item.costPricing,
    workingSheet?.pricing,
    workingSheet?.totals,
    workingSheet?.pricingMetrics,
  ].filter((value) => value && typeof value === "object" && !Array.isArray(value));

  for (const source of sources) {
    const value = pickFiniteNumber(...keys.map((key) => source[key]));
    if (value != null) return value;
  }

  const summaries = extractApiCollection(
    item.systemSummaries ?? workingSheet?.systemSummaries
  );
  if (!summaries.length) return null;

  let sum = 0;
  let found = false;
  summaries.forEach((entry) => {
    const value = pickFiniteNumber(...keys.map((key) => entry?.[key]));
    if (value != null) {
      sum += value;
      found = true;
    }
  });
  return found ? sum : null;
}

/** Map persisted Cost + Pricing / Working Sheet totals from GET/Update response. */
function mapBackendPricingMetrics(item = {}) {
  const netProfit = pickPricingMetricFromQuotation(item, "netProfit", "net_profit");
  const grossProfit = pickPricingMetricFromQuotation(item, "grossProfit", "gross_profit");
  const overheadsAmount = pickPricingMetricFromQuotation(
    item,
    "overheadsAmount",
    "overheads_amount",
    "totalOverheadsAmount"
  ) ?? (
    netProfit != null && grossProfit != null
      ? grossProfit - netProfit
      : null
  );
  const totalSellingSupplyAmount = pickPricingMetricFromQuotation(
    item,
    "totalSellingSupplyAmount",
    "total_selling_supply_amount",
    "sellingSupplyTotal",
    "selling_supply_total",
    "sellingSupplyAmount"
  );
  const totalSellingInstallationAmount = pickPricingMetricFromQuotation(
    item,
    "totalSellingInstallationAmount",
    "total_selling_installation_amount",
    "sellingInstallationTotal",
    "selling_installation_total",
    "sellingInstallationAmount"
  );
  const totalInputSupplyAmount = pickPricingMetricFromQuotation(
    item,
    "totalInputSupplyAmount",
    "total_input_supply_amount",
    "inputSupplyTotal",
    "input_supply_total",
    "inputSupplyAmount"
  );
  const totalInputInstallationAmount = pickPricingMetricFromQuotation(
    item,
    "totalInputInstallationAmount",
    "total_input_installation_amount",
    "inputInstallationTotal",
    "input_installation_total",
    "inputInstallationAmount"
  );
  // Realised selling = Working Sheet customer selling (supply + installation), never supply alone.
  const sheetCustomerSellingTotal =
    totalSellingSupplyAmount != null || totalSellingInstallationAmount != null
      ? (totalSellingSupplyAmount ?? 0) + (totalSellingInstallationAmount ?? 0)
      : null;
  const explicitRealizedSelling = pickPricingMetricFromQuotation(
    item,
    "realizedSellingTotal",
    "realized_selling_total",
    "customerSellingTotal",
    "customer_selling_total"
  );
  // If API omits realised or returns supply-only while installation selling exists, use sheet sum.
  const realizedSellingTotal =
    sheetCustomerSellingTotal != null &&
    (
      explicitRealizedSelling == null ||
      (
        totalSellingInstallationAmount != null &&
        totalSellingInstallationAmount !== 0 &&
        explicitRealizedSelling === totalSellingSupplyAmount
      ) ||
      explicitRealizedSelling < sheetCustomerSellingTotal
    )
      ? sheetCustomerSellingTotal
      : (explicitRealizedSelling ?? sheetCustomerSellingTotal);

  return {
    overheadsAmount,
    netProfit,
    netMarginPct: pickPricingMetricFromQuotation(
      item,
      "netMarginPct",
      "net_margin_pct",
      "netMarginPercentage"
    ),
    grossProfit,
    grossMarginPct: pickPricingMetricFromQuotation(
      item,
      "grossMarginPct",
      "gross_margin_pct",
      "grossMarginPercentage"
    ),
    calculatedSellingPrice: pickPricingMetricFromQuotation(
      item,
      "calculatedSellingPrice",
      "calculated_selling_price",
      "finalSellingPrice"
    ),
    realizedGrossMargin: pickPricingMetricFromQuotation(
      item,
      "realizedGrossMargin",
      "realized_gross_margin",
      "realizedGrossMarginPct"
    ),
    realizedSellingTotal,
    totalInputSupplyAmount,
    totalInputInstallationAmount,
    totalSellingSupplyAmount,
    totalSellingInstallationAmount,
  };
}

/**
 * Realised selling from persisted Working Sheet selling totals.
 * Always supply + installation (SITC / SERVICE); never undercount when systems are collapsed.
 */
function resolveBackendRealizedSellingTotal(metrics, quotationType = "Supply") {
  if (!metrics || typeof metrics !== "object") return null;
  const supply = pickFiniteNumber(metrics.totalSellingSupplyAmount);
  const install = supportsInstallation(quotationType)
    ? pickFiniteNumber(metrics.totalSellingInstallationAmount)
    : null;
  if (supply != null || install != null) {
    return (supply ?? 0) + (install ?? 0);
  }
  return pickFiniteNumber(metrics.realizedSellingTotal);
}

function hasBackendPricingMetrics(metrics) {
  if (!metrics || typeof metrics !== "object") return false;
  return (
    metrics.netProfit != null ||
    metrics.grossProfit != null ||
    metrics.calculatedSellingPrice != null ||
    metrics.realizedGrossMargin != null ||
    metrics.overheadsAmount != null ||
    metrics.totalInputSupplyAmount != null ||
    metrics.totalInputInstallationAmount != null ||
    metrics.totalSellingSupplyAmount != null ||
    metrics.totalSellingInstallationAmount != null ||
    metrics.realizedSellingTotal != null
  );
}

/** Excel ROUND — half away from zero — for Working Sheet amount columns. */
function roundExcelWhole(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return numeric >= 0 ? Math.round(numeric) : -Math.round(Math.abs(numeric));
}

function formatCurrencyWhole(value) {
  return `₹${roundExcelWhole(value).toLocaleString("en-IN")}`;
}

/** Whole-number display for Working Sheet rate columns (Excel ROUND). */
function formatRateWhole(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = typeof value === "number"
    ? value
    : Number(String(value).replace(/,/g, "").trim());
  if (!Number.isFinite(numeric)) return "";
  return String(roundExcelWhole(numeric));
}

function isIncompleteDecimalInput(value) {
  const raw = String(value ?? "").trim();
  return raw === "-" || raw === "." || raw === "-." || /[.]$/.test(raw);
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function isExpired(expiry) {
  return Boolean(expiry) && expiry < currentIsoDate();
}

function statusTone(status) {
  switch (status) {
    case "Draft":
      return "draft";
    case "Pending Approval":
      return "pending";
    case "Approved":
      return "approved";
    case "Sent to Customer":
      return "sent";
    case "Under Negotiation":
      return "negotiation";
    case "Accepted":
      return "accepted";
    case "Rejected":
    case "Import Failed":
      return "rejected";
    default:
      return "draft";
  }
}

function normalizeStatusLabel(status) {
  if (status && typeof status === "object") {
    status = status.label ?? status.name ?? status.code ?? status.value ?? status.status ?? "";
  }

  const raw = String(status || "").trim();
  if (!raw) return "";

  const normalized = raw
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();

  if (
    normalized === "pending approval" ||
    normalized === "pending l1 approval" ||
    normalized === "pending l2 approval" ||
    normalized === "pending l3 approval" ||
    (normalized.startsWith("pending ") && normalized.includes("approval"))
  ) {
    return "Pending Approval";
  }

  switch (normalized) {
    case "approved":
      return "Approved";
    case "sent to customer":
      return "Sent to Customer";
    case "under negotiation":
      return "Under Negotiation";
    case "accepted":
      return "Accepted";
    case "rejected":
      return "Rejected";
    case "draft":
      return "Draft";
    case "expired":
      return "Expired";
    case "in progress":
      return "In Progress";
    case "import failed":
      return "Import Failed";
    default:
      // Preserve backend-provided status text instead of forcing Draft.
      return raw
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
}

/** Convert UI/workflow status labels to API status codes for POST .../status. */
function mapQuotationStatusForApi(status) {
  const normalized = normalizeStatusLabel(status) || String(status || "").trim();
  if (!normalized) return "";
  return normalized
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_")
    .toUpperCase();
}

function isAwaitingApprovalNote(note) {
  const text = String(note || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("submitted and locked") ||
    (text.includes("awaiting") && text.includes("approval"))
  );
}

function buildAwaitingApprovalMessage(item = {}) {
  const roleName = String(item.approverRoleName || "Sales Manager").trim() || "Sales Manager";
  const level = String(item.requiredApprovalLevel || "LEVEL_1")
    .replace(/[_-]+/g, " ")
    .trim();
  const levelLabel = /level\s*2/i.test(level)
    ? "Level 2"
    : /level\s*3/i.test(level)
      ? "Level 3"
      : "Level 1";
  return `Submitted and locked — awaiting ${levelLabel} approval from ${roleName} (Default)`;
}

function resolveQuotationNotes(item = {}, status) {
  const rawNote = String(item.managerNote || item.revisionNote || "").trim();

  if (status === "Pending Approval") {
    return {
      submissionNote: rawNote || buildAwaitingApprovalMessage(item),
      revisionNote: "",
    };
  }

  if (status === "Draft" && rawNote && !isAwaitingApprovalNote(rawNote)) {
    return {
      submissionNote: "",
      revisionNote: rawNote,
    };
  }

  return {
    submissionNote: "",
    revisionNote: "",
  };
}

function normalizeQuotationType(type) {
  if (type && typeof type === "object") {
    type = type.label ?? type.name ?? type.code ?? type.value ?? type.type ?? "";
  }

  const normalized = String(type || "").trim().toUpperCase();
  if (normalized.includes("INSTALL") || normalized === "SITC") return "SITC";
  if (normalized === "SERVICE") return "SERVICE";
  return "Supply";
}

function selectLatestQuotationsPerRefId(quotations = []) {
  const latestByRefId = new Map();

  for (const item of quotations) {
    const refKey = String(item?.refId ?? item?.id ?? "").trim().toUpperCase();
    if (!refKey) continue;

    const existing = latestByRefId.get(refKey);
    if (!existing || Number(item.version || 0) > Number(existing.version || 0)) {
      latestByRefId.set(refKey, item);
    }
  }

  return Array.from(latestByRefId.values()).sort((left, right) =>
    String(right.updated || "").localeCompare(String(left.updated || ""))
  );
}

/** Prefer backend versionNo / version / versionNumber; ignore missing or invalid values. */
function resolveBackendVersionNo(item = {}) {
  const raw =
    item?.versionNo ??
    item?.version_number ??
    item?.versionNumber ??
    (item?.version != null && item?.version !== "" ? item.version : null);
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatBackendVersionLabel(item = {}) {
  const backendLabel = String(
    item?.versionLabel ?? item?.version_label ?? item?.versionDisplay ?? ""
  ).trim();
  if (backendLabel) return backendLabel;
  const versionNo = resolveBackendVersionNo(item);
  return versionNo != null ? `V${versionNo}` : "";
}

function resolveLatestBackendVersionNo(versions = []) {
  let latest = null;
  for (const item of versions) {
    const versionNo = resolveBackendVersionNo(item);
    if (versionNo == null) continue;
    if (latest == null || versionNo > latest) latest = versionNo;
  }
  return latest;
}

/** Quotation IDs that already have a newer version (session + history sync). */
const quotationIdsWithCreatedVersion = new Set();

function markQuotationVersionCreated(id) {
  const numericId = extractNumericId(id);
  if (numericId != null) quotationIdsWithCreatedVersion.add(numericId);
}

function hasQuotationCreatedVersion(id) {
  const numericId = extractNumericId(id);
  return numericId != null && quotationIdsWithCreatedVersion.has(numericId);
}

/**
 * From loaded version history, lock Create New Version on every id that is not
 * the true current version — including existing quotations created earlier.
 */
function syncCreatedVersionLocksFromHistory(history = []) {
  if (!Array.isArray(history) || history.length < 2) return;

  const latestNo = resolveLatestBackendVersionNo(history);
  const latestGroup = history.filter((item) => resolveBackendVersionNo(item) === latestNo);
  const latestId = latestGroup.length
    ? Math.max(...latestGroup.map((item) => Number(item?.id) || 0))
    : null;

  history.forEach((item) => {
    const id = extractNumericId(item?.id);
    if (id == null) return;
    const versionNo = resolveBackendVersionNo(item);
    const isCurrent =
      latestNo != null &&
      versionNo === latestNo &&
      (latestId == null || Number(item.id) === latestId);
    if (!isCurrent) markQuotationVersionCreated(id);
  });
}

function isLatestQuotationVersion(quotation, versions = []) {
  if (quotation?.superseded) return false;

  const currentVersionNo = resolveBackendVersionNo(quotation);
  const latestVersionNo = resolveLatestBackendVersionNo(versions);
  if (currentVersionNo != null && latestVersionNo != null) {
    if (currentVersionNo !== latestVersionNo) return false;
    // Multiple rows can share the same version number after a duplicate revise.
    // Only the newest id among that version group is treated as current.
    const sameVersion = (Array.isArray(versions) ? versions : []).filter(
      (item) => resolveBackendVersionNo(item) === latestVersionNo
    );
    if (sameVersion.length > 1) {
      const maxId = Math.max(...sameVersion.map((item) => Number(item?.id) || 0));
      return Number(quotation?.id) === maxId;
    }
    return true;
  }
  // Without backend version numbers, only a single known row can be treated as current.
  if (!versions.length) return true;
  if (versions.length === 1) {
    const only = versions[0];
    if (quotation?.id != null && only?.id != null) {
      return Number(quotation.id) === Number(only.id);
    }
    return true;
  }
  // Multiple rows but no version numbers: newest id wins.
  const maxId = Math.max(...versions.map((item) => Number(item?.id) || 0));
  return Number(quotation?.id) === maxId;
}

function buildQuotationVersionHistory(quotations = [], refId = "") {
  const refKey = String(refId || "").trim().toUpperCase();
  if (!refKey) return [];

  const versions = dedupeQuotationVersions(
    quotations
      .filter((item) => String(item?.refId || "").trim().toUpperCase() === refKey)
      .slice()
      .sort((left, right) => {
        const leftVersion = resolveBackendVersionNo(left) ?? 0;
        const rightVersion = resolveBackendVersionNo(right) ?? 0;
        if (rightVersion !== leftVersion) return rightVersion - leftVersion;
        return Number(right.id || 0) - Number(left.id || 0);
      })
  );

  if (!versions.length) return [];

  const latestVersionNo = resolveLatestBackendVersionNo(versions);
  return versions.map((item) => {
    const versionNo = resolveBackendVersionNo(item);
    return hydrateQuotation({
      ...item,
      version: versionNo ?? item.version,
      superseded:
        latestVersionNo != null && versionNo != null
          ? versionNo < latestVersionNo
          : false,
    });
  });
}

/** Keep one row per quotation id (preferred) or per refId+versionNo. */
function dedupeQuotationVersions(versions = []) {
  const seenIds = new Set();
  const seenVersionKeys = new Set();
  const result = [];

  for (const item of versions) {
    if (!item || typeof item !== "object") continue;

    const id = extractNumericId(item.id ?? item.quotationId);
    const versionNo = resolveBackendVersionNo(item);
    const refKey = String(item.refId || "").trim().toUpperCase();
    const versionKey =
      versionNo != null && refKey ? `${refKey}::V${versionNo}` : "";

    if (id != null && seenIds.has(id)) continue;
    if (versionKey && seenVersionKeys.has(versionKey)) continue;

    if (id != null) seenIds.add(id);
    if (versionKey) seenVersionKeys.add(versionKey);
    result.push(item);
  }

  return result;
}

function matchesQuotationFilters(item, { search = "", statusFilter = "", typeFilter = "" } = {}) {
  const query = String(search || "").trim().toLowerCase();
  const refId = String(item?.refId ?? "").toLowerCase();
  const customer = String(item?.customer ?? "").toLowerCase();
  const versionLabel = String(item?.versionLabel ?? "").toLowerCase();
  const location = String(item?.location ?? item?.locationName ?? "").toLowerCase();

  const matchesSearch =
    !query ||
    refId.includes(query) ||
    customer.includes(query) ||
    versionLabel.includes(query) ||
    location.includes(query);
  const matchesStatus = !statusFilter || item?.status === statusFilter;
  const matchesType = !typeFilter || item?.type === typeFilter;

  return matchesSearch && matchesStatus && matchesType;
}

function extractApiCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.quotations)) return payload.quotations;
  if (Array.isArray(payload?.terms)) return payload.terms;
  if (Array.isArray(payload?.standardTerms)) return payload.standardTerms;
  if (Array.isArray(payload?.pmcConfigs)) return payload.pmcConfigs;
  if (Array.isArray(payload?.pmcCostHeads)) return payload.pmcCostHeads;
  if (Array.isArray(payload?.heads)) return payload.heads;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.terms)) return payload.data.terms;
  if (Array.isArray(payload?.data?.standardTerms)) return payload.data.standardTerms;
  if (Array.isArray(payload?.data?.pmcConfigs)) return payload.data.pmcConfigs;
  if (Array.isArray(payload?.data?.pmcCostHeads)) return payload.data.pmcCostHeads;
  if (Array.isArray(payload?.data?.heads)) return payload.data.heads;
  if (Array.isArray(payload?.approvalRules)) return payload.approvalRules;
  if (Array.isArray(payload?.data?.approvalRules)) return payload.data.approvalRules;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data) && payload.data.id != null) {
    return [payload.data];
  }
  if (payload?.id != null && (payload.sellingPriceMin != null || payload.approvalLevel != null)) {
    return [payload];
  }
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function extractNumericId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parseContentDispositionFilename(headerValue, fallback = "quotation.pdf") {
  if (!headerValue) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const basicMatch = /filename="?([^";]+)"?/i.exec(headerValue);
  return basicMatch?.[1] || fallback;
}

function triggerBrowserDownload(blob, filename) {
  if (typeof window === "undefined") return;
  const href = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(href);
}

function extractObjectPayload(payload) {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) return payload;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) return payload.data;
  return null;
}

function looksLikeQuotationRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return (
    value.id != null ||
    value.quotationId != null ||
    value.refId != null ||
    value.referenceId != null ||
    value.lineItems != null ||
    value.systemSummaries != null ||
    value.productServiceIds != null ||
    value.systemNames != null ||
    value.systemName != null ||
    value.workingSheet != null ||
    value.customerName != null ||
    value.customerId != null
  );
}

/** Unwrap get/{id} envelopes (nested data, content[], or single-item arrays). */
function unwrapQuotationDetailPayload(payload) {
  let current = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (current == null) return {};
    if (Array.isArray(current)) {
      const match = current.find(looksLikeQuotationRecord) || current[0];
      current = match;
      continue;
    }
    if (typeof current !== "object") return {};
    if (looksLikeQuotationRecord(current)) return current;
    if (Array.isArray(current.content) && current.content.length) {
      current = current.content;
      continue;
    }
    if (Array.isArray(current.quotations) && current.quotations.length) {
      current = current.quotations;
      continue;
    }
    if (Array.isArray(current.records) && current.records.length) {
      current = current.records;
      continue;
    }
    if (current.data != null) {
      current = current.data;
      continue;
    }
    break;
  }
  return current && typeof current === "object" && !Array.isArray(current) ? current : {};
}

function mapApiNetPriceRuleToUi(raw = {}) {
  const source =
    extractObjectPayload(raw) ||
    (Array.isArray(raw) ? extractObjectPayload(raw[0]) : null) ||
    (Array.isArray(raw?.content) ? extractObjectPayload(raw.content[0]) : null) ||
    {};

  const percentage = firstDefined(
    source.netPricePercentage,
    source.net_price_percentage,
    source.percentage,
    source.netPricePct,
    source.value
  );

  return {
    id: source.id ?? "",
    tenantId: String(source.tenantId ?? source.tenant_id ?? ""),
    netPricePercentage:
      percentage === undefined || percentage === null || percentage === ""
        ? ""
        : String(percentage),
  };
}

function firstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function hasQuotationDetailPayload(payload) {
  if (!payload) return false;
  if (Array.isArray(payload)) return payload.length > 0;
  if (Array.isArray(payload?.data)) return payload.data.length > 0;
  if (extractObjectPayload(payload)) return true;
  return false;
}

function parseApiBoolean(value, defaultValue = true) {
  if (value === false || value === "false" || value === 0 || value === "0") return false;
  if (value === true || value === "true" || value === 1 || value === "1") return true;
  if (value === undefined || value === null) return defaultValue;
  return Boolean(value);
}

function mapApiApprovalRuleToUi(rule = {}, index = 0) {
  const approvalLevel = String(rule.approvalLevel || "").trim();
  const autoApprove =
    rule.autoApprove != null
      ? parseApiBoolean(rule.autoApprove, false)
      : rule.auto_approve != null
        ? parseApiBoolean(rule.auto_approve, false)
        : rule.needsApproval != null
          ? !parseApiBoolean(rule.needsApproval, true)
          : !approvalLevel ||
            approvalLevel.toUpperCase() === "AUTO_APPROVE" ||
            approvalLevel.toUpperCase() === "AUTO-APPROVE";

  return {
    id: rule.id ?? `rule-${index + 1}`,
    sellingPriceMin: formatRuleEditableValue(rule.sellingPriceMin),
    sellingPriceMax: formatRuleEditableValue(rule.sellingPriceMax),
    marginMin: formatRuleEditableValue(rule.marginMin),
    marginMax: formatRuleEditableValue(rule.marginMax),
    approvalLevel,
    displayOrder: Number(rule.displayOrder ?? index + 1),
    isActive: parseApiBoolean(rule.isActive, true),
    autoApprove,
  };
}

function formatRuleEditableValue(value) {
  const parsed = Number(value);
  if (value === "" || value === null || value === undefined) return "";
  if (Number.isFinite(parsed) && parsed === 0) return "";
  return String(value);
}

function parseRuleEditableValue(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed || trimmed.toLowerCase() === "any") return "";
  return trimmed;
}

function buildApprovalRulePayload(rule = {}) {
  const autoApprove = Boolean(rule.autoApprove);
  const numericId = extractNumericId(rule.id);
  const payload = {
    sellingPriceMin: Number(parseRuleEditableValue(rule.sellingPriceMin)) || 0,
    sellingPriceMax: Number(parseRuleEditableValue(rule.sellingPriceMax)) || 0,
    marginMin: Number(parseRuleEditableValue(rule.marginMin)) || 0,
    marginMax: Number(parseRuleEditableValue(rule.marginMax)) || 0,
    approvalLevel: autoApprove ? "" : String(rule.approvalLevel || "").trim(),
    displayOrder: Number(rule.displayOrder) || 1,
    isActive: Boolean(rule.isActive),
    autoApprove,
  };

  // Only include id for existing rules (edit). New creates must not send id: 0.
  if (numericId) {
    payload.id = numericId;
  }

  return payload;
}

function extractApiApprovalRules(payload) {
  if (!payload) return [];
  const collection = extractApiCollection(payload);
  if (collection.length) {
    return collection.map((rule, index) => mapApiApprovalRuleToUi(rule, index));
  }
  const single = extractObjectPayload(payload);
  if (single && single.id != null) {
    return [mapApiApprovalRuleToUi(single)];
  }
  return [];
}

function dedupeApprovalRules(rules = []) {
  const grouped = new Map();

  for (const rule of rules) {
    const key = [
      rule.displayOrder,
      rule.sellingPriceMin,
      rule.sellingPriceMax,
      rule.marginMin,
      rule.marginMax,
      rule.approvalLevel,
      rule.autoApprove ? "1" : "0",
      rule.isActive ? "1" : "0",
    ].join("|");
    const existing = grouped.get(key);
    const ruleId = Number(rule.id) || 0;
    const existingId = Number(existing?.id) || 0;
    if (!existing || ruleId >= existingId) {
      grouped.set(key, rule);
    }
  }

  return sortApprovalRules(Array.from(grouped.values()));
}

function mapApiApprovalRuleResponse(response) {
  const saved =
    extractObjectPayload(response?.data) ||
    extractObjectPayload(response) ||
    null;
  return saved ? mapApiApprovalRuleToUi(saved) : null;
}

async function persistApprovalRule(rule, { previousId = 0 } = {}) {
  const resolvedPreviousId = extractNumericId(previousId) || 0;
  const isNewRule = Boolean(rule?.isDraft) || !resolvedPreviousId;
  const payload = buildApprovalRulePayload(
    isNewRule ? { ...rule, id: undefined } : { ...rule, id: resolvedPreviousId }
  );
  const response = await createApprovalRule(payload);
  const savedRule = mapApiApprovalRuleResponse(response);
  const savedId = extractNumericId(savedRule?.id) || 0;

  if (resolvedPreviousId > 0 && savedId > 0 && resolvedPreviousId !== savedId) {
    try {
      await deleteApprovalRule(resolvedPreviousId);
    } catch {
      // New rule was saved; stale duplicate cleanup can be retried on refresh.
    }
  }

  return savedRule;
}

function resolveDefaultApproverLabel(options = []) {
  const salesManager = options.find((option) =>
    String(option || "").trim().toLowerCase().includes("sales manager")
  );
  return salesManager || options[0] || i18n.t("salesQuotation.salesManager");
}

const APPROVAL_RULE_KIND_OPTIONS = [
  { value: "selling", label: "Realised Selling" },
  { value: "margin", label: "Realised Gross Margin" },
  { value: "both", label: "Both" },
];

function createEmptyApprovalRuleDraft(approverOptions = []) {
  return {
    ruleKind: "",
    sellingPriceMin: "",
    sellingPriceMax: "",
    marginMin: "",
    marginMax: "",
    needsApproval: true,
    approvalLevel: approverOptions[0] || "",
    isActive: true,
  };
}

function parseRuleBoundValue(value) {
  return String(parseRuleEditableValue(value) || "").trim();
}

function ruleHasSellingBounds(rule = {}) {
  return Boolean(parseRuleBoundValue(rule.sellingPriceMin) || parseRuleBoundValue(rule.sellingPriceMax));
}

function ruleHasMarginBounds(rule = {}) {
  return Boolean(parseRuleBoundValue(rule.marginMin) || parseRuleBoundValue(rule.marginMax));
}

function inferApprovalRuleKind(rule = {}) {
  const hasSelling = ruleHasSellingBounds(rule);
  const hasMargin = ruleHasMarginBounds(rule);
  if (hasSelling && hasMargin) return "both";
  if (hasMargin && !hasSelling) return "margin";
  return "selling";
}

function formatRuleThresholdSummary(greaterValue, lessValue, { currency = false, percent = false } = {}) {
  const greater = parseRuleBoundValue(greaterValue);
  const less = parseRuleBoundValue(lessValue);
  const prefix = currency ? "₹" : "";
  const suffix = percent ? "%" : "";
  const formatPart = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return `${prefix}${value}${suffix}`;
    return `${prefix}${numeric.toLocaleString("en-IN")}${suffix}`;
  };
  if (!greater && !less) return "Any";
  if (greater && less) return `≥ ${formatPart(greater)} · ≤ ${formatPart(less)}`;
  if (greater) return `≥ ${formatPart(greater)}`;
  return `≤ ${formatPart(less)}`;
}

function buildApprovalRulesFromDraft(draft = {}, { startOrder = 1 } = {}) {
  const kind = String(draft.ruleKind || "").trim();
  if (!kind) return [];

  const shared = {
    autoApprove: !draft.needsApproval,
    approvalLevel: draft.needsApproval ? String(draft.approvalLevel || "").trim() : "",
    displayOrder: Number(startOrder) || 1,
    isActive: Boolean(draft.isActive),
    isDraft: true,
  };

  if (kind === "selling") {
    return [
      {
        ...shared,
        sellingPriceMin: draft.sellingPriceMin,
        sellingPriceMax: draft.sellingPriceMax,
        marginMin: "",
        marginMax: "",
      },
    ];
  }

  if (kind === "margin") {
    return [
      {
        ...shared,
        sellingPriceMin: "",
        sellingPriceMax: "",
        marginMin: draft.marginMin,
        marginMax: draft.marginMax,
      },
    ];
  }

  // Both: one rule with both condition sets and a single shared Approver.
  return [
    {
      ...shared,
      sellingPriceMin: draft.sellingPriceMin,
      sellingPriceMax: draft.sellingPriceMax,
      marginMin: draft.marginMin,
      marginMax: draft.marginMax,
    },
  ];
}

function sortApprovalRules(rules = []) {
  return [...rules]
    .sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder))
    .map((rule, index) => ({ ...rule, displayOrder: index + 1 }));
}

function formatRuleBound(value, prefix = "") {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? `${prefix}${parsed.toLocaleString("en-IN")}` : "Any";
}

function formatRulePriceRange(rule = {}) {
  const min = formatRuleBound(rule.sellingPriceMin, ">= Rs ");
  const max = formatRuleBound(rule.sellingPriceMax, "<= Rs ");
  if (min === "Any" && max === "Any") return "Any";
  if (min !== "Any" && max !== "Any") return `${min} / ${max}`;
  return min !== "Any" ? min : max;
}

function formatApprovalLevelLabel(value) {
  return String(value || "").trim() || i18n.t("common.none");
}

function quotationIsEditable(status) {
  return String(status || "").trim().toLowerCase() === "draft";
}

function ruleStatusLabel(rule) {
  return rule.isActive ? "Active" : "Inactive";
}

function inferPmcCategory(name = "") {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "";
  return trimmed.split("(")[0].trim();
}

function buildPmcHeadPresentation(headName = "", category = "") {
  const resolvedCategory = String(category || inferPmcCategory(headName)).trim();
  const normalizedCategory = resolvedCategory.toLowerCase();
  const isManpower = normalizedCategory.includes("manpower");

  return {
    category: resolvedCategory,
    description: isManpower
      ? "Roles for this head. Nos, months and CTC are entered per-quotation in Cost + Pricing."
      : "Line items for this head. Quantities and rates are entered per-quotation in Cost + Pricing.",
  };
}

function normalizePmcColumnsInput(value, fallback = []) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const objectValues = Object.values(value).filter(Boolean);
    if (objectValues.length) return objectValues;
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return normalizePmcColumnsInput(parsed, fallback);
    } catch {
      if (value.includes("\n")) {
        return normalizePmcColumnsInput(value.split("\n").map((line) => line.trim()).filter(Boolean), fallback);
      }
      if (value.includes(",")) {
        return normalizePmcColumnsInput(value.split(",").map((part) => part.trim()).filter(Boolean), fallback);
      }
    }
  }

  if (Array.isArray(value) && value.length) return value.filter(Boolean);
  return Array.isArray(fallback) ? fallback.filter(Boolean) : [];
}

function resolvePmcColumnLabel(column, index = 0) {
  if (typeof column === "string") return column.trim();
  if (column && typeof column === "object") {
    return String(
      column.label ??
      column.name ??
      column.title ??
      column.header ??
      column.columnName ??
      column.displayName ??
      column.fieldLabel ??
      column.columnLabel ??
      column.text ??
      column.key ??
      ""
    ).trim() || `Column ${index + 1}`;
  }
  return `Column ${index + 1}`;
}

function resolvePmcColumnFieldKey(column, index = 0) {
  if (column && typeof column === "object") {
    const explicitKey = String(
      column.fieldKey ??
      column.field ??
      column.dataKey ??
      column.binding ??
      column.key ??
      ""
    ).trim();
    if (explicitKey && !/^(label|name|title|header|columnName|displayName)$/i.test(explicitKey)) {
      return explicitKey;
    }
  }
  return inferPmcColumnFieldKey(resolvePmcColumnLabel(column, index), index);
}

function normalizePmcColumnLabels(value) {
  return normalizePmcColumnsInput(value, [])
    .map((column, index) => resolvePmcColumnLabel(column, index))
    .filter(Boolean);
}

function resolvePmcLineLabel(row = {}) {
  if (typeof row === "string") return String(row || "").trim();
  return String(
    row?.lineName ??
    row?.description ??
    row?.name ??
    row?.label ??
    row?.item ??
    row?.title ??
    row?.rowName ??
    row?.itemName ??
    row?.value ??
    row?.text ??
    ""
  ).trim();
}

function resolvePmcLineId(row = {}) {
  if (!row || typeof row !== "object") return null;

  // Prefer explicit line-item id fields over generic `id` (some payloads reuse `id` for the cost head).
  const lineSpecificId = extractNumericId(
    row.lineId ??
    row.pmcLineId ??
    row.configLineId ??
    row.pmcConfigLineId ??
    row.line_id ??
    row.pmc_line_id
  );
  if (lineSpecificId) return lineSpecificId;

  const id = extractNumericId(row.id);
  const configId = extractNumericId(
    row.pmcConfigId ??
    row.configId ??
    row.pmc_config_id ??
    row.costHeadId
  );
  if (id && configId && id === configId) return null;
  return id;
}

function isPmcLineActive(row = {}) {
  if (typeof row === "string") return Boolean(String(row || "").trim());
  const candidates = [row.isActive, row.active, row.status, row.lineStatus];
  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    if (value === false || value === 0) return false;
    const text = String(value).trim().toLowerCase();
    if (["inactive", "false", "0", "n", "no", "deactivated", "disabled"].includes(text)) return false;
    if (["active", "true", "1", "y", "yes", "enabled"].includes(text)) return true;
  }
  return true;
}

function createBlankPmcLine(overrides = {}) {
  return {
    clientId: overrides.clientId || `pmc-line-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
    id: overrides.id ?? null,
    label: overrides.label ?? "",
    dirty: Boolean(overrides.dirty),
    isNew: overrides.isNew !== false && !Number(overrides.id),
    isActive: overrides.isActive !== false,
    boqQty: overrides.boqQty != null ? String(overrides.boqQty) : "",
    unitRate: overrides.unitRate != null ? String(overrides.unitRate) : "",
    consQty: overrides.consQty != null ? String(overrides.consQty) : "",
    nos: overrides.nos != null ? String(overrides.nos) : "",
    months: overrides.months != null ? String(overrides.months) : "",
    ctc: overrides.ctc != null ? String(overrides.ctc) : "",
  };
}

/** Metric fields for the selected Head Type (Cost Head level; not used for LUMPSUM). */
function getPmcHeadTypeMetricFields(costHeadType) {
  const type = canonicalizePmcHeadType(costHeadType);
  if (type === "BOQ_QTY_BASED") {
    return [
      { key: "boqQty", label: "BOQ Qty" },
      { key: "unitRate", label: "Unit Rate" },
      { key: "consQty", label: "Consumed Qty" },
    ];
  }
  if (type === "MANPOWER_MONTHLY") {
    return [
      { key: "nos", label: "Nos" },
      { key: "months", label: "Months" },
      { key: "ctc", label: "CTC" },
    ];
  }
  return [];
}

/** @deprecated alias — prefer getPmcHeadTypeMetricFields */
function getPmcLineTemplateFields(costHeadType) {
  return getPmcHeadTypeMetricFields(costHeadType);
}

function emptyPmcHeadMetrics() {
  return {
    boqQty: "",
    unitRate: "",
    consQty: "",
    nos: "",
    months: "",
    ctc: "",
  };
}

function hasPmcMetricValue(value) {
  return String(value ?? "").trim() !== "";
}

/** First non-empty metric among candidate values (skips null/undefined/""). */
function firstPmcMetricValue(...values) {
  for (const value of values) {
    if (hasPmcMetricValue(value)) return String(value).trim();
  }
  return "";
}

function pickPmcLineMetricFields(row = {}, costHeadType = "") {
  const type = canonicalizePmcHeadType(costHeadType);
  const boqQty = firstPmcMetricValue(
    row?.boqQtyDefault,
    row?.boq_qty_default,
    row?.boqQty,
    row?.boq_qty
  );
  const unitRate = firstPmcMetricValue(
    row?.unitRateDefault,
    row?.unit_rate_default,
    row?.unitRate,
    row?.unit_rate,
    type === "BOQ_QTY_BASED" ? row?.rate : ""
  );
  const consQty = firstPmcMetricValue(
    row?.consumedQtyDefault,
    row?.consumed_qty_default,
    row?.consumedQty,
    row?.consumed_qty,
    row?.consQty,
    row?.cons_qty
  );
  const nos = firstPmcMetricValue(row?.nosDefault, row?.nos_default, row?.nos);
  const months = firstPmcMetricValue(row?.monthsDefault, row?.months_default, row?.months);
  const ctc = firstPmcMetricValue(
    row?.ctcDefault,
    row?.ctc_default,
    row?.ctc,
    row?.CTC,
    type === "MANPOWER_MONTHLY" ? row?.rate : "",
    type === "MANPOWER_MONTHLY" ? row?.unitRate : ""
  );
  return { boqQty, unitRate, consQty, nos, months, ctc };
}

function pickPmcHeadMetricFields(head = {}, costHeadType = "", fallbackLine = null) {
  const type = canonicalizePmcHeadType(costHeadType);
  // Prefer backend *Default fields on the Cost Head, then local UI keys.
  const fromDefaults = {
    boqQty: firstPmcMetricValue(
      head?.boqQtyDefault,
      head?.boq_qty_default,
      head?.boqQty,
      head?.boq_qty
    ),
    unitRate: firstPmcMetricValue(
      head?.unitRateDefault,
      head?.unit_rate_default,
      head?.unitRate,
      head?.unit_rate,
      type === "BOQ_QTY_BASED" ? head?.rate : ""
    ),
    consQty: firstPmcMetricValue(
      head?.consumedQtyDefault,
      head?.consumed_qty_default,
      head?.consQtyDefault,
      head?.consQty,
      head?.consumedQty,
      head?.cons_qty
    ),
    nos: firstPmcMetricValue(head?.nosDefault, head?.nos_default, head?.nos),
    months: firstPmcMetricValue(head?.monthsDefault, head?.months_default, head?.months),
    ctc: firstPmcMetricValue(
      head?.ctcDefault,
      head?.ctc_default,
      head?.ctc,
      head?.CTC,
      type === "MANPOWER_MONTHLY" ? head?.rate : ""
    ),
  };

  if (Object.values(fromDefaults).some((value) => hasPmcMetricValue(value))) {
    return fromDefaults;
  }
  if (fallbackLine) return pickPmcLineMetricFields(fallbackLine, costHeadType);
  return emptyPmcHeadMetrics();
}

/** Cost Head create/update payload metrics — backend *Default field names only. */
function buildPmcHeadMetricPayload(metrics = {}, costHeadType = "") {
  const type = canonicalizePmcHeadType(costHeadType);
  if (type === "BOQ_QTY_BASED") {
    return {
      boqQtyDefault: parseCurrency(metrics.boqQty),
      unitRateDefault: parseCurrency(metrics.unitRate),
      consumedQtyDefault: parseCurrency(metrics.consQty),
    };
  }
  if (type === "MANPOWER_MONTHLY") {
    return {
      nosDefault: parseCurrency(metrics.nos),
      monthsDefault: parseCurrency(metrics.months),
      // CTC is the rate/value — do not send a separate `rate` field.
      ctcDefault: parseCurrency(metrics.ctc),
    };
  }
  return {};
}

/** Copy Cost Head-level metric values onto every PMC line. */
function applyHeadMetricsToLines(lines = [], headMetrics = {}, costHeadType = "", { markDirty = false } = {}) {
  const metrics = pickPmcLineMetricFields(headMetrics, costHeadType);
  return normalizePmcLineEntries(lines, costHeadType).map((line) =>
    createBlankPmcLine({
      ...line,
      ...metrics,
      dirty: markDirty ? true : Boolean(line.dirty),
      isNew: line.isNew,
    })
  );
}

function normalizePmcLineEntries(value, costHeadType = "") {
  if (Array.isArray(value)) {
    return value.map((row, index) => {
      if (typeof row === "string") {
        return createBlankPmcLine({
          clientId: `pmc-line-str-${index + 1}`,
          label: row,
          dirty: false,
          isNew: true,
          isActive: true,
        });
      }
      const numericId = resolvePmcLineId(row);
      const metrics = pickPmcLineMetricFields(row, costHeadType);
      return createBlankPmcLine({
        clientId: row?.clientId || (numericId ? `pmc-line-${numericId}` : `pmc-line-${index + 1}`),
        id: numericId || null,
        label: resolvePmcLineLabel(row),
        dirty: Boolean(row?.dirty),
        isNew: !numericId,
        isActive: isPmcLineActive(row),
        ...metrics,
      });
    });
  }
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizePmcLineEntries(parsed, costHeadType);
    } catch {
      /* plain newline / comma text */
    }
    if (value.includes("\n")) {
      return normalizePmcLineEntries(
        value.split("\n").map((line) => line.trim()).filter(Boolean),
        costHeadType
      );
    }
    if (value.includes(",")) {
      return normalizePmcLineEntries(
        value.split(",").map((part) => part.trim()).filter(Boolean),
        costHeadType
      );
    }
    return normalizePmcLineEntries([value.trim()], costHeadType);
  }
  return [];
}

function normalizePmcRowLabels(value) {
  return normalizePmcLineEntries(value).map((row) => row.label);
}

function buildPmcConfigLineEntry(pmcConfigId, line = {}, { isUpdate = false, costHeadType = "" } = {}) {
  const lineId = Number(line.id);
  const type = canonicalizePmcHeadType(costHeadType);
  const entry = {
    id: isUpdate && Number.isFinite(lineId) && lineId > 0 ? lineId : 0,
    pmcConfigId: Number(pmcConfigId) || 0,
    lineName: String(line.label || "").trim(),
    isActive: line.isActive !== false,
  };

  // Same Head Type default field names as Cost Head payload (values copied from head level).
  if (type === "BOQ_QTY_BASED") {
    entry.boqQtyDefault = parseCurrency(line.boqQty);
    entry.unitRateDefault = parseCurrency(line.unitRate);
    entry.consumedQtyDefault = parseCurrency(line.consQty);
  } else if (type === "MANPOWER_MONTHLY") {
    entry.nosDefault = parseCurrency(line.nos);
    entry.monthsDefault = parseCurrency(line.months);
    // CTC is the rate/value — send ctcDefault only (no separate rate/ctc).
    entry.ctcDefault = parseCurrency(line.ctc);
  }
  // LUMPSUM: no additional line metric fields.

  return entry;
}

function buildPmcConfigLinesPayload(pmcConfigId, lines = [], { isUpdate = false, costHeadType = "" } = {}) {
  const configId = Number(pmcConfigId) || 0;
  return {
    // Root id is always the PMC cost head / config id.
    id: configId,
    lines: (Array.isArray(lines) ? lines : []).map((line) =>
      buildPmcConfigLineEntry(configId, line, { isUpdate, costHeadType })
    ),
  };
}

const PMC_HEAD_TYPE_COLUMN_SCHEMAS = {
  MANPOWER_MONTHLY: [
    { key: "description", label: "Description", readonly: true, computed: false },
    { key: "nos", label: "Nos", readonly: false, computed: false },
    { key: "months", label: "Months", readonly: false, computed: false },
    { key: "rate", label: "CTC", readonly: false, computed: false },
    { key: "totalCost", label: "Total", readonly: true, computed: true },
  ],
  BOQ_QTY_BASED: [
    { key: "description", label: "Description", readonly: true, computed: false },
    { key: "boqQty", label: "BOQ Qty", readonly: false, computed: false },
    { key: "unitRate", label: "Unit Rate", readonly: false, computed: false },
    { key: "consQty", label: "Consumed Qty", readonly: false, computed: false },
    { key: "totalCost", label: "Amount", readonly: true, computed: true },
  ],
  LUMPSUM: [
    { key: "description", label: "Description", readonly: true, computed: false },
    { key: "totalCost", label: "Amount", readonly: false, computed: false },
  ],
};

function canonicalizePmcHeadType(value) {
  const text = String(Array.isArray(value) ? value[0] : value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (!text) return "";
  if (text === "MANPOWER_MONTHLY" || text.includes("MANPOWER")) return "MANPOWER_MONTHLY";
  if (text === "BOQ_QTY_BASED" || text.includes("BOQ")) return "BOQ_QTY_BASED";
  if (text === "LUMPSUM" || text.includes("LUMP")) return "LUMPSUM";
  return "";
}

function resolvePmcColumnsForHeadType(head = {}) {
  const type = canonicalizePmcHeadType(head.costHeadType ?? head.cost_head_type ?? head.headType);
  const schema = type ? PMC_HEAD_TYPE_COLUMN_SCHEMAS[type] : null;
  return schema ? schema.map((column) => ({ ...column })) : null;
}

function pickPmcColumnsFromCostHeadType(head = {}) {
  const typed = resolvePmcColumnsForHeadType(head);
  if (typed?.length) return typed;

  const value = head.costHeadType ?? head.cost_head_type;
  if (Array.isArray(value)) {
    // Ignore bare enum tokens so C+P can fall through to API columns when needed.
    const labels = value.map((item) => String(item || "").trim()).filter(Boolean);
    if (labels.length === 1 && canonicalizePmcHeadType(labels[0])) return [];
    return labels.filter((item) => !canonicalizePmcHeadType(item));
  }
  if (typeof value === "string" && value.trim()) {
    if (canonicalizePmcHeadType(value)) return [];
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function resolvePmcDynamicColumns(head = {}) {
  const typed = resolvePmcColumnsForHeadType(head);
  if (typed?.length) return typed;
  const fromCostHeadType = pickPmcColumnsFromCostHeadType(head);
  if (fromCostHeadType.length) return fromCostHeadType;
  return pickPmcColumnsFromApi(head);
}

function pickPmcColumnsFromApi(head = {}) {
  const direct = normalizePmcColumnsInput(
    head.columns ??
    head.columnLabels ??
    head.breakdownColumns ??
    head.headers ??
    head.columnDefinitions ??
    head.columnDefs ??
    head.breakdownStructure?.columns ??
    head.breakdown?.columns ??
    head.config?.columns,
    []
  );
  if (direct.length) return direct;

  if (head.breakdownStructure && typeof head.breakdownStructure === "object") {
    const nested = pickPmcColumnsFromApi(head.breakdownStructure);
    if (nested.length) return nested;
  }

  for (const [key, value] of Object.entries(head || {})) {
    if (
      !/(column|header|breakdown)/i.test(key) ||
      ["defaultRows", "rows", "items", "lineItems"].includes(key) ||
      !Array.isArray(value) ||
      !value.length
    ) {
      continue;
    }
    const columns = normalizePmcColumnsInput(value, []);
    if (!columns.length) continue;
    const looksLikeColumns = columns.every((column) => {
      if (typeof column === "string") return Boolean(column.trim());
      return Boolean(
        column?.label ||
        column?.name ||
        column?.title ||
        column?.header ||
        column?.columnName ||
        column?.displayName ||
        column?.fieldLabel ||
        column?.columnLabel
      );
    });
    if (looksLikeColumns) return columns;
  }

  return [];
}

function mapPmcConfigStatus(head = {}) {
  const candidates = [head.isActive, head.active, head.status, head.headStatus];
  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    if (value === false || value === 0) return "Inactive";
    const text = String(value).trim().toLowerCase();
    if (["inactive", "false", "0", "n", "no", "deactivated", "disabled"].includes(text)) return "Inactive";
    if (["active", "true", "1", "y", "yes", "enabled"].includes(text)) return "Active";
  }
  return "Active";
}

function isPmcCostHeadActive(head = {}) {
  return mapPmcConfigStatus(head) === "Active";
}

const PMC_COST_HEAD_TYPE_OPTIONS = ["BOQ_QTY_BASED", "MANPOWER_MONTHLY", "LUMPSUM"];

function createEmptyPmcCreateForm() {
  return {
    headName: "",
    costHeadType: "",
    defaultRows: [createBlankPmcLine({ dirty: true, isNew: true })],
    isActive: true,
    ...emptyPmcHeadMetrics(),
  };
}

function buildPmcCreateApiPayload(formData = {}) {
  const costHeadType = String(formData.costHeadType || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .join(", ");
  const metrics = pickPmcHeadMetricFields(formData, costHeadType);
  const defaultRows = normalizePmcLineEntries(formData.defaultRows, costHeadType)
    .map((row) => String(row.label || "").trim())
    .filter(Boolean);

  return {
    id: 0,
    headName: String(formData.headName || "").trim(),
    costHeadType,
    defaultRows,
    isActive: formData.isActive !== false,
    ...buildPmcHeadMetricPayload(metrics, costHeadType),
  };
}

function isPersistedPmcHead(head) {
  const numericId = Number(head?.id);
  return Number.isFinite(numericId) && numericId > 0;
}

function pickPmcRowsFromApi(head = {}, { activeOnly = false } = {}) {
  const sources = [
    head.lines,
    head.pmcLines,
    head.configLines,
    head.lineItems,
    head.defaultRows,
    head.rows,
    head.breakdownRows,
    head.items,
    head.defaultItems,
    head.rowLabels,
    head.breakdownItems,
    head.defaultRowLabels,
    head.rowNames,
    head.breakdown,
    head.configRows,
  ];

  for (const source of sources) {
    let rows = normalizePmcLineEntries(source).filter((row) => row.label || row.id);
    if (activeOnly) {
      rows = rows.filter((row) => row.isActive !== false);
    }
    if (rows.length) return rows;
  }

  // Fallback: detect any array of row-like values on the API object.
  for (const [key, value] of Object.entries(head || {})) {
    if (
      ["columns", "columnLabels", "breakdownColumns", "headers"].includes(key) ||
      !Array.isArray(value) ||
      !value.length
    ) {
      continue;
    }
    let rows = normalizePmcLineEntries(value).filter((row) => row.label || row.id);
    if (activeOnly) {
      rows = rows.filter((row) => row.isActive !== false);
    }
    if (rows.length) return rows;
  }

  return [];
}

function normalizePmcCostHeadType(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean).join(", ");
  }
  return String(value || "").trim();
}

function mapApiPmcConfigToUi(head = {}, index = 0) {
  const presentation = buildPmcHeadPresentation(head.headName || head.name, head.category);
  const costHeadType = normalizePmcCostHeadType(head.costHeadType) || head.category || presentation.category || "";
  const pickedRows = pickPmcRowsFromApi(head);
  const headMetrics = pickPmcHeadMetricFields(head, costHeadType, pickedRows[0] || null);
  const defaultRows = pickedRows.map((row) => {
    const lineMetrics = pickPmcLineMetricFields(row, costHeadType);
    const mergedMetrics = {
      boqQty: hasPmcMetricValue(lineMetrics.boqQty) ? lineMetrics.boqQty : headMetrics.boqQty,
      unitRate: hasPmcMetricValue(lineMetrics.unitRate) ? lineMetrics.unitRate : headMetrics.unitRate,
      consQty: hasPmcMetricValue(lineMetrics.consQty) ? lineMetrics.consQty : headMetrics.consQty,
      nos: hasPmcMetricValue(lineMetrics.nos) ? lineMetrics.nos : headMetrics.nos,
      months: hasPmcMetricValue(lineMetrics.months) ? lineMetrics.months : headMetrics.months,
      ctc: hasPmcMetricValue(lineMetrics.ctc) ? lineMetrics.ctc : headMetrics.ctc,
    };
    return createBlankPmcLine({
      ...row,
      ...mergedMetrics,
      dirty: false,
      isNew: !row.id,
    });
  });
  const rawColumns = resolvePmcDynamicColumns(head);
  const columns = normalizePmcColumnLabels(rawColumns);

  return {
    id: head.id ?? head.pmcConfigId ?? `pmc-${index + 1}`,
    category: head.category || presentation.category,
    costHeadType,
    applicableQuotationType: head.applicableQuotationType || head.quotationType || "All",
    name: head.headName || head.name || `PMC Head ${index + 1}`,
    status: mapPmcConfigStatus(head),
    order: Number(head.displayOrder ?? head.order ?? index + 1),
    columns,
    rawColumns,
    ...headMetrics,
    defaultRows: defaultRows.length
      ? normalizePmcLineEntries(defaultRows, costHeadType)
      : [createBlankPmcLine({ dirty: false, isNew: true, ...headMetrics })],
    description: head.description || presentation.description,
    dirty: false,
  };
}

function extractApiPmcConfigs(payload) {
  const direct = extractApiCollection(payload);
  if (direct.length) return direct.map(mapApiPmcConfigToUi);

  const nestedKeys = ["pmcConfigs", "configs", "pmcCostHeads", "heads", "pmcHeadList"];
  for (const key of nestedKeys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key].map(mapApiPmcConfigToUi);
    }
    if (Array.isArray(payload?.data?.[key])) {
      return payload.data[key].map(mapApiPmcConfigToUi);
    }
  }

  const single = extractObjectPayload(payload);
  if (single && (single.headName || single.name || single.id != null) && !Array.isArray(single)) {
    return [mapApiPmcConfigToUi(single)];
  }

  return [];
}

function buildPmcConfigPayload(head = {}) {
  const presentation = buildPmcHeadPresentation(head.name, head.category);
  const category = String(
    head.category || presentation.category || inferPmcCategory(head.name) || ""
  ).trim();
  const costHeadType = normalizePmcCostHeadType(head.costHeadType) || category;
  const metrics = pickPmcHeadMetricFields(head, costHeadType);

  return {
    id: Number(head.id) || 0,
    category,
    headName: String(head.name || "").trim(),
    costHeadType,
    applicableQuotationType: String(head.applicableQuotationType || "All").trim(),
    isActive: head.status !== "Inactive",
    ...buildPmcHeadMetricPayload(metrics, costHeadType),
  };
}

function applySavedRowsToHeads(heads = [], savedRowsById = {}) {
  return heads.map((head) => {
    const key = String(head.id);
    const costHeadType = head.costHeadType || head.category || "";
    const savedRows = normalizePmcLineEntries(savedRowsById[key], costHeadType)
      .filter((row) => row.label || row.id);
    const apiRows = normalizePmcLineEntries(head.defaultRows, costHeadType)
      .filter((row) => row.label || row.id);
    if (apiRows.length) {
      return { ...head, defaultRows: apiRows, dirty: false };
    }
    if (savedRows.length) {
      return { ...head, defaultRows: savedRows, dirty: false };
    }
    return {
      ...head,
      defaultRows: Array.isArray(head.defaultRows) && head.defaultRows.length
        ? normalizePmcLineEntries(head.defaultRows, costHeadType)
        : [createBlankPmcLine({ dirty: false, isNew: true })],
      dirty: false,
    };
  });
}

function mapApiTermToUi(term = {}, index = 0) {
  const numericId = extractNumericId(term.id ?? term.termId ?? term.term_id);

  return {
    id: numericId ?? `term-${index + 1}`,
    title: term.title || term.name || `Term ${index + 1}`,
    text: term.description || term.text || "",
    order: Number(term.displayOrder ?? term.order ?? index + 1),
    status: term.isActive === false || term.status === "Inactive" ? "Inactive" : "Active",
    quotationTypes: normalizeTermQuotationTypes(term),
  };
}

function sortTermsForUi(terms = []) {
  return terms
    .sort((a, b) => Number(a.order) - Number(b.order))
    .map((item, index) => ({ ...item, order: index + 1 }));
}

const TERM_QUOTATION_TYPE_VALUES = ["Supply", "SITC", "SERVICE"];

/** Expand one API/UI type token into zero or more canonical quotation types (OR multiselect). */
function expandTermQuotationTypeTokens(value) {
  if (value && typeof value === "object") {
    return expandTermQuotationTypeTokens(
      value.label ?? value.name ?? value.code ?? value.value ?? value.type ?? ""
    );
  }

  const text = String(value || "").trim();
  if (!text) return [];

  const splitParts = text
    .split(/[,|;+/]|\band\b/i)
    .map((part) => String(part || "").trim())
    .filter((part) => part && !/^and$/i.test(part));

  if (splitParts.length > 1) {
    return splitParts.flatMap(expandTermQuotationTypeTokens);
  }

  const compact = text.toUpperCase().replace(/[\s_-]+/g, "");
  if (compact === "SUPPLYSITC" || compact === "SUPPLYANDSITC") {
    return ["Supply", "SITC"];
  }
  if (compact === "SERVICE") return ["SERVICE"];
  if (compact === "SITC") return ["SITC"];
  if (compact === "SUPPLY") return ["Supply"];

  const single = normalizeQuotationType(text);
  return single ? [single] : [];
}

function canonicalizeTermQuotationType(value) {
  return expandTermQuotationTypeTokens(value)[0] || "";
}

function readTermQuotationTypeFlag(term = {}, ...keys) {
  for (const key of keys) {
    if (term[key] === undefined || term[key] === null || term[key] === "") continue;
    return parseApiBoolean(term[key], false);
  }
  return null;
}

function normalizeTermQuotationTypes(term = {}) {
  const supplyFlag = readTermQuotationTypeFlag(term, "isSupply", "supply", "Supply");
  const sitcFlag = readTermQuotationTypeFlag(term, "isSitc", "isSITC", "sitc", "SITC");
  const serviceFlag = readTermQuotationTypeFlag(term, "isService", "service", "SERVICE", "Service");
  const hasExplicitFlags = supplyFlag !== null || sitcFlag !== null || serviceFlag !== null;

  // Boolean multiselect flags from API/config: a term applies to any flagged type.
  if (hasExplicitFlags) {
    const types = [];
    if (supplyFlag) types.push("Supply");
    if (sitcFlag) types.push("SITC");
    if (serviceFlag) types.push("SERVICE");
    return types;
  }

  if (Array.isArray(term.quotationTypes)) {
    return Array.from(new Set(term.quotationTypes.flatMap(expandTermQuotationTypeTokens)));
  }

  const raw =
    term.applicableQuotationTypes ??
    term.quotationType ??
    term.applicableQuotationType ??
    term.types ??
    [];
  const list = Array.isArray(raw)
    ? raw
    : String(raw || "")
        .split(/[,|;]/)
        .map((item) => item.trim())
        .filter(Boolean);
  return Array.from(new Set(list.flatMap(expandTermQuotationTypeTokens)));
}

function buildTermQuotationTypeFlags(term = {}) {
  const types = normalizeTermQuotationTypes(term);
  return {
    isSupply: types.includes("Supply"),
    isSitc: types.includes("SITC"),
    isService: types.includes("SERVICE"),
  };
}

function buildTermApiPayload(term = {}) {
  const typeFlags = buildTermQuotationTypeFlags(term);
  const numericId = extractNumericId(term.id);
  const payload = {
    title: String(term.title || "").trim(),
    description: String(term.text || term.description || "").trim(),
    displayOrder: Number(term.order) || 1,
    isActive: term.status !== "Inactive",
    isSupply: typeFlags.isSupply,
    isSitc: typeFlags.isSitc,
    isService: typeFlags.isService,
  };

  // Only include id for existing terms (edit). New creates must not send id: 0.
  if (numericId) {
    payload.id = numericId;
  }

  return payload;
}

function toggleTermQuotationType(current = [], value) {
  const nextValue = canonicalizeTermQuotationType(value);
  if (!nextValue) return current;
  return current.includes(nextValue)
    ? current.filter((item) => item !== nextValue)
    : [...current, nextValue];
}

function termAppliesToQuotationType(term, quotationType) {
  const resolvedType = normalizeQuotationType(quotationType);
  if (!resolvedType) return true;
  const types = normalizeTermQuotationTypes(term);
  // No type metadata → treat as already scoped by GET /terms/get/{type}.
  if (!types.length) return true;
  // Multiselect: show the term for any one of its configured quotation types.
  return types.includes(resolvedType);
}

function filterTermsForQuotationType(terms = [], quotationType) {
  return terms.filter((term) => termAppliesToQuotationType(term, quotationType));
}

/** Load active standard terms for a quotation via GET /saas/crm/terms/get/{type}. */
const termsLibraryInflight = new Map();

  async function loadStandardTermsForQuotationType(quotationType) {
  const cacheKey = String(normalizeQuotationType(quotationType) || quotationType || "").trim().toUpperCase() || "DEFAULT";
  const existing = termsLibraryInflight.get(cacheKey);
  if (existing) return existing;

  const promise = (async () => {
    const response = await getTerms(quotationType);
    return extractApiTerms(response?.data)
      .sort((a, b) => Number(a.order) - Number(b.order))
      .map((term, index) => ({
        ...term,
        id: term.id ?? `term-${index + 1}`,
        description: term.text || term.description || "",
        // Already scoped by GET /terms/get/{type} — clear type metadata so a second
        // client-side filter cannot drop terms from Selected Terms Preview.
        quotationTypes: [],
        isSupply: undefined,
        isSitc: undefined,
        isSITC: undefined,
        isService: undefined,
        supply: undefined,
        sitc: undefined,
        SITC: undefined,
        service: undefined,
        SERVICE: undefined,
        quotationType: undefined,
        applicableQuotationTypes: undefined,
        applicableQuotationType: undefined,
        types: undefined,
      }))
      .filter((term) => !term.status || term.status === "Active");
  })().finally(() => {
    termsLibraryInflight.delete(cacheKey);
  });

  termsLibraryInflight.set(cacheKey, promise);
  return promise;
}

function termQuotationTypeLabel(value) {
  if (value === "SITC") return sq("termTypeSupplySitc");
  if (value === "SERVICE") return sq("termTypeService");
  return sq("termTypeSupply");
}

function extractApiTerms(payload) {
  return extractApiCollection(payload).map(mapApiTermToUi);
}

function inferPmcColumnFieldKey(label = "", index = 0) {
  const normalized = String(label || "").trim().toLowerCase();
  if (/^(description|item|role|particulars)/.test(normalized)) return "description";
  if (/^(amount|total)$/.test(normalized)) return "totalCost";
  if (/boq|bill of qty/.test(normalized)) return "boqQty";
  if (/cons\.?\s*qty|consumed\s*qty|consumables?\s*qty/.test(normalized)) return "consQty";
  if (/^nos$/.test(normalized)) return "nos";
  if (/month/.test(normalized)) return "months";
  if (/^ctc$/.test(normalized)) return "rate";
  if (/unit\s*rate|unitrate/.test(normalized)) return "unitRate";
  if (/^rate$/.test(normalized)) return "rate";
  if (/^qty$|^quantity$/.test(normalized)) return "qty";
  return `col${index}`;
}

function buildPmcColumnDefinitions(columns = []) {
  const normalizedInput = normalizePmcColumnsInput(columns);
  return normalizedInput.map((column, index) => {
    const label = resolvePmcColumnLabel(column, index);
    const key = resolvePmcColumnFieldKey(column, index);
    const numeric = key !== "description";
    const explicitComputed = column && typeof column === "object" && typeof column.computed === "boolean"
      ? column.computed
      : null;
    const explicitReadonly = column && typeof column === "object" && typeof column.readonly === "boolean"
      ? column.readonly
      : null;
    const computed = explicitComputed ?? (key === "totalCost");
    const readonly = explicitReadonly ?? (key === "description" || key === "totalCost");
    return {
      id: `pmc-col-${index}`,
      reactKey: `pmc-col-${index}`,
      key,
      label,
      computed,
      readonly,
      align: key === "description" ? "left" : "right",
      numeric,
    };
  });
}

function buildPmcDisplayColumns(columns = [], headType = "") {
  const typed = resolvePmcColumnsForHeadType({ costHeadType: headType });
  const defs = buildPmcColumnDefinitions(typed?.length ? typed : columns);
  const hasDescriptionColumn = defs.some((column) => column.key === "description");
  if (hasDescriptionColumn) {
    // Ensure Description stays first.
    const description = defs.find((column) => column.key === "description");
    const rest = defs.filter((column) => column.key !== "description");
    return description ? [description, ...rest] : defs;
  }

  return [
    {
      id: "pmc-col-description",
      reactKey: "pmc-col-description",
      key: "description",
      label: "Description",
      computed: false,
      readonly: true,
      align: "left",
      numeric: false,
    },
    ...defs,
  ];
}

function getPmcColumnKeys(columns = []) {
  return buildPmcColumnDefinitions(columns).map((column) => column.key);
}

function computePmcRowTotal(entry = {}, columns = []) {
  const headType = canonicalizePmcHeadType(entry.costHeadType);
  const nos = parseCurrency(entry.nos);
  const months = parseCurrency(entry.months);
  const ctc = parseCurrency(entry.rate ?? entry.ctc);
  const unitRate = parseCurrency(entry.unitRate ?? entry.rate);
  const consQty = parseCurrency(entry.consQty ?? entry.qty);
  const manualAmount = parseCurrency(entry.totalCost ?? entry.amount ?? entry.total ?? 0);

  if (headType === "MANPOWER_MONTHLY") {
    return nos * months * ctc;
  }
  if (headType === "BOQ_QTY_BASED") {
    return unitRate * consQty;
  }
  if (headType === "LUMPSUM") {
    return manualAmount;
  }

  const columnKeys = getPmcColumnKeys(columns);
  const rate = parseCurrency(entry.rate ?? entry.unitRate ?? entry.ctc);
  const boqQty = parseCurrency(entry.boqQty);
  const qty = parseCurrency(entry.qty);

  if (columnKeys.includes("nos") && columnKeys.includes("months") && (columnKeys.includes("rate") || columnKeys.includes("unitRate"))) {
    return nos * months * rate;
  }
  if (columnKeys.includes("unitRate") && columnKeys.includes("consQty")) {
    return unitRate * consQty;
  }
  if (columnKeys.includes("boqQty") && (columnKeys.includes("rate") || columnKeys.includes("unitRate"))) {
    const multiplier = consQty || qty || 1;
    return boqQty * rate * multiplier;
  }
  const resolvedQty = qty || nos || boqQty || consQty;
  if (resolvedQty && rate) {
    return resolvedQty * rate;
  }

  return manualAmount;
}

function normalizePmcEntry(entry = {}, index = 0) {
  const costHeadType = canonicalizePmcHeadType(entry.costHeadType) || String(entry.costHeadType || "").trim();
  const typedColumns = resolvePmcColumnsForHeadType({ costHeadType });
  const columnSource = typedColumns?.length
    ? typedColumns
    : (entry.rawColumns?.length ? entry.rawColumns : entry.columns);
  const columns = Array.isArray(columnSource) && columnSource.length
    ? normalizePmcColumnLabels(columnSource)
    : [];
  const isLumpSum = canonicalizePmcHeadType(costHeadType) === "LUMPSUM";
  // Description column shows the PMC Line Item Name (lineName), not the cost-head description.
  const lineItemName = resolvePmcLineLabel(entry);
  const defaults = pickPmcLineMetricFields(entry, costHeadType);
  const normalized = {
    id: entry.id ?? `pmc-entry-${index + 1}`,
    pmcConfigId: entry.pmcConfigId ?? entry.pmc_config_id ?? null,
    category: entry.category || inferPmcCategory(entry.headName || entry.description || ""),
    costHeadType,
    headName: entry.headName || entry.configName || `PMC Head ${index + 1}`,
    description: lineItemName,
    lineName: lineItemName,
    columns,
    rawColumns: Array.isArray(columnSource) ? columnSource : [],
    configDescription: entry.configDescription || entry.headDescription || "",
    status: mapPmcConfigStatus(entry),
    isActive: isPmcCostHeadActive(entry),
    boqQty: hasPmcMetricValue(entry.boqQty) ? String(entry.boqQty) : String(defaults.boqQty || ""),
    consQty: hasPmcMetricValue(entry.consQty ?? entry.consumedQty)
      ? String(entry.consQty ?? entry.consumedQty)
      : String(defaults.consQty || ""),
    qty: String(entry.qty ?? entry.quantity ?? ""),
    nos: hasPmcMetricValue(entry.nos) ? String(entry.nos) : String(defaults.nos || ""),
    months: hasPmcMetricValue(entry.months) ? String(entry.months) : String(defaults.months || ""),
    // CTC displays in the C+P `rate` column for MANPOWER_MONTHLY.
    rate: hasPmcMetricValue(entry.rate ?? entry.ctc)
      ? String(entry.rate ?? entry.ctc)
      : String(defaults.ctc || ""),
    unitRate: (() => {
      const headType = canonicalizePmcHeadType(costHeadType);
      if (headType === "BOQ_QTY_BASED") {
        // Preserve in-progress edits (including empty while replacing the value).
        if (entry.unitRateTouched) {
          return String(entry.unitRate ?? "");
        }
        if (hasPmcMetricValue(entry.unitRate) && parseCurrency(entry.unitRate) !== 0) {
          return String(entry.unitRate);
        }
        if (hasPmcMetricValue(entry.rate) && parseCurrency(entry.rate) !== 0) {
          return String(entry.rate);
        }
        if (hasPmcMetricValue(defaults.unitRate)) return String(defaults.unitRate);
        if (hasPmcMetricValue(entry.unitRate)) return String(entry.unitRate);
        return "";
      }
      return hasPmcMetricValue(entry.unitRate)
        ? String(entry.unitRate)
        : String(defaults.unitRate || "");
    })(),
    totalCost: isLumpSum
      ? (entry.totalCost ?? entry.amount ?? entry.total ?? "")
      : (Number(entry.totalCost ?? entry.amount ?? entry.total ?? 0) || 0),
    unitRateTouched: Boolean(entry.unitRateTouched),
  };
  if (!isLumpSum) {
    normalized.totalCost = computePmcRowTotal(normalized, normalized.rawColumns.length ? normalized.rawColumns : columns);
  }
  return normalized;
}

/** Cost Head / line *Default metrics for C+P seeding (Cost Head wins; line fills gaps). */
function resolvePmcMetricDefaults(config = {}, line = {}) {
  const costHeadType =
    canonicalizePmcHeadType(config.costHeadType) ||
    String(config.costHeadType || "").trim();
  const headDefaults = pickPmcHeadMetricFields(config, costHeadType);
  const lineDefaults = pickPmcLineMetricFields(line, costHeadType);
  return {
    boqQty: String(headDefaults.boqQty || lineDefaults.boqQty || ""),
    unitRate: String(headDefaults.unitRate || lineDefaults.unitRate || ""),
    consQty: String(headDefaults.consQty || lineDefaults.consQty || ""),
    nos: String(headDefaults.nos || lineDefaults.nos || ""),
    months: String(headDefaults.months || lineDefaults.months || ""),
    ctc: String(headDefaults.ctc || lineDefaults.ctc || ""),
  };
}

function createPmcEntryFromConfig(config = {}, rowLabel = "", rowIndex = 0, savedEntry = null, line = null) {
  const costHeadType = canonicalizePmcHeadType(config.costHeadType) || String(config.costHeadType || "").trim();
  const headType = canonicalizePmcHeadType(costHeadType);
  const rawColumns = resolvePmcDynamicColumns({
    ...config,
    costHeadType,
  });
  const columns = normalizePmcColumnLabels(rawColumns);
  const lineItemName = String(
    resolvePmcLineLabel(savedEntry || {}) || rowLabel || ""
  ).trim();
  const defaults = resolvePmcMetricDefaults(config, line || {});
  const entry = {
    id: `pmc-${config.id}-${rowIndex}`,
    pmcConfigId: config.id,
    category: config.category,
    costHeadType,
    headName: config.name,
    description: lineItemName,
    lineName: lineItemName,
    columns,
    rawColumns,
    configDescription: config.description || "",
    status: mapPmcConfigStatus(config),
    isActive: isPmcCostHeadActive(config),
    // Seed from PMC Cost Head defaults (backend *Default fields).
    boqQty: defaults.boqQty,
    consQty: defaults.consQty,
    qty: "",
    nos: defaults.nos,
    months: defaults.months,
    // MANPOWER CTC → `rate` column; BOQ keeps CTC out of unit rate.
    rate: headType === "MANPOWER_MONTHLY" ? defaults.ctc : "",
    unitRate: defaults.unitRate,
    totalCost: 0,
  };

  if (savedEntry) {
    if (savedEntry.id != null && !String(savedEntry.id).startsWith("pmc-")) {
      entry.id = savedEntry.id;
    }
    // Preserve quotation-saved / user-edited C+P values; only fill blanks from defaults.
    if (headType === "BOQ_QTY_BASED") {
      if (hasPmcMetricValue(savedEntry.boqQty)) entry.boqQty = String(savedEntry.boqQty);
      // API persists Unit Rate on `rate`; prefer non-zero unitRate, then rate.
      // Do not let a persisted 0 wipe Cost Head defaults.
      if (hasPmcMetricValue(savedEntry.unitRate) && parseCurrency(savedEntry.unitRate) !== 0) {
        entry.unitRate = String(savedEntry.unitRate);
      } else if (hasPmcMetricValue(savedEntry.rate) && parseCurrency(savedEntry.rate) !== 0) {
        entry.unitRate = String(savedEntry.rate);
      }
      if (hasPmcMetricValue(savedEntry.consQty ?? savedEntry.consumedQty)) {
        entry.consQty = String(savedEntry.consQty ?? savedEntry.consumedQty);
      }
    } else if (headType === "MANPOWER_MONTHLY") {
      if (hasPmcMetricValue(savedEntry.nos)) entry.nos = String(savedEntry.nos);
      if (hasPmcMetricValue(savedEntry.months)) entry.months = String(savedEntry.months);
      if (hasPmcMetricValue(savedEntry.rate ?? savedEntry.ctc)) {
        entry.rate = String(savedEntry.rate ?? savedEntry.ctc);
      }
    } else if (headType === "LUMPSUM") {
      if (hasPmcMetricValue(savedEntry.totalCost ?? savedEntry.amount ?? savedEntry.total)) {
        entry.totalCost = Number(savedEntry.totalCost ?? savedEntry.amount ?? savedEntry.total ?? 0) || 0;
      }
    } else {
      // Unknown type — keep prior generic merge behavior for any present fields.
      if (hasPmcMetricValue(savedEntry.boqQty)) entry.boqQty = String(savedEntry.boqQty);
      if (hasPmcMetricValue(savedEntry.consQty ?? savedEntry.consumedQty)) {
        entry.consQty = String(savedEntry.consQty ?? savedEntry.consumedQty);
      }
      if (hasPmcMetricValue(savedEntry.qty ?? savedEntry.quantity)) {
        entry.qty = String(savedEntry.qty ?? savedEntry.quantity);
      }
      if (hasPmcMetricValue(savedEntry.nos)) entry.nos = String(savedEntry.nos);
      if (hasPmcMetricValue(savedEntry.months)) entry.months = String(savedEntry.months);
      if (hasPmcMetricValue(savedEntry.rate ?? savedEntry.ctc ?? savedEntry.unitRate)) {
        entry.rate = String(savedEntry.rate ?? savedEntry.ctc ?? savedEntry.unitRate);
      }
      if (hasPmcMetricValue(savedEntry.unitRate ?? savedEntry.rate)) {
        entry.unitRate = String(savedEntry.unitRate ?? savedEntry.rate);
      }
      if (canonicalizePmcHeadType(costHeadType) === "LUMPSUM") {
        entry.totalCost = Number(savedEntry.totalCost ?? savedEntry.amount ?? savedEntry.total ?? 0) || 0;
      }
    }
  }

  entry.totalCost = computePmcRowTotal(entry, rawColumns.length ? rawColumns : columns);
  return entry;
}

function findSavedPmcEntry(savedEntries = [], config = {}, rowLabel = "", rowIndex = 0) {
  const configId = String(config.id ?? "");
  const scoped = savedEntries.filter((entry) => String(entry.pmcConfigId ?? entry.pmc_config_id ?? "") === configId);
  const label = String(rowLabel || "").trim().toLowerCase();

  if (label) {
    const byLabel = scoped.find((entry) => {
      const candidate = String(
        entry.lineName || entry.description || entry.item || entry.roleName || ""
      ).trim().toLowerCase();
      return candidate && candidate === label;
    });
    if (byLabel) return byLabel;
  }

  return scoped[rowIndex] || null;
}

function mergePmcEntriesWithConfigs(configs = [], savedEntries = []) {
  const activeConfigs = (Array.isArray(configs) ? configs : []).filter((config) =>
    isPmcCostHeadActive(config)
  );
  if (!activeConfigs.length) return [];

  const merged = [];
  activeConfigs
    .sort((a, b) => Number(a.order) - Number(b.order))
    .forEach((config) => {
      const activeLines = normalizePmcLineEntries(config.defaultRows, config.costHeadType).filter(
        (row) => row.isActive !== false && String(row.label || "").trim()
      );
      if (!activeLines.length) return;

      activeLines.forEach((line, rowIndex) => {
        const label = String(line.label || "").trim();
        const savedEntry = findSavedPmcEntry(savedEntries, config, label, rowIndex);
        merged.push(createPmcEntryFromConfig(config, label, rowIndex, savedEntry, line));
      });
    });

  return merged;
}

function patchPmcEntryValue(entries = [], entryId, fieldKey, value) {
  const targetId = String(entryId);
  return entries.map((entry) => {
    if (String(entry.id) !== targetId) return entry;
    const next = { ...entry, [fieldKey]: value };
    // Keep CTC (`rate`) and BOQ `unitRate` independent — do not mirror across fields.
    if (fieldKey === "rate") {
      next.rate = value;
      next.ctc = value;
    } else if (fieldKey === "unitRate") {
      next.unitRate = value;
      next.unitRateTouched = true;
      if (canonicalizePmcHeadType(entry.costHeadType) === "BOQ_QTY_BASED") {
        // Keep API `rate` in sync so normalize/save do not revert to the old saved rate.
        next.rate = value;
      }
    }
    if (
      (fieldKey === "totalCost" || fieldKey === "amount") &&
      canonicalizePmcHeadType(entry.costHeadType) === "LUMPSUM"
    ) {
      next.totalCost = value;
      return next;
    }
    next.totalCost = computePmcRowTotal(next, entry.rawColumns?.length ? entry.rawColumns : entry.columns || []);
    return next;
  });
}

let pmcConfigsInflight = null;

async function resolvePmcEntriesForQuotation(_quotationType, savedEntries = []) {
  if (!pmcConfigsInflight) {
    pmcConfigsInflight = getPmcConfigs().finally(() => {
      pmcConfigsInflight = null;
    });
  }
  const response = await pmcConfigsInflight;
  const rawPayload = response?.data ?? response;
  const rawHeads = extractApiCollection(rawPayload);
  const configs = extractApiPmcConfigs(rawPayload).map((config) => {
    const matchedRaw = rawHeads.find((item) =>
      String(item?.id ?? item?.pmcConfigId ?? "") === String(config.id)
    ) || {};
    const activeLines = pickPmcRowsFromApi(
      {
        ...matchedRaw,
        lines: matchedRaw.lines ?? matchedRaw.pmcLines ?? matchedRaw.lineItems,
        defaultRows: config.defaultRows,
      },
      { activeOnly: true }
    );
    const costHeadType = canonicalizePmcHeadType(
      matchedRaw.costHeadType ?? config.costHeadType
    ) || String(matchedRaw.costHeadType ?? config.costHeadType ?? "").trim();
    // C+P columns are driven by PMC Head Type (MANPOWER_MONTHLY / BOQ_QTY_BASED / LUMPSUM).
    const rawColumns = resolvePmcDynamicColumns({
      ...matchedRaw,
      ...config,
      costHeadType,
    });
    return {
      ...config,
      // Keep Cost Head *Default metrics on the config for C+P seeding.
      ...pickPmcHeadMetricFields({ ...config, ...matchedRaw }, costHeadType),
      costHeadType,
      rawColumns,
      columns: normalizePmcColumnLabels(rawColumns),
      defaultRows: activeLines.length ? activeLines : [],
    };
  });
  const activeConfigs = configs.filter((config) => isPmcCostHeadActive(config));
  if (!activeConfigs.length) {
    return { entries: [], hasPmcConfigs: false };
  }

  return {
    entries: mergePmcEntriesWithConfigs(activeConfigs, savedEntries),
    hasPmcConfigs: true,
  };
}

function groupPmcEntriesForDisplay(entries = []) {
  const grouped = new Map();

  entries.forEach((entry, index) => {
    if (!isPmcCostHeadActive(entry)) return;
    const normalized = normalizePmcEntry(entry, index);
    const columnSource = normalized.rawColumns?.length ? normalized.rawColumns : normalized.columns;
    const columns = buildPmcDisplayColumns(columnSource, normalized.costHeadType);

    const groupKey = String(normalized.pmcConfigId ?? normalized.id ?? `group-${index}`);
    const row = {
      ...normalized,
      description: String(normalized.lineName || normalized.description || "").trim(),
      totalCost: canonicalizePmcHeadType(normalized.costHeadType) === "LUMPSUM"
        ? normalized.totalCost
        : computePmcRowTotal(normalized, columnSource?.length ? columnSource : normalized.columns),
    };
    const rowTotal = Number(computePmcRowTotal(
      { ...normalized, totalCost: row.totalCost },
      columnSource?.length ? columnSource : normalized.columns
    )) || 0;
    const existing = grouped.get(groupKey);

    if (existing) {
      existing.rows.push(row);
      existing.totalCost += rowTotal;
      return;
    }

    grouped.set(groupKey, {
      id: normalized.pmcConfigId ?? normalized.id ?? `pmc-group-${index + 1}`,
      title: normalized.headName || normalized.category,
      category: normalized.category,
      costHeadType: normalized.costHeadType,
      description: normalized.configDescription || buildPmcHeadPresentation(normalized.headName, normalized.category).description,
      columns,
      rows: [row],
      totalCost: rowTotal,
    });
  });

  return Array.from(grouped.values());
}

function resolvePersistedListPrice(item = {}) {
  const raw =
    item.listPrice ??
    item.tPrice ??
    item.list_price ??
    item.t_price ??
    item.catalogPrice ??
    item.cataloguePrice ??
    item.costPrice ??
    item.cost_price;
  const amount = parseCurrency(raw);
  return amount > 0 ? formatInputNumber(amount) : "";
}

function mapApiLineItemToWorkingRow(item = {}, index = 0, quotationType = "Supply", options = {}) {
  const backendDerivedMultiplier = String(options.backendDerivedMultiplier || "").trim();
  const adjustmentType =
    String(
      item.multiplierDiscountType ||
      item.multiplier_discount_type ||
      item.adjustmentType ||
      item.adjustment_type ||
      item.pricingMode ||
      ""
    ).trim() === "Discount %"
      ? "Discount %"
      : "Multiplier";
  const dedicatedMultiplierRate = firstDefined(
    item.multiplierDiscountRate,
    item.multiplier_discount_rate,
    item.adjustmentRate,
    item.adjustment_rate,
    item.multDiscRate
  );
  const hasDedicatedMultiplierRate =
    dedicatedMultiplierRate !== undefined &&
    dedicatedMultiplierRate !== null &&
    String(dedicatedMultiplierRate).trim() !== "";
  const hasMultiplierField =
    item.multiplier !== undefined &&
    item.multiplier !== null &&
    String(item.multiplier).trim() !== "";
  const explicitCpMultiplier = firstDefined(
    item.cpMultiplier,
    item.cp_multiplier,
    item.costPricingMultiplier,
    item.cost_pricing_multiplier
  );
  const hasExplicitCpMultiplier =
    explicitCpMultiplier !== undefined &&
    explicitCpMultiplier !== null &&
    String(explicitCpMultiplier).trim() !== "";
  // `multiplier` is a C+P reference only when it differs from the saved Mult/Disc rate.
  // When they match, it is the applied rate (or an in-sync seed), not a separate reference.
  const savedCpReference = hasExplicitCpMultiplier
    ? explicitCpMultiplier
    : (
      hasDedicatedMultiplierRate &&
      hasMultiplierField &&
      !multipliersMatchForPersistence(dedicatedMultiplierRate, item.multiplier)
        ? item.multiplier
        : undefined
    );
  const hasSavedCpReference =
    savedCpReference !== undefined &&
    savedCpReference !== null &&
    String(savedCpReference).trim() !== "";
  const persistedMultiplierRate = hasDedicatedMultiplierRate
    ? dedicatedMultiplierRate
    : (adjustmentType === "Multiplier" && hasMultiplierField ? item.multiplier : undefined);
  const hasPersistedMultDiscRate =
    persistedMultiplierRate !== undefined &&
    persistedMultiplierRate !== null &&
    String(persistedMultiplierRate).trim() !== "";
  const cpReferenceForManualCheck = hasSavedCpReference
    ? savedCpReference
    : backendDerivedMultiplier;
  // Backend defaulted Mult/Disc Rate of 1 is not a real manual value when C+P derived exists.
  const isPlaceholderMultiplierOneFlag =
    hasPersistedMultDiscRate &&
    isPlaceholderMultiplierOne(persistedMultiplierRate, cpReferenceForManualCheck || backendDerivedMultiplier);
  // A saved rate that differs from this line's C+P reference (or live derived) is a
  // user edit and must survive refresh. Bare 1 is the backend default, not an edit.
  // Compare against backendDerived too — some GETs mirror the applied rate into cpMultiplier.
  const rateDiffersFromCpReference = Boolean(
    cpReferenceForManualCheck &&
    !multipliersMatchForPersistence(persistedMultiplierRate, cpReferenceForManualCheck)
  );
  const rateDiffersFromBackendDerived = Boolean(
    backendDerivedMultiplier &&
    !multipliersMatchForPersistence(persistedMultiplierRate, backendDerivedMultiplier)
  );
  const adjustmentRateManual = Boolean(
    adjustmentType === "Multiplier" &&
    hasPersistedMultDiscRate &&
    !isUnsetNumeric(persistedMultiplierRate) &&
    !isPlaceholderMultiplierOneFlag &&
    !multipliersMatchForPersistence(persistedMultiplierRate, 1) &&
    (
      (!cpReferenceForManualCheck && !backendDerivedMultiplier) ||
      rateDiffersFromCpReference ||
      rateDiffersFromBackendDerived
    )
  );
  const shouldUseBackendMultiplierDefault =
    adjustmentType === "Multiplier" &&
    !adjustmentRateManual &&
    Boolean(backendDerivedMultiplier) &&
    (!hasPersistedMultDiscRate || isPlaceholderMultiplierOneFlag);
  const adjustmentRate = shouldUseBackendMultiplierDefault
    ? backendDerivedMultiplier
    : (hasPersistedMultDiscRate ? String(persistedMultiplierRate) : "");
  const persistedCpMultiplier = String(
    (hasSavedCpReference ? savedCpReference : undefined) ??
    backendDerivedMultiplier ??
    ""
  ).trim();
  const multiplier = adjustmentType === "Multiplier"
    ? (adjustmentRate || persistedCpMultiplier)
    : (persistedCpMultiplier || String(item.multiplier ?? "").trim() || "");
  const cpMultiplier = adjustmentRateManual
    ? (persistedCpMultiplier || backendDerivedMultiplier || "")
    : (
      (hasSavedCpReference ? String(savedCpReference).trim() : "") ||
      backendDerivedMultiplier ||
      adjustmentRate ||
      persistedCpMultiplier ||
      ""
    );
  const persistedInstallMultiplier = firstDefined(
    item.installationMultiplier,
    item.installation_multiplier,
    item.installMultiplier
  );
  const hasPersistedInstallMultiplier =
    persistedInstallMultiplier !== undefined &&
    persistedInstallMultiplier !== null &&
    String(persistedInstallMultiplier).trim() !== "";
  // Backend often persists Install Mult as 0/1 before C+P — not a manual user value.
  const installLooksUnsetOrDefault =
    !hasPersistedInstallMultiplier ||
    isUnsetNumeric(persistedInstallMultiplier) ||
    isPlaceholderMultiplierOne(
      persistedInstallMultiplier,
      cpReferenceForManualCheck || backendDerivedMultiplier
    );
  const installDiffersFromCpReference = Boolean(
    cpReferenceForManualCheck &&
    !multipliersMatchForPersistence(persistedInstallMultiplier, cpReferenceForManualCheck)
  );
  const installDiffersFromBackendDerived = Boolean(
    backendDerivedMultiplier &&
    !multipliersMatchForPersistence(persistedInstallMultiplier, backendDerivedMultiplier)
  );
  const installationMultiplierManual = Boolean(
    hasPersistedInstallMultiplier &&
    !installLooksUnsetOrDefault &&
    !multipliersMatchForPersistence(persistedInstallMultiplier, 1) &&
    (
      (!cpReferenceForManualCheck && !backendDerivedMultiplier) ||
      installDiffersFromCpReference ||
      installDiffersFromBackendDerived
    )
  );
  const shouldUseBackendInstallDefault =
    supportsInstallation(quotationType) &&
    shouldUseCpMultiplierDefault(
      hasPersistedInstallMultiplier ? persistedInstallMultiplier : "",
      backendDerivedMultiplier,
      installationMultiplierManual
    );
  const installationMultiplier = shouldUseBackendInstallDefault
    ? backendDerivedMultiplier
    : (hasPersistedInstallMultiplier && !isUnsetNumeric(persistedInstallMultiplier)
      ? String(persistedInstallMultiplier)
      : "");
  // Persisted Mult/Disc + Install Mult must survive Cost + Pricing re-seeding after refresh.
  const storedUnitRate = mapStoredAmount(
    item.sellingSupplyRate ??
    item.selling_supply_rate ??
    item.unitRate ??
    item.unit_rate ??
    item.netRate
  );

  const listPrice = resolvePersistedListPrice(item);
  const netPrice = mapStoredAmount(
    item.netPrice ??
    item.net_price ??
    item.netPriceAmount ??
    item.netPriceRulePct ??
    item.net_price_rule_pct
  );
  const discount = String(
    firstDefined(item.discountPct, item.discount_pct, item.discount, item.additionalDiscount) ?? ""
  );
  const derivedInputSupplyRate = deriveInputSupplyRate(netPrice, discount);
  const storedInputSupplyRate = mapStoredAmount(
    item.inputSupplyRate ??
    item.input_supply_rate ??
    item.initialUnitRate ??
    item.initRate
  );
  const hasStoredInputSupplyRate = !isUnsetNumeric(storedInputSupplyRate);
  const storedMatchesDerived =
    derivedInputSupplyRate > 0 &&
    Math.abs(parseCurrency(storedInputSupplyRate) - derivedInputSupplyRate) <= 0.01;
  const keepStoredAsManual =
    hasStoredInputSupplyRate &&
    (!storedMatchesDerived || Boolean(item.inputSupplyRate ?? item.input_supply_rate));
  const inputRateForFormula = hasStoredInputSupplyRate
    ? parseCurrency(storedInputSupplyRate)
    : derivedInputSupplyRate;
  const formulaSellingSupplyRate = adjustmentType === "Multiplier"
    ? roundExcelWhole(inputRateForFormula * parseCurrency(adjustmentRate || multiplier || 0))
    : 0;
  // Formula-derived selling rates must not block C+P Derived Multiplier reseeding.
  // A selling rate that differs from Input × Mult is a user edit and must survive refresh.
  const unitRateLooksFormulaDriven =
    Boolean(storedUnitRate) &&
    adjustmentType === "Multiplier" &&
    !adjustmentRateManual &&
    formulaSellingSupplyRate > 0 &&
    Math.abs(parseCurrency(storedUnitRate) - formulaSellingSupplyRate) <= 1;
  const unitRateManual = Boolean(storedUnitRate) &&
    !shouldUseBackendMultiplierDefault &&
    !unitRateLooksFormulaDriven;

  const lineItemId = extractNumericId(
    item.id ?? item.lineItemId ?? item.quotationLineId ?? item.quotation_line_id
  );

  return {
    id: lineItemId != null ? `api-row-${lineItemId}` : `api-row-${index + 1}`,
    lineItemId,
    slNo: item.slNo ?? item.sl_no ?? item.serialNo ?? item.sNo ?? "",
    description: (() => {
      const direct = String(item.description || item.productName || "").trim();
      if (direct) return direct;
      // Never copy placeholder System names into the line description (shows in PDF).
      const systemFallback = String(item.systemName || item.system_name || "").trim();
      return isClientPlaceholderSystemName(systemFallback) ? "" : systemFallback;
    })(),
    brandId: String(item.brandId ?? item.brand_id ?? ""),
    make: item.brandName || item.brand_name || item.brandId || item.brand_id || "",
    productId: String(item.productId ?? item.product_id ?? item.productCode ?? ""),
    partNo: item.partNumber || item.part_number || item.productCode || item.partNo || item.productId || "",
    uom: item.uom || "Nos",
    qty: String(firstDefined(item.quantity, item.qty, 1)),
    listPrice,
    netPrice,
    discount,
    // Always restore backend Input Supply Rate when present so edits survive refresh.
    initialUnitRate: hasStoredInputSupplyRate
      ? toExcelWholeRate(storedInputSupplyRate)
      : derivedInputSupplyRate > 0
        ? toExcelWholeRate(derivedInputSupplyRate)
        : "",
    initialUnitRateManual: keepStoredAsManual || hasStoredInputSupplyRate,
    adjustmentType,
    adjustmentRate,
    adjustmentRateManual,
    multiplier,
    cpMultiplier,
    unitRate: unitRateManual && storedUnitRate
      ? toExcelWholeRate(storedUnitRate)
      : (shouldUseBackendMultiplierDefault ? "" : (storedUnitRate ? toExcelWholeRate(storedUnitRate) : "")),
    unitRateManual,
    installationPerUnit: String(
      firstDefined(
        item.inputInstallationRate,
        item.input_installation_rate,
        item.installationPerUnit,
        supportsInstallation(quotationType) ? "" : "0"
      ) ?? (supportsInstallation(quotationType) ? "" : "0")
    ),
    installationMultiplier,
    installationMultiplierManual,
    backendInputSupplyAmount: pickFiniteNumber(
      item.inputSupplyAmount,
      item.input_supply_amount
    ) ?? mapStoredAmount(item.inputSupplyAmount ?? item.input_supply_amount),
    backendSellingSupplyAmount: shouldUseBackendMultiplierDefault
      ? ""
      : (pickFiniteNumber(
        item.sellingSupplyAmount,
        item.selling_supply_amount
      ) ?? mapStoredAmount(item.sellingSupplyAmount ?? item.selling_supply_amount)),
    backendInputInstallationAmount: pickFiniteNumber(
      item.inputInstallationAmount,
      item.input_installation_amount
    ) ?? mapStoredAmount(item.inputInstallationAmount ?? item.input_installation_amount),
    backendSellingInstallationAmount: shouldUseBackendInstallDefault
      ? ""
      : (pickFiniteNumber(
        item.sellingInstallationAmount,
        item.selling_installation_amount
      ) ?? mapStoredAmount(item.sellingInstallationAmount ?? item.selling_installation_amount)),
    backendSellingInstallationRate: shouldUseBackendInstallDefault
      ? ""
      : (pickFiniteNumber(
        item.sellingInstallationRate,
        item.selling_installation_rate
      ) ?? mapStoredAmount(item.sellingInstallationRate ?? item.selling_installation_rate)),
    preferBackendAmounts: !(shouldUseBackendMultiplierDefault || shouldUseBackendInstallDefault),
    remarks: item.remarks || "",
    systemId: item.systemId ?? item.productServiceId ?? item.productServiceID ?? item.system_id ?? "",
    systemName: resolveBackendSystemName(item),
  };
}

function normalizeApiQuotation(item = {}) {
  item = unwrapQuotationDetailPayload(item);

  const type = normalizeQuotationType(item.type ?? item.quotationType ?? item.salesType);
  const fallbackDate = currentIsoDate();
  const pricingMetrics = mapBackendPricingMetrics(item);
  const backendDerivedMultiplier = resolveBackendDerivedMultiplier(pricingMetrics, type);
  const lineItemSource =
    item.lineItems ??
    item.lines ??
    item.items ??
    item.quotationItems ??
    item.workingSheet?.lineItems ??
    [];
  const workingRows = extractApiCollection(lineItemSource).map((lineItem, index) =>
    mapApiLineItemToWorkingRow(lineItem, index, type, { backendDerivedMultiplier })
  );
  const pmcs = extractApiCollection(item.pmcs).map(normalizePmcEntry);
  const specificTerms = normalizeSpecificTermsCollection(item.specificTerms);

  const status = normalizeStatusLabel(item.status ?? item.quotationStatus ?? item.statusLabel) || "Draft";
  const notes = resolveQuotationNotes(item, status);
  const refId = String(
    item.refId ??
    item.referenceId ??
    item.refNo ??
    item.quotationRefId ??
    item.quotationNo ??
    item.quotationNumber ??
    item.quoteNumber ??
    extractNumericId(item.id ?? item.quotationId) ??
    ""
  ).trim().toUpperCase();

  const apiSystems = sanitizeQuotationSystems(extractSystemsFromQuotationApi(item));
  // Do not invent Systems from working rows — only backend system fields/summaries.
  const productServiceIds = apiSystems.length
    ? apiSystems
        .map((system) => system.id)
        .filter((id) => id && !String(id).startsWith("name-") && !isClientPlaceholderSystemId(id))
    : Array.isArray(item.productServiceIds)
      ? item.productServiceIds
      : Array.isArray(item.systemIds)
        ? item.systemIds
        : [];
  const systemNames = apiSystems.length
    ? apiSystems.map((system) => system.name).filter(Boolean)
    : normalizeSystemNameList(
        Array.isArray(item.systemNames) && item.systemNames.length
          ? item.systemNames
          : item.systemName
      );

  return hydrateQuotation({
    id: extractNumericId(item.id ?? item.quotationId),
    refId,
    version: resolveBackendVersionNo(item),
    versionLabel: formatBackendVersionLabel(item),
    customer: item.customerName || item.customer || item.customerDisplayName || item.companyName || "",
    customerId: extractNumericId(item.customerId),
    location: item.locationName || item.location || item.address || "",
    locationId: extractNumericId(item.locationId),
    pointOfContact: item.contactName || item.pointOfContactName || item.pointOfContact || "",
    phone: item.contactPhone || item.phone || "",
    email: item.contactEmail || item.email || "",
    salesExecutive: item.salesExecutive || item.salesExecutiveName || item.assignedSalesExecutiveName || "",
    salesSupport: item.salesSupport || item.salesSupportName || item.assignedSalesSupportName || "",
    assignedSalesExecutive: item.assignedSalesExecutive ?? item.assigned_sales_executive ?? "",
    assignedSalesSupport: item.assignedSalesSupport ?? item.assigned_sales_support ?? "",
    ...pickQuotationServiceRoleFields(item),
    productServiceIds,
    systemNames,
    systemName: systemNames.join(", "),
    systems: apiSystems,
    systemSummaries: extractApiCollection(item.systemSummaries ?? item.workingSheet?.systemSummaries),
    type,
    complaintId: item.complaintId ?? item.complaint_id ?? "",
    status,
    sellingPrice: formatCurrency(
      item.finalSellingPrice ??
      item.sellingPrice ??
      item.customerSellingTotal ??
      item.totalSellingPrice ??
      0
    ),
    tenderDue: item.tenderDueDate || item.tenderDue || item.expiryDate || fallbackDate,
    expiry: item.expiryDate || item.expiry || fallbackDate,
    updated: String(item.updatedAt || item.lastUpdatedAt || item.updatedOn || item.createdAt || fallbackDate).slice(0, 10),
    // Systems mean line items load lazily — never invent a blank WS row on GET.
    workingRows: workingRows.length
      ? workingRows
      : (apiSystems.length ? [] : [buildBlankWorkingRow(type)]),
    pmcs,
    overheadsPct: String(item.overheadsPct ?? item.overheadsPercentage ?? 0),
    markUpPct: String(item.markUpPct ?? item.markupPct ?? item.markUpPercentage ?? 0),
    totalPmcCost: Number(item.totalPmcCost ?? 0) || pmcs.reduce((sum, pmc) => sum + Number(pmc.totalCost || 0), 0),
    pricingMetrics,
    specificTerms,
    additionalTerms: "",
    submissionNote: notes.submissionNote,
    revisionNote: notes.revisionNote,
    importStatusMessage: String(
      item.importStatusMessage ?? item.import_status_message ?? ""
    ).trim(),
    approverRoleName: item.approverRoleName || "",
    requiredApprovalLevel: item.requiredApprovalLevel || "",
  });
}

function buildWorkingSheetPayload(
  quotation,
  rows,
  additionalTermEntries,
  pmcEntries = [],
  pricing = {},
  termsContext = {}
) {
  const totalPmcCost = pmcEntries.reduce((sum, entry) => sum + Number(entry.totalCost || 0), 0);
  const specificTerms = Array.isArray(termsContext.specificTerms)
    ? termsContext.specificTerms
    : buildSpecificTermsPayload(
      termsContext.standardTerms || [],
      termsContext.selectedStandardTerms || [],
      additionalTermEntries,
      termsContext.previewOrder || []
    );
  const liveCostPricingMultiplier = resolveCostPricingMultiplier({
    rows,
    quotationType: quotation.type,
    pmcEntries,
    overheadsPercent: pricing.overheadsPct ?? quotation.overheadsPct ?? 0,
    markUpPercent: pricing.markUpPct ?? quotation.markUpPct ?? 0,
    backendPricingMetrics: quotation.pricingMetrics,
    preferBackendPricing: rows.length === 0,
    forceBackendSheetCosts: rows.length === 0,
  });
  const sheetTotals = computeWorkingTotals(rows, quotation.type, {
    costPricingMultiplier: liveCostPricingMultiplier,
  });
  const pricingMetrics = computeWorkingSheetPricingMetrics({
    rows,
    quotationType: quotation.type,
    pmcEntries,
    overheadsPct: pricing.overheadsPct ?? quotation.overheadsPct ?? 0,
    markUpPct: pricing.markUpPct ?? quotation.markUpPct ?? 0,
  });

  return {
    overheadsPct: Number(pricing.overheadsPct ?? quotation.overheadsPct ?? 0) || 0,
    markUpPct: Number(pricing.markUpPct ?? quotation.markUpPct ?? 0) || 0,
    totalPmcCost,
    totalInputSupplyAmount: sheetTotals.supplyInputAmount,
    totalInputInstallationAmount: sheetTotals.inputInstallationAmount,
    totalSellingSupplyAmount: sheetTotals.supplySubtotal,
    totalSellingInstallationAmount: sheetTotals.installationSubtotal,
    netProfit: pricingMetrics.netProfit,
    netMarginPct: pricingMetrics.netMarginPct,
    grossProfit: pricingMetrics.grossProfit,
    grossMarginPct: pricingMetrics.grossMarginPct,
    calculatedSellingPrice: pricingMetrics.calculatedSellingPrice,
    realizedGrossMargin: pricingMetrics.realizedGrossMargin,
    lineItems: rows.map((row) => {
      const prepared = computePreparedWorkingRow(row, quotation.type, {
        costPricingMultiplier: liveCostPricingMultiplier,
      });
      const lineItemId = extractNumericId(row.lineItemId ?? row.quotationLineId);
      // Exact latest UI rates (Excel-rounded) — never substitute catalogue/defaults/stale formula.
      const latestInputSupplyRate = resolveSupplyRateForPayload(
        row.initialUnitRate,
        prepared.initialUnitRate,
        Boolean(row.initialUnitRateManual)
      );
      const latestSellingSupplyRate = resolveSupplyRateForPayload(
        row.unitRate,
        prepared.unitRate,
        Boolean(row.unitRateManual)
      );
      const qty = parseCurrency(row.qty) || 1;
      const latestMultiplierRate = Boolean(row.adjustmentRateManual)
        ? parseCurrency(row.adjustmentRate ?? prepared.adjustmentRate ?? prepared.multiplier ?? 0)
        : parseCurrency(prepared.adjustmentRate ?? row.adjustmentRate ?? prepared.multiplier ?? 0);
      const latestInstallMultiplier = Boolean(row.installationMultiplierManual)
        ? parseCurrency(row.installationMultiplier ?? prepared.installationMultiplier ?? 1)
        : parseCurrency(prepared.installationMultiplier ?? row.installationMultiplier ?? 1);
      const lineItem = {
        ...(lineItemId ? { id: lineItemId } : {}),
        slNo: row.slNo ?? row.sl_no ?? "",
        description: (() => {
          const text = String(row.description || "").trim();
          return isClientPlaceholderSystemName(text) ? "" : (row.description || "");
        })(),
        brandId: String(row.brandId || row.make || ""),
        partNumber: String(row.partNo || ""),
        uom: row.uom || "",
        quantity: qty,
        remarks: row.remarks || "",
        // Never persist placeholder labels (Default System, etc.) — they reappear in GET/PDF.
        systemName: sanitizeSystemNameForPayload(row.systemName),
        netPriceRulePct: parseCurrency(prepared.netPrice),
        netPrice: parseCurrency(prepared.netPrice),
        discountPct: parseCurrency(row.discount),
        listPrice: parseCurrency(prepared.listPrice),
        tPrice: parseCurrency(prepared.listPrice),
        inputSupplyRate: latestInputSupplyRate,
        inputSupplyAmount: latestInputSupplyRate * qty,
        multiplierDiscountType: prepared.adjustmentType || "Multiplier",
        multiplierDiscountRate: latestMultiplierRate,
        sellingSupplyRate: latestSellingSupplyRate,
        sellingSupplyAmount: latestSellingSupplyRate * qty,
        // `multiplier` must be the applied Mult/Disc rate. System-line GET often omits
        // multiplierDiscountRate and only returns `multiplier` — if we store C+P here,
        // mobile/desktop reload replaces the user's Mult/Disc Rate after refresh.
        multiplier: String(row.adjustmentType || prepared.adjustmentType || "").trim() === "Discount %"
          ? parseCurrency(
            prepared.cpMultiplier ||
            resolveRowCpMultiplier(row, liveCostPricingMultiplier) ||
            row.multiplier ||
            0
          )
          : latestMultiplierRate,
        cpMultiplier: parseCurrency(
          prepared.cpMultiplier || resolveRowCpMultiplier(row, liveCostPricingMultiplier) || 0
        ),
        adjustmentType: String(row.adjustmentType || "Multiplier"),
        adjustmentRate: latestMultiplierRate,
        inputInstallationRate: parseCurrency(row.installationPerUnit),
        installationMultiplier: supportsInstallation(quotation.type)
          ? latestInstallMultiplier
          : parseCurrency(row.installationMultiplier || 0),
      };

      if (supportsInstallation(quotation.type)) {
        lineItem.inputInstallationAmount = prepared.numericTotals.inputInstallationAmount;
        lineItem.sellingInstallationRate = prepared.numericTotals.sellingInstallationRate;
        lineItem.sellingInstallationAmount = prepared.numericTotals.sellingInstallationAmount;
      }

      return lineItem;
    }),
    pmcs: pmcEntries.map((entry, index) => {
      const headType = canonicalizePmcHeadType(entry.costHeadType ?? entry.headType);
      // Quotation PMC API persists Unit Rate via `rate`. BOQ keeps the value in
      // `unitRate` in UI state (rate is ""), so map unitRate → rate on save.
      // Do not use `??` alone — empty string would block the fallback and send 0.
      const resolvedUnitRate = hasPmcMetricValue(entry.unitRate)
        ? entry.unitRate
        : entry.rate;
      const resolvedRate = headType === "BOQ_QTY_BASED"
        ? resolvedUnitRate
        : (hasPmcMetricValue(entry.rate) ? entry.rate : entry.unitRate);
      return {
        id: extractNumericId(entry.id) || 0,
        pmcConfigId: extractNumericId(entry.pmcConfigId) || 0,
        category: entry.category || "",
        headName: entry.headName || `PMC Head ${index + 1}`,
        costHeadType: normalizePmcCostHeadType(entry.costHeadType ?? entry.headType) || "",
        lineName: entry.lineName || entry.description || "",
        description: entry.lineName || entry.description || "",
        nos: parseCurrency(entry.nos),
        months: parseCurrency(entry.months),
        rate: parseCurrency(resolvedRate ?? 0),
        boqQty: parseCurrency(entry.boqQty),
        consQty: parseCurrency(entry.consQty),
        unitRate: parseCurrency(resolvedUnitRate ?? 0),
        totalCost: computePmcRowTotal(
          entry,
          entry.rawColumns?.length ? entry.rawColumns : entry.columns || []
        ) || Number(entry.totalCost || 0) || 0,
      };
    }),
    specificTerms,
  };
}

function buildBlankWorkingRow(type, system = {}) {
  return {
    id: `row-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
    lineItemId: null,
    slNo: "",
    description: "",
    brandId: "",
    make: "",
    productId: "",
    partNo: "",
    uom: "",
    qty: "",
    listPrice: "",
    netPrice: "",
    discount: "",
    initialUnitRate: "",
    adjustmentType: "Multiplier",
    adjustmentRate: "",
    adjustmentRateManual: false,
    initialUnitRateManual: false,
    multiplier: "",
    cpMultiplier: "",
    unitRate: "",
    unitRateManual: false,
    installationPerUnit: supportsInstallation(type) ? "" : "0",
    installationMultiplier: supportsInstallation(type) ? "" : "0",
    installationMultiplierManual: false,
    remarks: "",
    systemId: system.id ?? system.systemId ?? "",
    systemName: system.name ?? system.systemName ?? "",
  };
}

/** Client-only empty placeholders must never be PUT as new Working Sheet line items. */
function isClientOnlyBlankWorkingRow(row = {}) {
  if (extractNumericId(row.lineItemId ?? row.quotationLineId)) return false;
  const hasContent = Boolean(
    String(row.description || "").trim() ||
    String(row.partNo || "").trim() ||
    String(row.make || row.brandId || "").trim() ||
    String(row.productId || "").trim() ||
    String(row.slNo || row.sl_no || "").trim() ||
    String(row.remarks || "").trim() ||
    parseCurrency(row.qty) > 0 ||
    parseCurrency(row.listPrice) > 0 ||
    parseCurrency(row.netPrice) > 0 ||
    parseCurrency(row.initialUnitRate) > 0 ||
    parseCurrency(row.unitRate) > 0 ||
    parseCurrency(row.installationPerUnit) > 0
  );
  return !hasContent;
}

function filterOutClientOnlyBlankWorkingRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).filter((row) => !isClientOnlyBlankWorkingRow(row));
}

/** Keep a single row per persisted lineItemId (prevents duplicate totals / re-inserts). */
function dedupeWorkingRowsByLineItemId(rows = []) {
  const seenLineIds = new Set();
  const result = [];
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const lineId = extractNumericId(row?.lineItemId ?? row?.quotationLineId);
    if (lineId) {
      const key = String(lineId);
      if (seenLineIds.has(key)) return;
      seenLineIds.add(key);
    }
    result.push(row);
  });
  return result;
}

/** Content key used to drop client copies that already exist as backend line items. */
function workingRowContentKey(row = {}) {
  return [
    String(row.productId || "").trim().toLowerCase(),
    String(row.partNo || "").trim().toLowerCase(),
    String(row.description || "").trim().toLowerCase(),
    String(row.make || row.brandId || "").trim().toLowerCase(),
    String(parseCurrency(row.qty) || 0),
  ].join("|");
}

/**
 * Mobile post-save: reload real system lines so rows carry backend lineItemIds.
 * Without this, client-only rows are PUT again as new inserts and totals double.
 */
async function reloadMobileWorkingRowsAfterSave({
  quotationId,
  systems = [],
  quotationType,
  costPricingMultiplier = "",
  fallbackRows = [],
} = {}) {
  const realSystems = (Array.isArray(systems) ? systems : []).filter((system) => {
    const name = String(system?.name || "").trim();
    return Boolean(name) && !isClientPlaceholderSystemName(name);
  });
  const fallback = dedupeWorkingRowsByLineItemId(
    filterOutClientOnlyBlankWorkingRows(fallbackRows)
  );

  if (!quotationId || !realSystems.length) {
    return fallback.map((row) => ({ ...row, preferBackendAmounts: true }));
  }

  const chunks = await Promise.all(
    realSystems.map(async (system) => {
      try {
        const response = await getQuotationSystemLines(quotationId, system.name);
        const lineItems = extractQuotationSystemLineItems(response);
        if (!lineItems.length) return [];
        return mapSystemApiLinesToWorkingRows(lineItems, system, quotationType, {
          backendDerivedMultiplier: costPricingMultiplier,
        });
      } catch {
        return [];
      }
    })
  );

  const fromApi = dedupeWorkingRowsByLineItemId(
    filterOutClientOnlyBlankWorkingRows(chunks.flat())
  );
  if (fromApi.length) {
    return fromApi.map((row) => ({ ...row, preferBackendAmounts: true }));
  }

  return fallback.map((row) => ({ ...row, preferBackendAmounts: true }));
}

/**
 * Before Working Sheet / C+P save, load any System lines still missing from lazy state.
 * Fetch only real systems associated with the quotation — never probe hardcoded
 * placeholder labels (Default System / Other item / Unassigned / System 1 / …).
 * Mobile refreshes every real system before PUT; desktop loads only systems still
 * missing from lazy state (same contract as expanding a system).
 * Never invent blank rows for empty systems.
 */
async function ensureWorkingRowsLoadedForSave({
  quotationId,
  systems = [],
  rows = [],
  quotationType,
  costPricingMultiplier = "",
  // When false (mobile), refresh every real system before PUT.
  // When true (desktop), fetch only systems still missing from lazy state.
  fetchPlaceholderSystems = true,
} = {}) {
  const current = Array.isArray(rows) ? rows.slice() : [];
  if (!quotationId) {
    return clearPlaceholderSystemFromWorkingRows(
      filterOutClientOnlyBlankWorkingRows(current)
    );
  }

  const realSystems = (Array.isArray(systems) ? systems : []).filter((system) => {
    const name = String(system?.name || "").trim();
    return Boolean(name) && !isClientPlaceholderSystemName(name);
  });

  // Mobile: refresh every real system before PUT. Client rows without lineItemId would
  // otherwise re-POST as new inserts while the backend already has the same line.
  if (!fetchPlaceholderSystems) {
    const apiBySystemKey = new Map();
    await Promise.all(
      realSystems.map(async (system) => {
        const key = String(system.name || "").trim().toLowerCase();
        try {
          const response = await getQuotationSystemLines(quotationId, system.name);
          const lineItems = extractQuotationSystemLineItems(response);
          const mapped = lineItems.length
            ? mapSystemApiLinesToWorkingRows(lineItems, system, quotationType, {
                backendDerivedMultiplier: costPricingMultiplier,
              })
            : [];
          apiBySystemKey.set(key, mapped);
        } catch {
          apiBySystemKey.set(key, null);
        }
      })
    );

    const merged = [];
    const usedSystems = new Set();
    realSystems.forEach((system) => {
      const key = String(system.name || "").trim().toLowerCase();
      if (usedSystems.has(key)) return;
      usedSystems.add(key);
      const apiRows = apiBySystemKey.get(key);
      const localRows = current.filter((row) => rowBelongsToSystem(row, system));
      if (Array.isArray(apiRows) && apiRows.length) {
        const apiByLineId = new Map();
        const apiContentKeys = new Set();
        apiRows.forEach((row) => {
          const lineId = extractNumericId(row.lineItemId ?? row.quotationLineId);
          if (lineId) apiByLineId.set(String(lineId), row);
          apiContentKeys.add(workingRowContentKey(row));
        });

        // Prefer local edits when they already carry a backend lineItemId.
        const byLineId = new Map(apiByLineId);
        localRows.forEach((row) => {
          if (isClientOnlyBlankWorkingRow(row)) return;
          const lineId = extractNumericId(row.lineItemId ?? row.quotationLineId);
          if (lineId) byLineId.set(String(lineId), row);
        });

        // Truly new local lines only (no id and not already represented on the API).
        const unsavedLocal = localRows.filter((row) => {
          if (isClientOnlyBlankWorkingRow(row)) return false;
          if (extractNumericId(row.lineItemId ?? row.quotationLineId)) return false;
          return !apiContentKeys.has(workingRowContentKey(row));
        });

        merged.push(...byLineId.values(), ...unsavedLocal);
        return;
      }
      merged.push(...localRows.filter((row) => !isClientOnlyBlankWorkingRow(row)));
    });

    // Preserve any non-system / unmatched local rows (rare on mobile).
    current.forEach((row) => {
      if (isClientOnlyBlankWorkingRow(row)) return;
      if (realSystems.some((system) => rowBelongsToSystem(row, system))) return;
      merged.push(row);
    });

    return clearPlaceholderSystemFromWorkingRows(
      dedupeWorkingRowsByLineItemId(filterOutClientOnlyBlankWorkingRows(merged))
    );
  }

  // Desktop: only GET lines for real quotation systems still missing from lazy state.
  // Do not probe default/placeholder/non-existent names — clearPlaceholderSystemFromWorkingRows
  // + sanitizeSystemNameForPayload already strip those labels from the PUT payload.
  const systemsToFetch = realSystems.filter((system) => {
    return !current.some((row) => rowBelongsToSystem(row, system));
  });

  const loadedChunks = systemsToFetch.length
    ? await Promise.all(
      systemsToFetch.map(async (system) => {
        try {
          const response = await getQuotationSystemLines(quotationId, system.name);
          const lineItems = extractQuotationSystemLineItems(response);
          if (!lineItems.length) return [];
          return mapSystemApiLinesToWorkingRows(lineItems, system, quotationType, {
            backendDerivedMultiplier: costPricingMultiplier,
          });
        } catch {
          return [];
        }
      })
    )
    : [];

  const merged = current.slice();
  const seenLineIds = new Set(
    merged
      .map((row) => extractNumericId(row.lineItemId ?? row.quotationLineId))
      .filter(Boolean)
      .map(String)
  );
  const seenRowIds = new Set(merged.map((row) => String(row.id)));

  loadedChunks.flat().forEach((row) => {
    const lineId = extractNumericId(row.lineItemId ?? row.quotationLineId);
    if (lineId && seenLineIds.has(String(lineId))) return;
    if (seenRowIds.has(String(row.id))) return;
    if (lineId) seenLineIds.add(String(lineId));
    seenRowIds.add(String(row.id));
    merged.push(row);
  });

  return clearPlaceholderSystemFromWorkingRows(
    dedupeWorkingRowsByLineItemId(filterOutClientOnlyBlankWorkingRows(merged))
  );
}

function normalizeSystemNameList(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry && typeof entry === "object") {
          const fromFields = resolveBackendSystemName(entry);
          if (fromFields) return fromFields;
          const fromName = String(entry.name || "").trim();
          return isClientPlaceholderSystemName(fromName) ? "" : fromName;
        }
        return String(entry || "").trim();
      })
      .filter((name) => name && !isClientPlaceholderSystemName(name));
  }
  return String(value || "")
    .split(",")
    .map((name) => name.trim())
    .filter((name) => name && !isClientPlaceholderSystemName(name));
}

/**
 * Client-invented / hardcoded System labels that must never appear on the Working Sheet,
 * in save payloads, GET hydration, or PDF-driving systemName values.
 * Real Systems come only from backend-created names.
 */
function isClientPlaceholderSystemName(value) {
  const name = String(value || "").trim().toLowerCase();
  if (!name) return true;
  return (
    name === "default" ||
    name === "default system" ||
    name === "defaultsystem" ||
    name === "system" ||
    name === "system 1" ||
    name === "system1" ||
    name === "working sheet" ||
    name === "workingsheet" ||
    name === "other items" ||
    name === "other item" ||
    name === "unassigned" ||
    name === "untitled" ||
    name === "untitled system" ||
    name === "n/a" ||
    name === "na" ||
    name === "none" ||
    name === "null" ||
    name === "undefined"
  );
}

function sanitizeSystemNameForPayload(value) {
  const name = String(value || "").trim();
  return isClientPlaceholderSystemName(name) ? "" : name;
}

function isClientPlaceholderSystemId(value) {
  const id = String(value || "").trim().toLowerCase();
  return !id || id === "default" || id === "unassigned";
}

function sanitizeQuotationSystems(systems = []) {
  if (!Array.isArray(systems)) return [];
  return systems
    .map((system) => ({
      id: String(system?.id || system?.systemId || "").trim(),
      name: String(system?.name || system?.systemName || "").trim(),
    }))
    .filter(
      (system) =>
        system.name &&
        !isClientPlaceholderSystemName(system.name) &&
        !isClientPlaceholderSystemId(system.id)
    );
}

function clearPlaceholderSystemFromWorkingRows(rows = []) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const systemName = String(row?.systemName || "").trim();
    const systemId = String(row?.systemId || "").trim();
    if (
      !isClientPlaceholderSystemName(systemName) &&
      !isClientPlaceholderSystemId(systemId)
    ) {
      return row;
    }
    return {
      ...row,
      systemId: "",
      systemName: "",
    };
  });
}

/** Read System Name only from dedicated backend fields — never from description. */
function resolveBackendSystemName(item = {}) {
  if (!item || typeof item !== "object") return "";
  const name = String(
    item.systemName ??
    item.system_name ??
    item.productServiceName ??
    item.product_service_name ??
    item.productService?.productServiceName ??
    item.productService?.systemName ??
    ""
  ).trim();
  return isClientPlaceholderSystemName(name) ? "" : name;
}

function resolveBackendSystemId(item = {}, { allowGenericId = false } = {}) {
  if (!item || typeof item !== "object") return "";
  const id =
    item.systemId ??
    item.system_id ??
    item.productServiceId ??
    item.productServiceID ??
    item.product_service_id ??
    item.productService?.productServiceId ??
    item.productService?.id ??
    (allowGenericId ? item.id : undefined) ??
    "";
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

/**
 * Collect systems from GET /quotations/get/{id} Working Sheet / system fields.
 * Prefer systemSummaries / systems arrays, then systemNames, then lineItems.systemName.
 */
function extractSystemsFromQuotationApi(item = {}) {
  const systems = [];
  const seenNames = new Set();
  const seenIds = new Set();
  const workingSheet =
    item.workingSheet && typeof item.workingSheet === "object" ? item.workingSheet : null;

  const push = (rawId, rawName) => {
    const name = String(rawName || "").trim();
    if (!name || isClientPlaceholderSystemName(name)) return;
    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey)) return;
    const id = rawId != null && String(rawId).trim() !== "" ? String(rawId).trim() : "";
    if (id && isClientPlaceholderSystemId(id)) return;
    if (id && seenIds.has(id)) return;
    seenNames.add(nameKey);
    if (id) seenIds.add(id);
    systems.push({
      id: id || `name-${name}`,
      name,
    });
  };

  // Backend Working Sheet summaries — primary System Name source on get/{id}.
  [
    item.systemSummaries,
    workingSheet?.systemSummaries,
  ].forEach((collection) => {
    extractApiCollection(collection).forEach((entry) => {
      push(
        resolveBackendSystemId(entry, { allowGenericId: false }),
        resolveBackendSystemName(entry)
      );
    });
  });

  // Explicit system objects — `name` is the System Name on these collections.
  [
    item.systems,
    item.workingSheetSystems,
    workingSheet?.systems,
  ].forEach((collection) => {
    extractApiCollection(collection).forEach((entry) => {
      const rawName =
        resolveBackendSystemName(entry) || String(entry.name || entry.systemName || "").trim();
      if (isClientPlaceholderSystemName(rawName)) return;
      push(
        resolveBackendSystemId(entry, { allowGenericId: true }),
        rawName
      );
    });
  });

  const ids = Array.isArray(item.productServiceIds)
    ? item.productServiceIds
    : Array.isArray(item.systemIds)
      ? item.systemIds
      : Array.isArray(workingSheet?.productServiceIds)
        ? workingSheet.productServiceIds
        : Array.isArray(workingSheet?.systemIds)
          ? workingSheet.systemIds
          : [];
  const names = normalizeSystemNameList(
    Array.isArray(item.systemNames) && item.systemNames.length
      ? item.systemNames
      : Array.isArray(item.productServiceNames) && item.productServiceNames.length
        ? item.productServiceNames
        : Array.isArray(workingSheet?.systemNames) && workingSheet.systemNames.length
          ? workingSheet.systemNames
          : Array.isArray(workingSheet?.productServiceNames) && workingSheet.productServiceNames.length
            ? workingSheet.productServiceNames
            : []
  );
  // Only pair backend ids with backend-provided names — never invent a System name.
  ids.forEach((id, index) => {
    if (!names[index]) return;
    push(id, names[index]);
  });
  names.forEach((name) => {
    push("", name);
  });

  // Product/service lists — only dedicated name fields (not generic product `name`).
  [
    item.productServices,
    item.productServiceList,
    workingSheet?.productServices,
  ].forEach((collection) => {
    extractApiCollection(collection).forEach((entry) => {
      push(
        resolveBackendSystemId(entry, { allowGenericId: true }),
        resolveBackendSystemName(entry)
      );
    });
  });

  // Working Sheet line items — only when the line has an explicit backend System Name.
  const lineSources = [
    item.lineItems,
    item.lines,
    item.items,
    item.quotationItems,
    item.workingRows,
    workingSheet?.lineItems,
    workingSheet?.lines,
    workingSheet?.workingRows,
  ];
  lineSources.forEach((collection) => {
    extractApiCollection(collection).forEach((line) => {
      const systemName = resolveBackendSystemName(line);
      if (!systemName) return;
      push(resolveBackendSystemId(line), systemName);
    });
  });

  return systems;
}

function rowBelongsToSystem(row = {}, system = {}) {
  const rowId = row.systemId != null && row.systemId !== "" ? String(row.systemId) : "";
  const systemId = system.id != null && system.id !== "" ? String(system.id) : "";
  if (rowId && systemId && !String(systemId).startsWith("name-")) return rowId === systemId;
  const rowName = String(row.systemName || "").trim().toLowerCase();
  const systemName = String(system.name || "").trim().toLowerCase();
  return Boolean(rowName && systemName && rowName === systemName);
}

function resolveQuotationSystems(quotation = {}, rows = []) {
  // Prefer the backend-mapped systems list (including empty) — never invent Systems client-side.
  if (Array.isArray(quotation.systems)) {
    return sanitizeQuotationSystems(quotation.systems);
  }

  const systems = [];
  const seenIds = new Set();
  const seenNames = new Set();

  const push = (rawId, rawName) => {
    const name = String(rawName || "").trim();
    if (!name || isClientPlaceholderSystemName(name)) return;
    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey)) return;
    const id = rawId != null && String(rawId).trim() !== "" ? String(rawId).trim() : "";
    if (id && isClientPlaceholderSystemId(id)) return;
    if (id && seenIds.has(id)) return;
    seenNames.add(nameKey);
    if (id) seenIds.add(id);
    systems.push({ id: id || `name-${name}`, name });
  };

  extractApiCollection(quotation.systemSummaries).forEach((entry) => {
    push(
      resolveBackendSystemId(entry),
      resolveBackendSystemName(entry)
    );
  });

  const ids = Array.isArray(quotation.productServiceIds) ? quotation.productServiceIds : [];
  const names = normalizeSystemNameList(
    Array.isArray(quotation.systemNames) && quotation.systemNames.length
      ? quotation.systemNames
      : []
  );

  ids.forEach((id, index) => {
    if (!names[index]) return;
    push(id, names[index]);
  });

  names.forEach((name) => {
    push("", name);
  });

  // Do not derive Systems from working rows — rows may be blank or client-authored.
  void rows;

  return systems;
}

function ensureSystemWorkingRows(rows = [], systems = [], type) {
  const current = Array.isArray(rows) && rows.length ? rows.map((row) => ({ ...row })) : [];
  if (!systems.length) {
    return current.length ? current : [buildBlankWorkingRow(type)];
  }
  // Do not auto-create blank rows or assign a default System — systems load lazily from backend.
  return current;
}

function validateWorkingRowsForSubmit(rows = []) {
  const invalidRows = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => {
      const hasCatalogReference = String(row.partNo || "").trim() || String(row.productId || "").trim();
      if (!hasCatalogReference) return false;
      return parseCurrency(row.listPrice) <= 0;
    });

  if (!invalidRows.length) return "";

  const rowNumbers = invalidRows.map(({ index }) => index + 1).join(", ");
  return `Row ${rowNumbers} ${invalidRows.length === 1 ? "has" : "have"} a part number but no catalogue list price. Select a valid catalogue part or enter pricing before submitting.`;
}

function formatCatalogWorkingAmount(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return formatInputNumber(parsed);
}

function buildRowFromCatalogProduct(row, product = {}) {
  const listPrice = Number(product.listPrice || 0);
  const netPrice = Number(product.netPrice || 0);
  const standardDiscount = product.standardDiscountPercent;
  const initialInputPrice = product.initialInputPrice;

  return {
    ...row,
    ...CLEARED_BACKEND_AMOUNT_FIELDS,
    productId: product.id ? String(product.id) : row.productId,
    partNo: product.partNumber || row.partNo,
    make: product.make || product.manufacturer || row.make,
    brandId: product.make || product.manufacturer || row.brandId || row.make,
    // Keep Excel/user description only — never fill from catalogue.
    uom: row.uom || product.uom || product.unitOfMeasure || "",
    listPrice: listPrice > 0 ? formatInputNumber(listPrice) : "",
    netPrice: netPrice > 0 ? formatInputNumber(netPrice) : "",
    discount: formatCatalogWorkingAmount(standardDiscount),
    // Same Excel whole rounding as Input Supply Amount (rate × qty uses this rounded rate).
    initialUnitRate: initialInputPrice != null && initialInputPrice !== ""
      ? toExcelWholeRate(initialInputPrice)
      : "",
    initialUnitRateManual: false,
    // New catalogue selection recalculates Selling Supply Rate from Mult/Disc.
    unitRate: "",
    unitRateManual: false,
  };
}

function mergeRowWithCatalogProduct(row, product = {}) {
  const catalogRow = buildRowFromCatalogProduct(row, product);
  // Only keep a rate when the row actually has one. lineItemId alone must not
  // block catalogue/derived rates (Excel rows often have id + empty rate + amount 0).
  const hasPersistedInputRate = Boolean(row.initialUnitRateManual)
    ? String(row.initialUnitRate ?? "").trim() !== ""
    : !isUnsetNumeric(row.initialUnitRate);
  const hasPersistedUnitRate = Boolean(row.unitRateManual)
    ? String(row.unitRate ?? "").trim() !== ""
    : !isUnsetNumeric(row.unitRate);

  return {
    ...row,
    ...CLEARED_BACKEND_AMOUNT_FIELDS,
    productId: row.productId || catalogRow.productId,
    partNo: row.partNo || catalogRow.partNo,
    make: row.make || catalogRow.make,
    brandId: row.brandId || catalogRow.brandId,
    uom: row.uom || catalogRow.uom,
    listPrice: catalogRow.listPrice || row.listPrice,
    netPrice: String(row.netPrice || "").trim() ? row.netPrice : catalogRow.netPrice,
    discount: String(row.discount || "").trim() ? row.discount : catalogRow.discount,
    // Never overwrite saved/API/edited supply rates with catalogue defaults.
    initialUnitRate: hasPersistedInputRate
      ? row.initialUnitRate
      : (catalogRow.initialUnitRate || row.initialUnitRate),
    initialUnitRateManual: hasPersistedInputRate,
    unitRate: hasPersistedUnitRate ? row.unitRate : "",
    unitRateManual: hasPersistedUnitRate,
  };
}

async function resolveCatalogProductForRow(row = {}) {
  const productId = String(row.productId || "").trim();
  const partNo = String(row.partNo || "").trim();
  const make = String(row.make || "").trim();
  if (!productId && !(partNo && make)) return null;

  if (productId) {
    try {
      const detailed = await fetchCatalogProductById(productId);
      if (detailed) return detailed;
    } catch {
      // Fall through to make/part lookup.
    }
  }

  if (!partNo || !make) return null;

  try {
    const options = await fetchPartNumbersByMake(make);
    const matched = options.find((item) => String(item.partNumber || "").trim() === partNo);
    if (!matched) return null;
    if (matched.id) {
      try {
        const detailed = await fetchCatalogProductById(matched.id);
        return detailed || matched;
      } catch {
        return matched;
      }
    }
    return matched;
  } catch {
    return null;
  }
}

function hydrateQuotation(quotation) {
  const systems = Array.isArray(quotation.systems)
    ? quotation.systems
    : resolveQuotationSystems(quotation, quotation.workingRows || []);
  const rows = Array.isArray(quotation.workingRows) && quotation.workingRows.length
    ? quotation.workingRows
    // Systems mean line items are lazy — do not invent a blank Working Sheet row.
    : (systems.length ? [] : [buildBlankWorkingRow(quotation.type)]);
  const backendDerivedMultiplier = resolveBackendDerivedMultiplier(
    quotation.pricingMetrics,
    quotation.type
  );
  const preparedWorkingRows = rows.map((row) => {
    const adjustmentType = String(row.adjustmentType || "").trim() === "Discount %" ? "Discount %" : "Multiplier";
    const cpMultiplier = resolveRowCpMultiplier(row, backendDerivedMultiplier) ||
      (adjustmentType === "Multiplier" ? String(row.adjustmentRate ?? row.multiplier ?? "").trim() : String(row.multiplier ?? "").trim());
    const rateIsPlaceholder = isPlaceholderMultiplierOne(row.adjustmentRate, cpMultiplier);
    let adjustmentRate = "";
    if (row.adjustmentRateManual && String(row.adjustmentRate ?? "").trim() !== "") {
      adjustmentRate = String(row.adjustmentRate);
    } else if (adjustmentType === "Multiplier") {
      if (!String(row.adjustmentRate ?? "").trim() || rateIsPlaceholder) {
        adjustmentRate = String(cpMultiplier || row.adjustmentRate || row.multiplier || "");
      } else {
        adjustmentRate = String(row.adjustmentRate || cpMultiplier || row.multiplier || "");
      }
    } else {
      adjustmentRate = String(row.adjustmentRate || row.multiplier || "");
    }
    const installRaw = String(row.installationMultiplier ?? "").trim();
    const installShouldUseCp = shouldUseCpMultiplierDefault(
      installRaw,
      cpMultiplier,
      Boolean(row.installationMultiplierManual) && !isUnsetNumeric(installRaw) && !isPlaceholderMultiplierOne(installRaw, cpMultiplier)
    );
    const installationMultiplier = installShouldUseCp
      ? cpMultiplier
      : (isUnsetNumeric(installRaw) ? "" : (row.installationMultiplier ?? ""));
    const installationMultiplierManual = Boolean(
      row.installationMultiplierManual &&
      !isUnsetNumeric(installRaw) &&
      !isPlaceholderMultiplierOne(installRaw, cpMultiplier)
    );
    const prepared = computePreparedWorkingRow(
      {
        ...row,
        adjustmentType,
        adjustmentRate,
        adjustmentRateManual: Boolean(row.adjustmentRateManual) && !rateIsPlaceholder,
        installationMultiplier,
        installationMultiplierManual,
        initialUnitRateManual: Boolean(row.initialUnitRateManual),
        cpMultiplier: cpMultiplier || "",
      },
      quotation.type,
      { costPricingMultiplier: backendDerivedMultiplier || cpMultiplier }
    );
    return {
      ...row,
      adjustmentType,
      adjustmentRate,
      adjustmentRateManual: Boolean(row.adjustmentRateManual) && !rateIsPlaceholder,
      // Keep Excel-rounded supply rates + manual flags so GET→UI does not lose saved values.
      initialUnitRate: prepared.initialUnitRate,
      initialUnitRateManual:
        Boolean(row.initialUnitRateManual) || !isUnsetNumeric(prepared.initialUnitRate),
      unitRate: prepared.unitRate,
      // Do not treat formula-derived Selling Supply as a manual edit — that blocks C+P reseeding.
      unitRateManual: Boolean(row.unitRateManual),
      installationMultiplier,
      installationMultiplierManual,
      multiplier: String(
        adjustmentType === "Multiplier"
          ? (adjustmentRate || cpMultiplier || "")
          : (cpMultiplier || row.multiplier || "")
      ),
      cpMultiplier: cpMultiplier || "",
    };
  });

  const normalizedSystems = sanitizeQuotationSystems(
    Array.isArray(quotation.systems)
      ? quotation.systems
      : normalizeSystemNameList(quotation.systemNames || quotation.systemName).map((name) => ({
          id: `name-${name}`,
          name,
        }))
  );
  const workingRows = clearPlaceholderSystemFromWorkingRows(preparedWorkingRows);

  return {
    ...quotation,
    version: resolveBackendVersionNo(quotation) ?? quotation.version,
    versionLabel: formatBackendVersionLabel(quotation),
    statusTone: statusTone(quotation.status),
    updatedLabel: formatDate(quotation.updated),
    tenderDueLabel: formatDate(quotation.tenderDue),
    expiryLabel: formatDate(quotation.expiry),
    expired: isExpired(quotation.expiry),
    systems: normalizedSystems,
    systemNames: normalizedSystems.map((system) => system.name),
    systemName: normalizedSystems.map((system) => system.name).join(", "),
    workingRows,
    pmcs: Array.isArray(quotation.pmcs) ? quotation.pmcs : [],
    specificTerms: Array.isArray(quotation.specificTerms) ? quotation.specificTerms : [],
    overheadsPct: String(quotation.overheadsPct ?? "0"),
    markUpPct: String(quotation.markUpPct ?? "0"),
    totalPmcCost: Number(quotation.totalPmcCost ?? 0) || 0,
  };
}

function createAdditionalTermEntry(index = 0, overrides = {}) {
  return {
    id: overrides.id || `custom-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
    title: overrides.title ?? "",
    description: overrides.description || "",
  };
}

function normalizeAdditionalTermEntry(entry = {}, index = 0) {
  if (typeof entry === "string") {
    return createAdditionalTermEntry(index, { description: entry });
  }
  return {
    id: entry.id || `custom-${index + 1}`,
    title: entry.title ?? "",
    description: entry.description || "",
  };
}

function getAdditionalTermDisplayTitle(entry, index) {
  void index;
  return String(entry?.title || "").trim();
}

function getVisibleTerms(standardTerms, selectedIds, additionalTermEntries, order) {
  const selectedIdSet = new Set((selectedIds || []).map((id) => String(id)));
  const baseTerms = standardTerms
    .filter((term) => selectedIdSet.has(String(term.id)))
    .map((term) => ({
      id: term.id,
      title: term.title,
      description: term.description || term.text || "",
      source: "standard",
      libraryId: term.id,
    }));

  const additional = (Array.isArray(additionalTermEntries) ? additionalTermEntries : [])
    .map((entry, index) => normalizeAdditionalTermEntry(entry, index))
    .filter((entry) => String(entry.description || "").trim() || String(entry.title || "").trim())
    .map((entry) => ({
      id: entry.id,
      title: String(entry.title || "").trim(),
      description: entry.description,
      source: "custom",
      libraryId: null,
    }));

  const combined = [...baseTerms, ...additional];
  const lookup = new Map(combined.map((item) => [String(item.id), item]));
  const ordered = (order || [])
    .map((id) => lookup.get(String(id)))
    .filter(Boolean);
  const orderedIds = new Set(ordered.map((item) => String(item.id)));
  const remainder = combined.filter((item) => !orderedIds.has(String(item.id)));
  return [...ordered, ...remainder];
}

function buildSpecificTermsPayload(standardTerms = [], selectedIds = [], additionalTermEntries = [], previewOrder = []) {
  return getVisibleTerms(standardTerms, selectedIds, additionalTermEntries, previewOrder).map((term, index) => ({
    id: term.source === "standard" ? Number(term.libraryId) || 0 : 0,
    title: term.title || `Term ${index + 1}`,
    description: term.description || "",
    displayOrder: index + 1,
  }));
}

function resolveLibraryTermId(term = {}) {
  // Prefer explicit library keys — `id` is often the quotation-term row pk.
  return extractNumericId(
    term.standardTermId ??
    term.libraryTermId ??
    term.libraryId ??
    term.termId ??
    term.term_id ??
    term.id
  );
}

function resolveTermsFromQuotation(specificTerms = [], libraryTerms = []) {
  const sorted = [...specificTerms].sort(
    (a, b) => Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0)
  );
  const selectedIds = [];
  const additionalTermEntries = [];
  const previewOrder = [];
  const usedLibraryIds = new Set();

  sorted.forEach((term) => {
    const description = String(term.description || term.text || "").trim();
    const title = String(term.title || "").trim();
    const termLibraryId = resolveLibraryTermId(term);

    let match = libraryTerms.find((libraryTerm) => {
      const libraryId = extractNumericId(libraryTerm.id);
      if (libraryId && usedLibraryIds.has(String(libraryId))) return false;

      if (termLibraryId && libraryId && String(libraryId) === String(termLibraryId)) {
        return true;
      }

      const libraryTitle = String(libraryTerm.title || "").trim();
      const libraryDescription = String(libraryTerm.description || libraryTerm.text || "").trim();
      return libraryTitle === title && libraryDescription === description;
    });

    // If library content was edited but quotation still has a stale copy, rematch by unique title.
    if (!match && title) {
      const titleMatches = libraryTerms.filter((libraryTerm) => {
        const libraryId = extractNumericId(libraryTerm.id);
        if (libraryId && usedLibraryIds.has(String(libraryId))) return false;
        return String(libraryTerm.title || "").trim() === title;
      });
      if (titleMatches.length === 1) {
        match = titleMatches[0];
      }
    }

    if (match) {
      const matchId = match.id;
      usedLibraryIds.add(String(matchId));
      selectedIds.push(matchId);
      previewOrder.push(matchId);
      return;
    }

    if (!description && !title) return;
    const customId = `custom-${additionalTermEntries.length}`;
    additionalTermEntries.push({
      id: customId,
      title: title,
      description,
    });
    previewOrder.push(customId);
  });

  return {
    selectedIds,
    additionalTermEntries,
    previewOrder,
  };
}

function isDiscountPercentAdjustment(value) {
  return String(value || "").trim() === "Discount %";
}

function sanitizePercentInput(value) {
  return String(value ?? "").replace(/%/g, "").trim();
}

/** Allow typing decimals (e.g. ".", "0.", "12.5") without reformatting mid-entry. */
function sanitizeDiscountRateInput(value) {
  let text = String(value ?? "").replace(/%/g, "").replace(/[^\d.]/g, "");
  const dotIndex = text.indexOf(".");
  if (dotIndex !== -1) {
    text = `${text.slice(0, dotIndex + 1)}${text.slice(dotIndex + 1).replace(/\./g, "")}`;
  }
  return text;
}

function resolveDiscountPercent(row = {}, netPrice = 0, additionalDiscount = 0) {
  const stored = sanitizeDiscountRateInput(row.adjustmentRate);
  const storedNum = parseCurrency(stored);
  const keepManual = Boolean(row.adjustmentRateManual) && stored !== "";
  if (!keepManual) return additionalDiscount;

  if (Math.abs(storedNum) <= 100) return storedNum;
  if (netPrice > 0) {
    const derived = (1 - storedNum / netPrice) * 100;
    if (Number.isFinite(derived)) return Math.max(0, derived);
  }
  return additionalDiscount;
}

function deriveInputSupplyRate(netPrice = 0, discountPct = 0) {
  const net = parseCurrency(netPrice);
  const discount = parseCurrency(discountPct);
  if (!(net > 0)) return 0;
  return net * (1 - (discount / 100));
}

/** Reject corrupted/stale Input Supply Rate values (e.g. rate inflated by listPrice factor). */
function isPlausibleInputSupplyRate(rateValue, netPrice = 0, listPrice = 0) {
  const rate = parseCurrency(rateValue);
  if (!(rate > 0)) return false;
  const net = parseCurrency(netPrice);
  const list = parseCurrency(listPrice);
  const ceiling = Math.max(net, list, 0) * 2;
  if (ceiling > 0 && rate > ceiling) return false;
  return true;
}

/** Excel ROUND to nearest whole for Working Sheet supply rates (display + calc + payload). */
function toExcelWholeRate(value) {
  if (value === null || value === undefined || value === "") return "";
  if (isIncompleteDecimalInput(value)) return String(value);
  const numeric = parseCurrency(value);
  if (!Number.isFinite(numeric)) return "";
  return String(roundExcelWhole(numeric));
}

/**
 * Resolve Input/Selling Supply Rate for the Update payload.
 * Prefer the latest edited state value; never substitute catalogue/formula when the field is manual.
 */
function resolveSupplyRateForPayload(rowValue, preparedValue, isManual) {
  const text = String(rowValue ?? "").trim();
  if (text !== "" && !isIncompleteDecimalInput(text)) {
    return parseCurrency(toExcelWholeRate(text));
  }
  if (isManual) {
    // User cleared or is mid-replace (e.g. 2353 → 2500) — do not send formula/catalogue.
    return text === "" ? 0 : parseCurrency(toExcelWholeRate(text));
  }
  const preparedText = String(preparedValue ?? "").trim();
  if (preparedText !== "" && !isIncompleteDecimalInput(preparedText)) {
    return parseCurrency(toExcelWholeRate(preparedText));
  }
  return 0;
}

function computePreparedWorkingRow(row = {}, quotationType = "Supply", options = {}) {
  const qty = parseCurrency(row.qty);
  const listPrice = parseCurrency(row.listPrice);
  const netPrice = !isUnsetNumeric(row.netPrice)
    ? parseCurrency(row.netPrice)
    : listPrice > 0
      ? listPrice / 2
      : 0;
  const discount = parseCurrency(row.discount);
  const derivedInitialUnitRate = deriveInputSupplyRate(netPrice, discount);
  const initialUnitRateText = String(row.initialUnitRate ?? "").trim();
  const initialUnitRateManual = Boolean(row.initialUnitRateManual);
  const hasStoredInitialUnitRate = initialUnitRateManual
    ? initialUnitRateText !== ""
    : !isUnsetNumeric(row.initialUnitRate);
  // Manual edits always win — even when cleared mid-edit — never refill from catalogue/derived.
  const rawInitialUnitRate = initialUnitRateManual
    ? (initialUnitRateText !== "" ? parseCurrency(row.initialUnitRate) : 0)
    : hasStoredInitialUnitRate
      ? parseCurrency(row.initialUnitRate)
      : derivedInitialUnitRate > 0
        ? derivedInitialUnitRate
        : 0;
  const roundingInputSupply = !isIncompleteDecimalInput(row.initialUnitRate);
  const roundedInitialUnitRate = (hasStoredInitialUnitRate || (!initialUnitRateManual && rawInitialUnitRate))
    ? (roundingInputSupply ? roundExcelWhole(rawInitialUnitRate) : parseCurrency(row.initialUnitRate))
    : 0;
  const hasInputSupplyRate = initialUnitRateManual
    ? initialUnitRateText !== ""
    : hasStoredInitialUnitRate || rawInitialUnitRate > 0 || roundedInitialUnitRate !== 0;

  const adjustmentType = isDiscountPercentAdjustment(row.adjustmentType) ? "Discount %" : "Multiplier";
  const fallbackCpMultiplier = String(options.costPricingMultiplier ?? "").trim();
  const rateText = String(row.adjustmentRate ?? "").trim();
  const liveDerivedText = fallbackCpMultiplier || String(row.cpMultiplier ?? "").trim();
  const liveDerivedValue = parseCurrency(liveDerivedText);
  // Follow live C+P only while this Mult is still the derived value. Saved edits stay put.
  const followsCpDerivedMultiplier =
    adjustmentType === "Multiplier" &&
    liveDerivedValue > 0 &&
    multiplierValueFollowsCostPricing(rateText, row.cpMultiplier, {
      isManual: Boolean(row.adjustmentRateManual),
      nextDerived: liveDerivedText,
    });
  const cpMultiplierValue = followsCpDerivedMultiplier
    ? liveDerivedValue
    : parseCurrency(resolveRowCpMultiplier(row, fallbackCpMultiplier));
  const rateIsPlaceholderOne =
    adjustmentType === "Multiplier" &&
    !row.adjustmentRateManual &&
    isPlaceholderMultiplierOne(rateText || row.multiplier, cpMultiplierValue);
  const hasMultiplierInput = String(row.adjustmentRate ?? row.multiplier ?? "").trim() !== "";
  const multiplier = adjustmentType === "Multiplier"
    ? (followsCpDerivedMultiplier
      ? cpMultiplierValue
      : (hasMultiplierInput
        ? parseCurrency(row.adjustmentRate ?? row.multiplier)
        : (cpMultiplierValue > 0 ? cpMultiplierValue : 0)))
    : 1;
  const discountPercent = resolveDiscountPercent(row, netPrice, discount);
  const discountedUnitRate = netPrice - (netPrice * (discountPercent / 100));
  const unitRateText = String(row.unitRate ?? "").trim();
  const unitRateManual = Boolean(row.unitRateManual);
  // Manual Selling Supply Rate wins; otherwise always apply Mult/Disc formula so C+P
  // Derived Multiplier changes recalculate amounts (do not keep a stale stored unitRate).
  const rawUnitRate = unitRateManual
    ? (unitRateText !== "" ? parseCurrency(row.unitRate) : 0)
    : adjustmentType === "Discount %"
      ? discountedUnitRate
      : roundedInitialUnitRate * multiplier;
  const roundingSellingSupply = !isIncompleteDecimalInput(row.unitRate);
  const roundedUnitRate = unitRateManual
    ? (unitRateText !== ""
      ? (roundingSellingSupply ? roundExcelWhole(rawUnitRate) : parseCurrency(row.unitRate))
      : 0)
    : (rawUnitRate || rawUnitRate === 0 ? roundExcelWhole(rawUnitRate) : 0);
  const hasSellingSupplyRate = unitRateManual
    ? unitRateText !== ""
    : rawUnitRate > 0 || roundedUnitRate !== 0;

  const installationPerUnit = parseCurrency(row.installationPerUnit);
  const installRawText = String(row.installationMultiplier ?? "").trim();
  // Saved Install Mult follows C+P only when it is still the derived value.
  const followsCpInstallMultiplier =
    supportsInstallation(quotationType) &&
    liveDerivedValue > 0 &&
    multiplierValueFollowsCostPricing(installRawText, row.cpMultiplier, {
      isManual: Boolean(row.installationMultiplierManual),
      nextDerived: liveDerivedText,
    });
  const installCpValue = parseCurrency(
    liveDerivedText || resolveRowCpMultiplier(row, fallbackCpMultiplier)
  );
  const installShouldUseCp = followsCpInstallMultiplier && installCpValue > 0;
  const installationMultiplier = supportsInstallation(quotationType)
    ? (row.installationMultiplierManual
      ? (installRawText && !isUnsetNumeric(installRawText) ? parseCurrency(row.installationMultiplier) : 0)
      : (installShouldUseCp
        ? installCpValue
        : (installRawText && !isUnsetNumeric(installRawText)
          ? parseCurrency(row.installationMultiplier)
          : (installCpValue > 0 ? installCpValue : 0))))
    : 0;

  const displayInitialUnitRate = initialUnitRateManual
    ? (isIncompleteDecimalInput(row.initialUnitRate) || initialUnitRateText === ""
      ? String(row.initialUnitRate ?? "")
      : String(roundedInitialUnitRate))
    : isIncompleteDecimalInput(row.initialUnitRate)
      ? String(row.initialUnitRate)
      : (hasInputSupplyRate ? String(roundedInitialUnitRate) : "");
  const displayUnitRate = unitRateManual
    ? (isIncompleteDecimalInput(row.unitRate) || unitRateText === ""
      ? String(row.unitRate ?? "")
      : String(roundedUnitRate))
    : isIncompleteDecimalInput(row.unitRate)
      ? String(row.unitRate)
      : (hasSellingSupplyRate ? String(roundedUnitRate) : "");

  // Prefer backend GET amounts when present; otherwise rates × Qty.
  // Never lock the UI to a stale backend 0 while live rate×qty is non-zero
  // (Excel/API rows often ship amount 0 before catalogue pricing).
  const preferBackendAmounts = row.preferBackendAmounts !== false;
  const backendInitialAmount = pickFiniteNumber(row.backendInputSupplyAmount);
  const backendSellingSupply = pickFiniteNumber(row.backendSellingSupplyAmount);
  const backendInputInstall = pickFiniteNumber(row.backendInputInstallationAmount);
  const backendSellingInstallRate = pickFiniteNumber(row.backendSellingInstallationRate);
  const backendSellingInstallAmount = pickFiniteNumber(row.backendSellingInstallationAmount);

  // Amounts use the same Excel-rounded rates shown in the Rate columns.
  const liveInitialAmount = roundExcelWhole(roundedInitialUnitRate) * qty;
  const liveSellingSupplyAmount = roundExcelWhole(roundedUnitRate) * qty;
  const liveInputInstallationAmount = installationPerUnit * qty;
  const computedSellingInstallationRate = supportsInstallation(quotationType)
    ? installationPerUnit * installationMultiplier
    : 0;
  const liveSellingInstallationAmount = supportsInstallation(quotationType)
    ? computedSellingInstallationRate * qty
    : 0;

  const shouldPreferBackendAmount = (backendAmount, liveAmount) =>
    preferBackendAmounts &&
    backendAmount != null &&
    !(backendAmount === 0 && liveAmount !== 0);

  const initialAmount = shouldPreferBackendAmount(backendInitialAmount, liveInitialAmount)
    ? backendInitialAmount
    : liveInitialAmount;
  const sellingSupplyAmount = shouldPreferBackendAmount(backendSellingSupply, liveSellingSupplyAmount)
    ? backendSellingSupply
    : liveSellingSupplyAmount;
  const inputInstallationAmount = shouldPreferBackendAmount(backendInputInstall, liveInputInstallationAmount)
    ? backendInputInstall
    : liveInputInstallationAmount;
  const sellingInstallationRate = shouldPreferBackendAmount(backendSellingInstallRate, computedSellingInstallationRate)
    ? backendSellingInstallRate
    : computedSellingInstallationRate;
  const sellingInstallationAmount = shouldPreferBackendAmount(backendSellingInstallAmount, liveSellingInstallationAmount)
    ? backendSellingInstallAmount
    : liveSellingInstallationAmount;

  return {
    ...row,
    adjustmentType,
    adjustmentRate: adjustmentType === "Discount %"
      ? (String(row.adjustmentRate ?? "").trim() !== ""
          ? sanitizeDiscountRateInput(row.adjustmentRate)
          : formatInputNumber(discountPercent))
      : (row.adjustmentRateManual
        // Preserve exact user entry (including cleared) — never refill from C+P.
        ? String(row.adjustmentRate ?? "")
        : (followsCpDerivedMultiplier
          ? formatInputNumber(cpMultiplierValue)
          : (rateText && !rateIsPlaceholderOne
            ? row.adjustmentRate
            : (cpMultiplierValue > 0 ? formatInputNumber(cpMultiplierValue) : "")))),
    multiplier: multiplier > 0 ? formatInputNumber(multiplier) : "",
    cpMultiplier: followsCpDerivedMultiplier
      ? (fallbackCpMultiplier || resolveRowCpMultiplier(row, fallbackCpMultiplier))
      : resolveRowCpMultiplier(row, fallbackCpMultiplier),
    installationMultiplier: supportsInstallation(quotationType)
      ? (row.installationMultiplierManual
        ? (installRawText || "")
        : (installShouldUseCp
          ? (installCpValue > 0 ? formatInputNumber(installCpValue) : "")
          : (installRawText && !isUnsetNumeric(installRawText)
            ? row.installationMultiplier
            : (installCpValue > 0 ? formatInputNumber(installCpValue) : ""))))
      : (row.installationMultiplier ?? ""),
    netPrice: !isUnsetNumeric(row.netPrice) ? row.netPrice : (netPrice ? formatInputNumber(netPrice) : ""),
    initialUnitRate: displayInitialUnitRate,
    unitRate: displayUnitRate,
    initialAmount: formatCurrencyWhole(initialAmount),
    amount: formatCurrencyWhole(sellingSupplyAmount),
    installationAmount: formatCurrencyWhole(sellingInstallationAmount),
    inputInstallationAmount: formatCurrencyWhole(inputInstallationAmount),
    sellingInstallationRate: formatCurrencyWhole(sellingInstallationRate),
    numericTotals: {
      initialAmount,
      sellingSupplyAmount,
      inputInstallationAmount,
      sellingInstallationRate,
      sellingInstallationAmount,
    },
  };
}

function applyNetPriceRuleToRows(rows = [], netPricePercentage = 0) {
  const percentage = Number(netPricePercentage) || 0;
  if (!percentage) return rows;

  return rows.map((row) => {
    if (!isUnsetNumeric(row.netPrice)) {
      return row;
    }

    const listPrice = parseCurrency(row.listPrice);
    if (!listPrice) {
      return row;
    }

    const derivedNetPrice = listPrice - ((listPrice * percentage) / 100);
    return {
      ...row,
      netPrice: String(Number(derivedNetPrice.toFixed(2))),
    };
  });
}

function computeWorkingTotals(rows, quotationType, options = {}) {
  const preparedRows = rows.map((row) => computePreparedWorkingRow(row, quotationType, options));
  const totals = preparedRows.reduce((sum, row) => ({
    supplyInputAmount: sum.supplyInputAmount + row.numericTotals.initialAmount,
    supplySellingAmount: sum.supplySellingAmount + row.numericTotals.sellingSupplyAmount,
    inputInstallationAmount: sum.inputInstallationAmount + row.numericTotals.inputInstallationAmount,
    sellingInstallationAmount: sum.sellingInstallationAmount + row.numericTotals.sellingInstallationAmount,
  }), {
    supplyInputAmount: 0,
    supplySellingAmount: 0,
    inputInstallationAmount: 0,
    sellingInstallationAmount: 0,
  });

  const supplySubtotal = totals.supplySellingAmount;
  const installationSubtotal = supportsInstallation(quotationType) ? totals.sellingInstallationAmount : 0;
  return {
    supplyInputAmount: totals.supplyInputAmount,
    supplySubtotal,
    inputInstallationAmount: totals.inputInstallationAmount,
    installationSubtotal,
    customerSellingTotal: supplySubtotal + installationSubtotal,
  };
}

function mapComplaintOption(item = {}) {
  const complaintId = item.complaintId ?? item.id ?? item.raw?.complaintId;
  if (complaintId == null || complaintId === "") return null;
  return {
    id: String(complaintId),
    label: String(complaintId),
  };
}

function sanitizePrefillText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "—" || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
    return "";
  }
  return text;
}

/** Resolve a display name from string/number or employee-like objects (no hardcoded names). */
function pickEmployeeDisplayName(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (typeof candidate === "string" || typeof candidate === "number") {
      const text = sanitizePrefillText(candidate);
      // Skip bare numeric ids used as name placeholders.
      if (text && !/^\d+$/.test(text)) return text;
      continue;
    }
    if (typeof candidate === "object") {
      const text = sanitizePrefillText(
        candidate.name ??
        candidate.employeeName ??
        candidate.fullName ??
        candidate.displayName ??
        candidate.serviceExecutive ??
        candidate.serviceManager ??
        [candidate.firstName ?? candidate.first_name, candidate.lastName ?? candidate.last_name]
          .filter(Boolean)
          .join(" ")
      );
      if (text) return text;
    }
  }
  return "";
}

/** Resolve an employee id from scalar or nested employee objects. */
function pickEmployeeIdValue(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    if (typeof candidate === "object") {
      const nested =
        candidate.empId ??
        candidate.technicianId ??
        candidate.managerId ??
        candidate.employeeId ??
        candidate.id;
      if (nested != null && String(nested).trim() !== "") return String(nested);
      continue;
    }
    const text = String(candidate).trim();
    if (text && /^\d+$/.test(text)) return text;
  }
  return "";
}

/** Map Service Executive / Service Manager from quotation or complaint API payloads. */
function pickQuotationServiceRoleFields(item = {}) {
  if (!item || typeof item !== "object") {
    return { technicianId: "", managerId: "", technicianName: "", managerName: "" };
  }
  const technicianId = pickEmployeeIdValue(
    item.technicianId,
    item.technician_id,
    item.serviceExecutiveId,
    item.service_executive_id,
    item.serviceExecutive,
    item.service_executive,
    item.technician,
    item.serviceExecutiveEmployee,
    item.assignedTechnician
  );
  const managerId = pickEmployeeIdValue(
    item.managerId,
    item.manager_id,
    item.serviceManagerId,
    item.service_manager_id,
    item.serviceManager,
    item.service_manager,
    item.manager,
    item.serviceManagerEmployee,
    item.assignedManager
  );
  const technicianName = pickEmployeeDisplayName(
    item.technicianName,
    item.technician_name,
    item.serviceExecutiveName,
    item.service_executive_name,
    // Prefer dedicated name fields before serviceExecutive (may be an id).
    typeof item.serviceExecutive === "string" && !/^\d+$/.test(String(item.serviceExecutive).trim())
      ? item.serviceExecutive
      : "",
    typeof item.service_executive === "string" && !/^\d+$/.test(String(item.service_executive).trim())
      ? item.service_executive
      : "",
    item.technician,
    item.serviceExecutiveEmployee,
    item.assignedTechnician
  );
  const managerName = pickEmployeeDisplayName(
    item.managerName,
    item.manager_name,
    item.serviceManagerName,
    item.service_manager_name,
    typeof item.serviceManager === "string" && !/^\d+$/.test(String(item.serviceManager).trim())
      ? item.serviceManager
      : "",
    typeof item.service_manager === "string" && !/^\d+$/.test(String(item.service_manager).trim())
      ? item.service_manager
      : "",
    item.manager,
    item.serviceManagerEmployee,
    item.assignedManager
  );
  return {
    technicianId,
    managerId,
    technicianName,
    managerName,
  };
}

function mergeQuotationServiceRoleFields(primary = {}, fallback = {}) {
  const fromPrimary = pickQuotationServiceRoleFields(primary);
  const fromFallback = pickQuotationServiceRoleFields(fallback);
  return {
    technicianId: fromPrimary.technicianId || fromFallback.technicianId || "",
    managerId: fromPrimary.managerId || fromFallback.managerId || "",
    technicianName: fromPrimary.technicianName || fromFallback.technicianName || "",
    managerName: fromPrimary.managerName || fromFallback.managerName || "",
  };
}

function toAssignedEmployeeId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function resolveAssignedEmployeeId(storedId, storedName = "", employees = []) {
  if (storedId != null && String(storedId).trim() !== "") return String(storedId);
  const name = String(storedName || "").trim().toLowerCase();
  if (!name) return "";
  const match = employees.find((item) => String(item.name || "").trim().toLowerCase() === name);
  return match?.id != null ? String(match.id) : "";
}

function resolveEmployeeDisplayName(employeeId, employees = [], fallbackName = "") {
  const id = employeeId != null && employeeId !== "" ? String(employeeId) : "";
  if (id) {
    const match = employees.find((item) => String(item.id) === id);
    if (match?.name) return match.name;
  }
  const fallback = sanitizePrefillText(fallbackName);
  if (fallback) return fallback;
  return id;
}

function mergeUniqueNamedOptions(current = [], incoming) {
  if (!incoming?.name) return current;
  const incomingId = incoming.id != null && incoming.id !== "" ? String(incoming.id) : "";
  const existingIndex = current.findIndex(
    (item) =>
      (incomingId && String(item.id) === incomingId) ||
      String(item.name || "").trim().toLowerCase() === String(incoming.name).trim().toLowerCase()
  );
  if (existingIndex >= 0) {
    const next = [...current];
    next[existingIndex] = {
      ...next[existingIndex],
      ...incoming,
      id: next[existingIndex].id || incoming.id,
      name: incoming.name || next[existingIndex].name,
    };
    return next;
  }
  return [...current, incoming];
}

function mergeUniqueComplaintOptions(current = [], incoming = []) {
  const next = [...current];
  const seen = new Set(current.map((item) => item.id));
  incoming.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    next.push(item);
  });
  return next;
}

function inputClass(hasError) {
  return `min-h-[44px] w-full rounded-[12px] border bg-white px-4 text-[13px] text-[#2d394c] outline-hidden transition placeholder:text-[#96a5bb] ${
    hasError ? "border-[#e4a5a0]" : "border-[#d1d9e8] focus:border-[#95addd]"
  }`;
}

function filterControlClass() {
  return "h-9 w-full rounded-[10px] border border-[#d1d9e8] bg-white px-3 text-[12px] text-[#2d394c] outline-hidden transition placeholder:text-[#96a5bb] focus:border-[#95addd]";
}

const ADD_ENTITY_OPTION = "__add_new_entity__";

const CLEARED_BACKEND_AMOUNT_FIELDS = {
  preferBackendAmounts: false,
  backendInputSupplyAmount: "",
  backendSellingSupplyAmount: "",
  backendInputInstallationAmount: "",
  backendSellingInstallationAmount: "",
  backendSellingInstallationRate: "",
};

const BACKEND_AMOUNT_INVALIDATING_KEYS = new Set([
  "qty",
  "initialUnitRate",
  "unitRate",
  "installationPerUnit",
  "installationMultiplier",
  "adjustmentType",
  "adjustmentRate",
  "listPrice",
  "netPrice",
  "discount",
]);

function computeDerivedMultiplier(calculatedSellingPrice, supplyTotal, installationTotal) {
  const supplyInstallTotal = Number(supplyTotal || 0) + Number(installationTotal || 0);
  if (!(supplyInstallTotal > 0) || !Number.isFinite(calculatedSellingPrice)) return 0;
  return calculatedSellingPrice / supplyInstallTotal;
}

/** Derived Multiplier from persisted GET C+P / Working Sheet pricing metrics (no live rows required). */
function resolveBackendDerivedMultiplier(pricingMetrics = null, quotationType = "Supply") {
  if (!pricingMetrics || typeof pricingMetrics !== "object") return "";
  const calculatedSellingPrice = pickFiniteNumber(pricingMetrics.calculatedSellingPrice);
  if (calculatedSellingPrice == null || !(calculatedSellingPrice > 0)) return "";
  const supplyCost = pickFiniteNumber(pricingMetrics.totalInputSupplyAmount) ?? 0;
  const installationCost = supportsInstallation(quotationType)
    ? (pickFiniteNumber(pricingMetrics.totalInputInstallationAmount) ?? 0)
    : 0;
  const derived = computeDerivedMultiplier(calculatedSellingPrice, supplyCost, installationCost);
  return derived > 0 ? formatInputNumber(Number(derived.toFixed(4))) : "";
}

function resolveCostPricingMultiplier({
  rows = [],
  quotationType,
  pmcEntries = [],
  overheadsPercent = "0",
  markUpPercent = "0",
  backendPricingMetrics = null,
  preferBackendPricing = false,
  forceBackendSheetCosts = false,
} = {}) {
  // Must match CostPricingTab's Derived Multiplier basis — including backend sheet
  // costs when systems are lazy/unloaded — or Working Sheet stays on a stale Mult
  // until the user toggles Discount % → Multiplier (which reads row.cpMultiplier
  // seeded by the C+P tab effect).
  const totals = computeWorkingTotals(rows, quotationType);
  const normalizedPmcEntries = pmcEntries.map((entry, index) => normalizePmcEntry(entry, index));
  const pmcGroups = groupPmcEntriesForDisplay(normalizedPmcEntries);
  const pmcTotal = pmcGroups.reduce((sum, head) => sum + (Number(head.totalCost) || 0), 0);
  const backendSupplyCost = pickFiniteNumber(backendPricingMetrics?.totalInputSupplyAmount);
  const backendInstallationCost = pickFiniteNumber(backendPricingMetrics?.totalInputInstallationAmount);
  const useBackendSheetCosts =
    (rows.length === 0 || preferBackendPricing || forceBackendSheetCosts) &&
    (backendSupplyCost != null || backendInstallationCost != null);
  const supplyCost = useBackendSheetCosts && backendSupplyCost != null
    ? backendSupplyCost
    : totals.supplyInputAmount;
  const installationCost = supportsInstallation(quotationType)
    ? (useBackendSheetCosts && backendInstallationCost != null
      ? backendInstallationCost
      : totals.inputInstallationAmount)
    : 0;
  const totalCost = supplyCost + installationCost + pmcTotal;
  const overheadsAmount = totalCost * (parseCurrency(overheadsPercent) / 100);
  const netProfit = totalCost * (parseCurrency(markUpPercent) / 100);
  const calculatedSellingPrice = totalCost + overheadsAmount + netProfit;
  const derived = computeDerivedMultiplier(calculatedSellingPrice, supplyCost, installationCost);
  if (derived > 0) return formatInputNumber(Number(derived.toFixed(4)));
  return resolveBackendDerivedMultiplier(backendPricingMetrics, quotationType);
}

/** Latest Cost + Pricing metrics from current Working Sheet / C+P state (same formulas as UI). */
function computeWorkingSheetPricingMetrics({
  rows = [],
  quotationType,
  pmcEntries = [],
  overheadsPct = 0,
  markUpPct = 0,
} = {}) {
  const totals = computeWorkingTotals(rows, quotationType);
  const normalizedPmcEntries = pmcEntries.map((entry, index) => normalizePmcEntry(entry, index));
  const pmcGroups = groupPmcEntriesForDisplay(normalizedPmcEntries);
  const pmcTotal = pmcGroups.reduce((sum, head) => sum + (Number(head.totalCost) || 0), 0);
  const supplyCost = totals.supplyInputAmount;
  const installationCost = supportsInstallation(quotationType)
    ? totals.inputInstallationAmount
    : 0;
  const totalCost = supplyCost + installationCost + pmcTotal;
  const overheadsAmount = totalCost * (parseCurrency(overheadsPct) / 100);
  const netProfit = totalCost * (parseCurrency(markUpPct) / 100);
  const grossProfit = overheadsAmount + netProfit;
  const calculatedSellingPrice = totalCost + grossProfit;
  const netMarginPct = calculatedSellingPrice > 0 ? (netProfit / calculatedSellingPrice) * 100 : 0;
  const grossMarginPct = calculatedSellingPrice > 0 ? (grossProfit / calculatedSellingPrice) * 100 : 0;

  const derivedMultiplier = computeDerivedMultiplier(
    calculatedSellingPrice,
    supplyCost,
    installationCost
  );
  // Match Cost + Pricing Realized Gross Margin: project non-manual Mult rows with derived multiplier.
  const projectedSellingTotal = derivedMultiplier > 0
    ? computeWorkingTotals(
      rows.map((row) => {
        if (isDiscountPercentAdjustment(row.adjustmentType) || row.adjustmentRateManual) {
          return row;
        }
        return {
          ...row,
          adjustmentType: "Multiplier",
          adjustmentRate: formatInputNumber(Number(derivedMultiplier.toFixed(4))),
          multiplier: formatInputNumber(Number(derivedMultiplier.toFixed(4))),
          unitRate: "",
          ...(supportsInstallation(quotationType) && !row.installationMultiplierManual
            ? { installationMultiplier: formatInputNumber(Number(derivedMultiplier.toFixed(4))) }
            : {}),
        };
      }),
      quotationType
    ).customerSellingTotal
    : totals.customerSellingTotal;
  const realizedSellingTotal = projectedSellingTotal > 0 ? projectedSellingTotal : totals.customerSellingTotal;
  const realizedGrossMargin = realizedSellingTotal > 0
    ? ((realizedSellingTotal - totalCost) / realizedSellingTotal) * 100
    : 0;

  return {
    supplyCost,
    installationCost,
    totalCost,
    overheadsAmount,
    netProfit,
    netMarginPct,
    grossProfit,
    grossMarginPct,
    calculatedSellingPrice,
    realizedGrossMargin,
    realizedSellingTotal,
  };
}

function resolveRowCpMultiplier(row = {}, fallback = "") {
  const fromRow = String(row?.cpMultiplier ?? "").trim();
  if (fromRow) return fromRow;
  const fromFallback = String(fallback ?? "").trim();
  if (fromFallback) return fromFallback;
  return "";
}

/** True when two multiplier inputs represent the same numeric value (C+P toFixed(4) tolerant). */
function multipliersApproximatelyEqual(left, right) {
  const a = parseCurrency(left);
  const b = parseCurrency(right);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= 0.000101;
}

/**
 * True when two multipliers render as the same Working Sheet value.
 * Derived C+P is often full precision while the saved cell is 2 decimal places.
 */
function multipliersMatchForPersistence(left, right) {
  if (multipliersApproximatelyEqual(left, right)) return true;
  const a = formatMultiplierFieldValue(left);
  const b = formatMultiplierFieldValue(right);
  return Boolean(a) && a === b;
}

/** True when a persisted multiplier looks like the old hardcoded default of 1. */
function isPlaceholderMultiplierOne(value, derivedMultiplier) {
  const derived = String(derivedMultiplier ?? "").trim();
  if (!derived) return false;
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  return multipliersApproximatelyEqual(raw, 1) && !multipliersApproximatelyEqual(raw, derived);
}

/**
 * Empty / 0 / hardcoded 1 are not real manual multiplier values when a C+P derived
 * multiplier is available — use the derived value instead.
 */
function shouldUseCpMultiplierDefault(value, derivedMultiplier, isManual = false) {
  if (isManual) return false;
  const derived = String(derivedMultiplier ?? "").trim();
  if (!derived || !(parseCurrency(derived) > 0)) return false;
  const raw = String(value ?? "").trim();
  if (!raw || isUnsetNumeric(raw)) return true;
  return isPlaceholderMultiplierOne(raw, derived);
}

/**
 * Cost + Pricing may refill a multiplier only when it is still following C+P.
 * A saved user value (manual, or different from this row's C+P reference) is kept.
 */
function multiplierValueFollowsCostPricing(currentValue, rowCpMultiplier, { isManual = false, nextDerived = "" } = {}) {
  if (isManual) return false;
  const raw = String(currentValue ?? "").trim();
  if (!raw || isUnsetNumeric(raw)) return true;
  const derivedForPlaceholder = String(nextDerived || rowCpMultiplier || "").trim();
  if (derivedForPlaceholder && isPlaceholderMultiplierOne(raw, derivedForPlaceholder)) return true;
  const cp = String(rowCpMultiplier ?? "").trim();
  if (cp && multipliersMatchForPersistence(raw, cp)) return true;
  return false;
}

/**
 * Format a Mult/Disc or Install Mult cell for comparison/seed updates.
 * Empty stays empty (do not coerce to "0") so C+P can populate defaults.
 */
function formatMultiplierFieldValue(value) {
  const text = String(value ?? "").trim();
  if (!text) return "";
  return formatInputNumber(parseCurrency(text));
}

const PDF_TEMPLATE_BODY_MAX = 2000;
const PDF_TEMPLATE_MAX_BYTES = 2 * 1024 * 1024;
const PDF_TEMPLATE_ACCEPT = "image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg";

function formatFileSizeBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function resolvePdfAssetUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;

  // Backend may return raw base64 (no data: URI / no file URL). Detect common image magic bytes.
  const compact = raw.replace(/\s+/g, "");
  const mime = compact.startsWith("iVBOR")
    ? "image/png"
    : compact.startsWith("/9j/")
      ? "image/jpeg"
      : compact.startsWith("R0lGOD")
        ? "image/gif"
        : compact.startsWith("UklGR")
          ? "image/webp"
          : "";
  if (mime && compact.length > 32 && /^[A-Za-z0-9+/]+=*$/.test(compact)) {
    return `data:${mime};base64,${compact}`;
  }

  try {
    return resolveApiUrl(raw.startsWith("/") ? raw : `/${raw}`);
  } catch {
    return raw;
  }
}

function createEmptyPdfImageSlot() {
  return {
    file: null,
    previewUrl: "",
    fileName: "",
    fileSize: null,
    existing: false,
    removed: false,
    objectUrl: "",
    retainedPayload: "",
  };
}

function pdfMimeFromBase64Magic(compact) {
  if (compact.startsWith("iVBOR")) return "image/png";
  if (compact.startsWith("/9j/")) return "image/jpeg";
  if (compact.startsWith("R0lGOD")) return "image/gif";
  if (compact.startsWith("UklGR")) return "image/webp";
  return "";
}

function asNamedPdfImageFile(blob, fileName) {
  if (blob instanceof File && String(blob.name || "").trim()) return blob;
  const type = blob?.type || "image/png";
  const name = String(fileName || "").trim() || "image.png";
  return new File([blob], name, { type });
}

function base64ToPdfImageFile(base64, mime, fileName) {
  const compact = String(base64 || "").replace(/\s+/g, "");
  if (!compact) return null;
  const binary = atob(compact);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], String(fileName || "").trim() || "image.png", {
    type: mime || "image/png",
  });
}

/**
 * Backend PUT requires headerImage/footerImage multipart parts (not null).
 * Rebuild a File from the new upload, or from the retained GET/PUT payload / preview.
 */
async function pdfImageSlotToUploadFile(slot, fallbackName = "image.png") {
  if (!slot || slot.removed) return null;
  const fileName = String(slot.fileName || "").trim() || fallbackName;

  if (slot.file instanceof Blob) {
    return asNamedPdfImageFile(slot.file, fileName);
  }

  const retained = String(slot.retainedPayload || "").trim();
  const preview = String(slot.previewUrl || "").trim();
  const source = retained || preview;
  if (!source) return null;

  const dataMatch = source.match(/^data:([^;]+);base64,(.+)$/i);
  if (dataMatch) {
    try {
      return base64ToPdfImageFile(dataMatch[2], dataMatch[1], fileName);
    } catch {
      return null;
    }
  }

  const compact = source.replace(/\s+/g, "");
  const magicMime = pdfMimeFromBase64Magic(compact);
  if (magicMime && compact.length > 32 && /^[A-Za-z0-9+/]+=*$/.test(compact)) {
    try {
      return base64ToPdfImageFile(compact, magicMime, fileName);
    } catch {
      return null;
    }
  }

  if (/^(blob:|https?:)/i.test(source)) {
    try {
      const headers = {};
      if (!source.startsWith("blob:")) {
        const authToken =
          typeof sessionDB.getAccessToken === "function" ? sessionDB.getAccessToken() : "";
        const tenantId =
          typeof sessionDB.getTenantId === "function" ? sessionDB.getTenantId() : "";
        const tenantCode =
          typeof sessionDB.getTenantCode === "function" ? sessionDB.getTenantCode() : "";
        const empId = typeof sessionDB.getEmpId === "function" ? sessionDB.getEmpId() : "";
        const authValue = authToken
          ? String(authToken).startsWith("Bearer ") || String(authToken).startsWith("bearer ")
            ? String(authToken)
            : `Bearer ${authToken}`
          : "";
        if (authValue) headers.Authorization = authValue;
        if (tenantId) headers["X-Tenant-Id"] = String(tenantId);
        if (tenantCode) {
          headers["X-Tenant-Code"] = String(tenantCode);
          headers["tenant-code"] = String(tenantCode);
        }
        if (empId) headers["X-Emp-Id"] = String(empId);
        headers["X-Service-Code"] = "CRM09S";
        headers["service-code"] = "CRM09S";
        headers.Accept = "*/*";
      }
      const response = await fetch(source, Object.keys(headers).length ? { headers } : undefined);
      if (!response.ok && !source.startsWith("blob:")) return null;
      const blob = await response.blob();
      if (!(blob instanceof Blob) || !blob.size) return null;
      return asNamedPdfImageFile(blob, fileName);
    } catch {
      return null;
    }
  }

  return null;
}

function mapPdfImageSlotFromApi(prefix, source = {}) {
  const nested =
    source[`${prefix}Image`] && typeof source[`${prefix}Image`] === "object"
      ? source[`${prefix}Image`]
      : null;
  const rawImageValue =
    typeof source[`${prefix}Image`] === "string" ? String(source[`${prefix}Image`]).trim() : "";
  const nestedBase64 =
    typeof nested?.base64 === "string"
      ? String(nested.base64).trim()
      : typeof nested?.data === "string"
        ? String(nested.data).trim()
        : typeof nested?.content === "string"
          ? String(nested.content).trim()
          : "";
  const rawUrl =
    source[`${prefix}ImageUrl`] ??
    source[`${prefix}ImageURL`] ??
    source[`${prefix}_image_url`] ??
    source[`${prefix}Url`] ??
    nested?.url ??
    nested?.imageUrl ??
    nested?.path ??
    rawImageValue;
  const fileName =
    source[`${prefix}ImageName`] ??
    source[`${prefix}ImageFileName`] ??
    source[`${prefix}_image_name`] ??
    source[`${prefix}FileName`] ??
    source[`${prefix}Filename`] ??
    nested?.fileName ??
    nested?.name ??
    "";
  const fileSizeRaw =
    source[`${prefix}ImageSize`] ??
    source[`${prefix}_image_size`] ??
    source[`${prefix}FileSize`] ??
    source[`${prefix}Size`] ??
    nested?.size ??
    nested?.fileSize ??
    null;
  const previewUrl = resolvePdfAssetUrl(typeof rawUrl === "string" ? rawUrl : "");
  const sizeNumber = Number(fileSizeRaw);
  const hasAsset = Boolean(previewUrl || String(fileName || "").trim() || rawImageValue || nestedBase64);
  // Prefer raw image/base64 from GET so PUT can re-send without depending on remote URLs.
  const retainedPayload =
    rawImageValue ||
    nestedBase64 ||
    (previewUrl.startsWith("data:") ? previewUrl : "") ||
    "";

  return {
    file: null,
    previewUrl,
    fileName: String(fileName || "").trim() || (hasAsset ? `${prefix}-image` : ""),
    fileSize: Number.isFinite(sizeNumber) && sizeNumber > 0 ? sizeNumber : null,
    existing: hasAsset,
    removed: false,
    objectUrl: "",
    retainedPayload,
  };
}

function pdfFileToRetainedPayload(file) {
  if (!(file instanceof Blob)) return Promise.resolve("");
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

async function mergePdfSlotAfterSave(current, normalizedSlot, uploadedFile) {
  if (normalizedSlot?.existing || normalizedSlot?.previewUrl || normalizedSlot?.retainedPayload) {
    revokePdfImageObjectUrl(current);
    const retainedFromUpload =
      normalizedSlot.retainedPayload || (await pdfFileToRetainedPayload(uploadedFile));
    return {
      ...normalizedSlot,
      retainedPayload:
        normalizedSlot.retainedPayload ||
        retainedFromUpload ||
        current?.retainedPayload ||
        "",
      previewUrl:
        normalizedSlot.previewUrl ||
        retainedFromUpload ||
        current?.previewUrl ||
        "",
      existing: true,
      removed: false,
      file: null,
    };
  }

  if (uploadedFile instanceof Blob) {
    const retained = await pdfFileToRetainedPayload(uploadedFile);
    if (current?.objectUrl && current.objectUrl !== retained) {
      revokePdfImageObjectUrl(current);
    }
    return {
      file: null,
      previewUrl: retained || current?.previewUrl || "",
      fileName: String(current?.fileName || uploadedFile.name || "").trim() || "image.png",
      fileSize: Number(uploadedFile.size) || current?.fileSize || null,
      existing: true,
      removed: false,
      objectUrl: "",
      retainedPayload: retained || current?.retainedPayload || "",
    };
  }

  if (current?.removed) {
    revokePdfImageObjectUrl(current);
    return createEmptyPdfImageSlot();
  }

  return {
    ...current,
    file: null,
    removed: false,
    existing: Boolean(current?.existing || current?.previewUrl || current?.retainedPayload),
  };
}

function normalizeQuotationPdfConfig(payload = {}) {
  let root =
    payload && typeof payload === "object" && payload.data && typeof payload.data === "object"
      ? payload.data
      : payload;
  if (Array.isArray(root)) {
    root = root[0] || {};
  } else if (root && typeof root === "object") {
    const looksLikeConfig =
      root.bodyContent != null ||
      root.body_content != null ||
      root.headerImage != null ||
      root.footerImage != null ||
      root.headerImageUrl != null ||
      root.footerImageUrl != null;
    if (!looksLikeConfig) {
      if (Array.isArray(root.content)) root = root.content[0] || {};
      else if (Array.isArray(root.results)) root = root.results[0] || {};
      else if (Array.isArray(root.items)) root = root.items[0] || {};
    }
  }
  const bodyContent = String(
    root?.bodyContent ??
      root?.body_content ??
      (typeof root?.content === "string" ? root.content : "") ??
      root?.body ??
      ""
  );
  return {
    headerImage: mapPdfImageSlotFromApi("header", root),
    footerImage: mapPdfImageSlotFromApi("footer", root),
    bodyContent: bodyContent.slice(0, PDF_TEMPLATE_BODY_MAX),
  };
}

function revokePdfImageObjectUrl(slot) {
  if (slot?.objectUrl) {
    try {
      URL.revokeObjectURL(slot.objectUrl);
    } catch {
      // ignore
    }
  }
}

function validatePdfTemplateImageFile(file) {
  if (!(file instanceof Blob)) {
    return sq("pdfTemplateImageRequired");
  }
  const name = String(file.name || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  const isPng = type === "image/png" || name.endsWith(".png");
  const isJpg =
    type === "image/jpeg" ||
    type === "image/jpg" ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg");
  if (!isPng && !isJpg) {
    return sq("pdfTemplateImageInvalidType");
  }
  if (Number(file.size) > PDF_TEMPLATE_MAX_BYTES) {
    return sq("pdfTemplateImageTooLarge");
  }
  return "";
}

const PDF_TEMPLATE_BODY_MIN_HEIGHT = 72;
const PDF_TEMPLATE_BODY_MAX_HEIGHT = 280;

function resizePdfBodyTextarea(el) {
  if (!el) return;
  el.style.height = "auto";
  const next = Math.min(
    Math.max(el.scrollHeight, PDF_TEMPLATE_BODY_MIN_HEIGHT),
    PDF_TEMPLATE_BODY_MAX_HEIGHT
  );
  el.style.height = `${next}px`;
  el.style.overflowY = el.scrollHeight > PDF_TEMPLATE_BODY_MAX_HEIGHT ? "auto" : "hidden";
}

/** Approval totals: prefer persisted pricingMetrics when Working Sheet rows are still lazy/empty. */
function resolveManagerApprovalSummary({ quotation, rows = [], pmcEntries = [] } = {}) {
  const backend = quotation?.pricingMetrics;
  const useBackend = hasBackendPricingMetrics(backend);
  const liveTotals = computeWorkingTotals(rows, quotation?.type);
  const pmcTotal = (Array.isArray(pmcEntries) ? pmcEntries : []).reduce(
    (sum, entry) => sum + Number(entry.totalCost || 0),
    0
  );

  const backendSupply = pickFiniteNumber(backend?.totalInputSupplyAmount);
  const backendInstall = pickFiniteNumber(backend?.totalInputInstallationAmount);
  const preferBackendCosts =
    useBackend &&
    (rows.length === 0 || !(liveTotals.supplyInputAmount > 0 || liveTotals.inputInstallationAmount > 0));

  const supplyCost =
    preferBackendCosts && backendSupply != null ? backendSupply : liveTotals.supplyInputAmount;
  const installationCost = supportsInstallation(quotation?.type)
    ? preferBackendCosts && backendInstall != null
      ? backendInstall
      : liveTotals.inputInstallationAmount
    : 0;

  let totalCost = supplyCost + installationCost + pmcTotal;
  const livePricing = computeWorkingSheetPricingMetrics({
    rows,
    quotationType: quotation?.type,
    pmcEntries,
    overheadsPct: quotation?.overheadsPct,
    markUpPct: quotation?.markUpPct,
  });

  const backendCalculated = pickFiniteNumber(backend?.calculatedSellingPrice);
  const backendGross = pickFiniteNumber(backend?.grossProfit);
  const backendNet = pickFiniteNumber(backend?.netProfit);
  if (!(totalCost > 0) && backendCalculated != null && backendGross != null) {
    totalCost = Math.max(0, backendCalculated - backendGross);
  }
  if (!(totalCost > 0) && livePricing.totalCost > 0) {
    totalCost = livePricing.totalCost;
  }

  const netMarginAmount = backendNet != null ? backendNet : livePricing.netProfit;
  const grossMarginAmount = backendGross != null ? backendGross : livePricing.grossProfit;
  const calculatedSellingPrice =
    backendCalculated != null ? backendCalculated : livePricing.calculatedSellingPrice;
  const netProfitPercent =
    pickFiniteNumber(backend?.netMarginPct) ??
    (calculatedSellingPrice > 0 ? (netMarginAmount / calculatedSellingPrice) * 100 : 0);
  const grossProfitPercent =
    pickFiniteNumber(backend?.grossMarginPct) ??
    (calculatedSellingPrice > 0 ? (grossMarginAmount / calculatedSellingPrice) * 100 : 0);

  const customerSellingTotal =
    resolveBackendRealizedSellingTotal(backend, quotation?.type) ??
    (liveTotals.customerSellingTotal > 0
      ? liveTotals.customerSellingTotal
      : livePricing.realizedSellingTotal);

  const realizedMarginPercent =
    pickFiniteNumber(backend?.realizedGrossMargin) ??
    (customerSellingTotal > 0
      ? ((customerSellingTotal - totalCost) / customerSellingTotal) * 100
      : livePricing.realizedGrossMargin);

  const configuredTargetMargin =
    Number(quotation?.netPricePercentage ?? quotation?.markUpPct ?? 0) || 0;
  const marginBelowTarget =
    configuredTargetMargin > 0 && realizedMarginPercent < configuredTargetMargin;

  return {
    totalCost,
    netMarginAmount,
    grossMarginAmount,
    calculatedSellingPrice,
    netProfitPercent,
    grossProfitPercent,
    customerSellingTotal,
    realizedMarginPercent,
    marginBelowTarget,
  };
}

export {
  ADD_ENTITY_OPTION,
  APPROVAL_RULE_KIND_OPTIONS,
  BACKEND_AMOUNT_INVALIDATING_KEYS,
  CLEARED_BACKEND_AMOUNT_FIELDS,
  CUSTOMER_STAGE_STATUSES,
  PDF_TEMPLATE_ACCEPT,
  PDF_TEMPLATE_BODY_MAX,
  PDF_TEMPLATE_BODY_MIN_HEIGHT,
  PMC_COST_HEAD_TYPE_OPTIONS,
  SALES_TYPES,
  STATUS_OPTIONS,
  TERM_QUOTATION_TYPE_VALUES,
  applyHeadMetricsToLines,
  applyNetPriceRuleToRows,
  applySavedRowsToHeads,
  buildApprovalRulesFromDraft,
  buildBlankWorkingRow,
  buildPmcConfigLinesPayload,
  buildPmcConfigPayload,
  buildPmcCreateApiPayload,
  buildPmcHeadMetricPayload,
  buildPmcHeadPresentation,
  buildQuotationVersionHistory,
  buildRowFromCatalogProduct,
  buildSpecificTermsPayload,
  buildTermApiPayload,
  buildWorkingSheetPayload,
  canonicalizePmcHeadType,
  computeDerivedMultiplier,
  computePreparedWorkingRow,
  computeWorkingSheetPricingMetrics,
  computeWorkingTotals,
  createAdditionalTermEntry,
  createBlankPmcLine,
  createEmptyApprovalRuleDraft,
  createEmptyPdfImageSlot,
  createEmptyPmcCreateForm,
  currentIsoDate,
  dedupeApprovalRules,
  dedupeQuotationVersions,
  dedupeWorkingRowsByLineItemId,
  ensureSystemWorkingRows,
  ensureWorkingRowsLoadedForSave,
  extractApiApprovalRules,
  extractApiCollection,
  extractApiPmcConfigs,
  extractApiTerms,
  extractNumericId,
  extractObjectPayload,
  extractQuotationSystemLineItems,
  fetchEmployeeOptions,
  filterControlClass,
  filterOutClientOnlyBlankWorkingRows,
  filterTermsForQuotationType,
  formatCurrency,
  formatCurrencyWhole,
  formatFileSizeBytes,
  formatInputNumber,
  formatMultiplierFieldValue,
  formatRuleThresholdSummary,
  getPmcHeadTypeMetricFields,
  getVisibleTerms,
  groupPmcEntriesForDisplay,
  hasBackendPricingMetrics,
  hasQuotationCreatedVersion,
  hasQuotationDetailPayload,
  hasQuotationPmcData,
  hasQuotationSpecificTermsData,
  hydrateQuotation,
  inferApprovalRuleKind,
  inputClass,
  isClientOnlyBlankWorkingRow,
  isClientPlaceholderSystemId,
  isClientPlaceholderSystemName,
  isDiscountPercentAdjustment,
  isIncompleteDecimalInput,
  isLatestQuotationVersion,
  isPersistedPmcHead,
  isPmcCostHeadActive,
  loadPmcEntriesForQuotation,
  loadSpecificTermsForQuotation,
  loadStandardTermsForQuotationType,
  loadTermsTabState,
  localizedFieldProps,
  mapApiNetPriceRuleToUi,
  mapComplaintOption,
  mapQuotationStatusForApi,
  mapSystemApiLinesToWorkingRows,
  markQuotationVersionCreated,
  matchesQuotationFilters,
  mergePdfSlotAfterSave,
  mergeQuotationServiceRoleFields,
  mergeRowWithCatalogProduct,
  mergeUniqueComplaintOptions,
  mergeUniqueNamedOptions,
  multiplierValueFollowsCostPricing,
  multipliersApproximatelyEqual,
  normalizeAdditionalTermEntry,
  normalizeApiQuotation,
  normalizeContactOption,
  normalizeCustomerOption,
  normalizeLocationOption,
  normalizePmcEntry,
  normalizePmcLineEntries,
  normalizeQuotationPdfConfig,
  normalizeSpecificTermsCollection,
  normalizeStatusLabel,
  normalizeTermQuotationTypes,
  parseContentDispositionFilename,
  parseCurrency,
  patchPmcEntryValue,
  pdfImageSlotToUploadFile,
  persistApprovalRule,
  pickFiniteNumber,
  pickPmcHeadMetricFields,
  pickPmcLineMetricFields,
  pickQuotationServiceRoleFields,
  quotationIsEditable,
  reloadMobileWorkingRowsAfterSave,
  resizePdfBodyTextarea,
  resolveAssignedEmployeeId,
  resolveBackendDerivedMultiplier,
  resolveBackendRealizedSellingTotal,
  resolveBackendVersionNo,
  resolveCatalogProductForRow,
  resolveCostPricingMultiplier,
  resolveEmployeeDisplayName,
  resolveLatestBackendVersionNo,
  resolveManagerApprovalSummary,
  resolvePmcEntriesForQuotation,
  resolvePmcLineId,
  resolvePmcLineLabel,
  resolveQuotationSystems,
  resolveRowCpMultiplier,
  resolveTermsFromQuotation,
  revokePdfImageObjectUrl,
  roundExcelWhole,
  rowBelongsToSystem,
  sanitizeDiscountRateInput,
  sanitizePercentInput,
  sanitizePrefillText,
  seedPmcEntriesFromQuotation,
  seedTermsStateFromQuotation,
  selectLatestQuotationsPerRefId,
  sortApprovalRules,
  sortTermsForUi,
  sq,
  supportsInstallation,
  syncCreatedVersionLocksFromHistory,
  termQuotationTypeLabel,
  toAssignedEmployeeId,
  toExcelWholeRate,
  toggleTermQuotationType,
  translateQuotationStatus,
  translateQuotationType,
  triggerBrowserDownload,
  unwrapQuotationDetailPayload,
  validatePdfTemplateImageFile,
  validateWorkingRowsForSubmit
};
